export const registrySchema = {
  $defs: {
    AIBox: {
      properties: {
        kind: {
          const: 'ai_box',
          default: 'ai_box',
          description: 'AIBox Resource Kind',
          title: 'AIBox Resource Kind',
          type: 'string',
        },
        Name: {
          description: 'AIBox Resource Name',
          title: 'AIBox Resource Name',
          type: 'string',
        },
        Repo: {
          default: 1,
          description: '# of repos for resource',
          maximum: 10,
          minimum: 1,
          title: '# of repos for resource',
          type: 'integer',
        },
        RepoFork: {
          default: false,
          description: 'Repo Fork option enabled',
          title: 'Repo Fork option enabled',
          type: 'boolean',
        },
      },
      required: ['Name'],
      title: 'AIBox',
      type: 'object',
    },
    AIFoundryBatchDeployment: {
      description:
        'A batch model deployment using DataZoneBatch or GlobalBatch SKU.\n\nBatch deployments enable high-volume, asynchronous processing of requests via the\nAzure AI Foundry Batch API.\n\nNote: GlobalBatch routes requests to any Azure data center globally.\nDataZoneBatch keeps data within the EU Data Zone.',
      properties: {
        name: {
          $ref: '#/$defs/AIFoundryBatchDeploymentName',
          description: 'Model name for the batch deployment.',
        },
        sku: {
          anyOf: [
            {
              enum: ['DataZoneBatch', 'GlobalBatch'],
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'Batch SKU to use. Defaults to DataZoneBatch when available, otherwise GlobalBatch. Note: GlobalBatch routes requests globally.',
          title: 'Sku',
        },
        maxTokensPerMinuteThousands: {
          default: 1,
          description:
            'Tokens per minute (in thousands) for the batch deployment.',
          maximum: 10000,
          minimum: 1,
          title: 'Maxtokensperminutethousands',
          type: 'integer',
        },
        version: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'Version of the model to use. Some models support specific versions.',
          title: 'Version',
        },
      },
      required: ['name'],
      title: 'AIFoundryBatchDeployment',
      type: 'object',
    },
    AIFoundryBatchDeploymentName: {
      description:
        'Models eligible for Batch processing via the Azure AI Foundry Batch API.\n\nBatch deployments process data asynchronously and are suitable for\nhigh-volume, non-real-time workloads at reduced cost. The Batch API allows uploading\n``.jsonl`` files and processing them asynchronously.\n\nModels support either ``DataZoneBatch`` (EU Data Zone), ``GlobalBatch`` (global), or both.\nSee: https://learn.microsoft.com/en-us/azure/ai-services/openai/how-to/batch',
      enum: [
        'gpt-5',
        'gpt-5.1',
        'gpt-4.1',
        'gpt-4.1-mini',
        'gpt-4.1-nano',
        'gpt-4o',
        'gpt-4o-mini',
        'o3',
        'o3-mini',
        'o4-mini',
      ],
      title: 'AIFoundryBatchDeploymentName',
      type: 'string',
    },
    AIFoundryConfig: {
      properties: {
        kind: {
          const: 'ai-foundry',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
        externalAccess: {
          default: true,
          description:
            'Enable external access to the service via the product Gateway.',
          title: 'External access',
          type: 'boolean',
          'ui:title': 'External Access',
        },
        location: {
          $ref: '#/$defs/AIFoundryLocation',
          default: 'swedencentral',
          description: 'Azure region where AI Services should be provisioned.',
          'ui:title': 'Location',
        },
        foundryCicd: {
          default: false,
          description: 'Whether to set up CI/CD pipelines for AI Foundry.',
          title: 'Foundrycicd',
          type: 'boolean',
          'ui:title': 'Enable Foundry CI/CD',
        },
      },
      required: ['kind'],
      title: 'AIFoundryConfig',
      type: 'object',
      'ui:options': {
        icon: 'ai-foundry',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/ai-analytics/aifoundry/',
          },
        ],
        supportedTargets: ['L5'],
        tags: ['ai', 'foundry', 'development'],
        teaser: 'Azure AI Foundry for comprehensive AI development.',
      },
      'ui:title': 'AI Foundry',
    },
    AIFoundryLegacyConfig: {
      properties: {
        kind: {
          const: 'ai-foundry-legacy',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
        externalAccess: {
          default: true,
          description:
            'Enable external access to the service via the product Gateway.',
          title: 'External access',
          type: 'boolean',
          'ui:title': 'External Access',
        },
        location: {
          $ref: '#/$defs/AIFoundryLocation',
          default: 'swedencentral',
          description: 'Azure region where AI Services should be provisioned.',
          'ui:title': 'Location',
        },
      },
      required: ['kind'],
      title: 'AIFoundryLegacyConfig',
      type: 'object',
      'ui:options': {
        icon: 'ai-foundry',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/ai-analytics/aifoundry/',
          },
        ],
        supportedTargets: ['L5'],
        tags: ['ai', 'foundry', 'development'],
        teaser: 'Azure AI Foundry Legacy for comprehensive AI development.',
      },
      'ui:title': 'AI Foundry Legacy',
    },
    AIFoundryLocation: {
      description:
        'The AI Foundry location to be used, see the [documentation] for details.\nWe provide only a feature complete subset of the available locations.\n[documentation]: https://learn.microsoft.com/en-us/azure/ai-foundry/reference/region-support',
      enum: [
        'swedencentral',
        'westeurope',
        'polandcentral',
        'germanywestcentral',
        'spaincentral',
        'francecentral',
      ],
      title: 'AIFoundryLocation',
      type: 'string',
    },
    AIFoundryModelDeployment: {
      properties: {
        name: {
          $ref: '#/$defs/AIFoundryModelDeploymentName',
          description: 'Model name that will be provisioned.',
        },
        maxTokensPerMinuteThousands: {
          default: 1,
          description: 'Tokens per minute for the model deployment.',
          title: 'Maxtokensperminutethousands',
          type: 'integer',
        },
        version: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'Version of the model to use. Some models support specific versions.',
          title: 'Version',
        },
      },
      required: ['name'],
      title: 'AIFoundryModelDeployment',
      type: 'object',
    },
    AIFoundryModelDeploymentName: {
      description:
        'The AI Foundry model name to be used, see the [documentation] for details.\nWe provide only a feature complete subset of the available models.\n[documentation]: https://learn.microsoft.com/en-us/azure/ai-foundry/model-inference/concepts/models',
      enum: [
        'gpt-5.4-mini',
        'gpt-5.4-nano',
        'gpt-5',
        'gpt-5.1',
        'gpt-5.1-chat',
        'gpt-5.1-codex',
        'gpt-5.1-codex-mini',
        'gpt-5.1-codex-max',
        'gpt-5.2',
        'gpt-5.2-chat',
        'gpt-5-mini',
        'gpt-5-nano',
        'gpt-4.1',
        'gpt-4.1-mini',
        'gpt-4.1-nano',
        'gpt-4o',
        'gpt-4o-mini',
        'o1',
        'o3',
        'o3-pro',
        'o3-mini',
        'o4-mini',
        'codex-mini',
        'DeepSeek-R1',
        'DeepSeek-R1-0528',
        'DeepSeek-V3.1',
        'DeepSeek-V3.2',
        'MAI-DS-R1',
        'Phi-4-mini-instruct',
        'Phi-4-multimodal-instruct',
        'Phi-4-reasoning',
        'grok-4-fast-reasoning',
        'grok-4-fast-non-reasoning',
        'grok-4-1-fast-reasoning',
        'grok-4-1-fast-non-reasoning',
        'grok-3',
        'grok-3-mini',
        'Llama-4-Maverick-17B-128E-Instruct-FP8',
        'Llama-3.3-70B-Instruct',
        'mistral-document-ai-2505',
        'mistral-document-ai-2512',
        'Mistral-Large-3',
        'text-embedding-ada-002',
        'text-embedding-3-large',
        'text-embedding-3-small',
        'dall-e-3',
        'FLUX.2-pro',
        'FLUX.2-flex',
        'whisper',
        'tts-hd',
        'tts',
        'gpt-realtime',
        'gpt-realtime-mini',
        'cohere-command-a',
        'Cohere-rerank-v4.0-pro',
      ],
      title: 'AIFoundryModelDeploymentName',
      type: 'string',
    },
    AIFoundryResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'ai-foundry',
          title: 'Kind',
          type: 'string',
        },
        location: {
          description:
            'Azure region where the AI Foundry resource is deployed.',
          title: 'Azure region',
          type: 'string',
        },
        storageAccountId: {
          description: 'The ID of the AI Foundry storage account.',
          title: 'AI Foundry storage account ID',
          type: 'string',
        },
      },
      required: ['resourceId', 'kind', 'location', 'storageAccountId'],
      title: 'AIFoundryResource',
      type: 'object',
    },
    AIGatewayConfig: {
      properties: {
        servicePrincipals: {
          anyOf: [
            {
              items: {
                description: 'AAD object ID of the service principal.',
                format: 'uuid',
                title: 'AAD service principal',
                type: 'string',
                'ui:field': 'AzureServicePrincipalSearch',
                'ui:title': 'AAD Service Principal',
                'vsc:picker': 'aad-serviceprincipal',
              },
              type: 'array',
            },
            {
              type: 'null',
            },
          ],
          default: [],
          description:
            'List of AAD object IDs for the service principals allowed for the AI Gateway.',
          title: 'Serviceprincipals',
        },
        userGroup: {
          anyOf: [
            {
              description: 'AAD object ID of the group.',
              format: 'uuid',
              title: 'AAD group',
              type: 'string',
              'ui:field': 'AzureGroupSearch',
              'ui:title': 'AAD Group',
              'vsc:picker': 'aad-group',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'AAD object ID for the consumer group of the AI Gateway. Optional; can be None.',
          title: 'Usergroup',
        },
        location: {
          $ref: '#/$defs/AIFoundryLocation',
          default: 'swedencentral',
          description: 'Azure region where AI Services should be provisioned.',
          'ui:title': 'Location',
        },
        modelDeployments: {
          anyOf: [
            {
              items: {
                $ref: '#/$defs/AIFoundryModelDeployment',
              },
              type: 'array',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'List of model deployments in Azure AI Foundry to be linked to the AI Gateway.',
          title: 'Modeldeployments',
        },
        batchDeployments: {
          anyOf: [
            {
              items: {
                $ref: '#/$defs/AIFoundryBatchDeployment',
              },
              type: 'array',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'List of batch model deployments using DataZoneBatch or GlobalBatch SKU. Enables the Batch API for high-volume, asynchronous processing via /foundry/openai/batches and /foundry/openai/files.',
          title: 'Batchdeployments',
        },
        databricksModelServing: {
          anyOf: [
            {
              items: {
                $ref: '#/$defs/DatabricksModelCollectionName',
              },
              type: 'array',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'List of Databricks model collections selected by the user for analytics purposes.',
          title: 'Databricksmodelserving',
        },
      },
      title: 'AIGatewayConfig',
      type: 'object',
      'ui:options': {
        icon: 'gateway',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/aigateway/introduction/',
          },
        ],
        tags: ['ai', 'gateway', 'devhub-mlai'],
        teaser: 'AI Gateway for unified access to AI models in BASF.',
      },
      'ui:title': 'AI Gateway',
    },
    AISearchLocation: {
      description:
        'The Azure Search service location to be used, see the [documentation] for details.\nWe provide only a feature complete subset of the available locations.\n[documentation]: https://learn.microsoft.com/en-us/azure/search/search-region-support',
      enum: ['northeurope', 'switzerlandnorth', 'westeurope'],
      title: 'AISearchLocation',
      type: 'string',
    },
    AISearchServiceConfigBasic: {
      properties: {
        kind: {
          const: 'ai-search-basic',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
        sku: {
          $ref: '#/$defs/AISearchSku',
          default: 'basic',
        },
        location: {
          $ref: '#/$defs/AISearchLocation',
          default: 'northeurope',
          description:
            'Azure region where search service should be provisioned.',
          'ui:title': 'Location',
        },
        semanticRankerEnabled: {
          default: false,
          description: 'Enable semantic ranking',
          title: 'Semanticrankerenabled',
          type: 'boolean',
          'ui:title': 'Enable Semantic Ranking',
        },
        externalAccess: {
          default: true,
          description:
            'Enable external access to the service via the product Gateway.',
          title: 'External access',
          type: 'boolean',
          'ui:title': 'External Access',
        },
        uniqueIdentifier: {
          default: '',
          description:
            'Add unique identifier to support multiple instances of a module.',
          title: 'Unique Identifier',
          type: 'string',
          'ui:title': 'Unique Identifier',
        },
        replicaCount: {
          default: 1,
          description: 'Number of replicas for the Azure Search service.',
          maximum: 3,
          minimum: 1,
          title: 'Replicacount',
          type: 'integer',
          'ui:title': 'Replica Count',
        },
        partitionCount: {
          anyOf: [
            {
              enum: [1, 2, 3],
              type: 'integer',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'Number of partitions for the Azure Search service.',
          title: 'Partitioncount',
          'ui:title': 'Partition Count',
        },
        managedIdentity: {
          anyOf: [
            {
              type: 'boolean',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'Whether to create system-assigned managed identity for the service.',
          title: 'Managedidentity',
          'ui:title': 'Managed Identity',
        },
      },
      required: ['kind'],
      title: 'AISearchServiceConfigBasic',
      type: 'object',
      'ui:options': {
        icon: 'cognitive-search',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/ai-analytics/ai-search-service/',
          },
        ],
        tags: ['search', 'ai'],
        teaser: 'Basic tier Azure Cognitive Search service.',
      },
      'ui:title': 'AI Search Basic',
    },
    AISearchServiceConfigStandard: {
      properties: {
        kind: {
          const: 'ai-search',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
        sku: {
          $ref: '#/$defs/AISearchSku',
          default: 'standard',
        },
        location: {
          $ref: '#/$defs/AISearchLocation',
          default: 'northeurope',
          description:
            'Azure region where search service should be provisioned.',
          'ui:title': 'Location',
        },
        semanticRankerEnabled: {
          default: false,
          description: 'Enable semantic ranking',
          title: 'Semanticrankerenabled',
          type: 'boolean',
          'ui:title': 'Enable Semantic Ranking',
        },
        externalAccess: {
          default: true,
          description:
            'Enable external access to the service via the product Gateway.',
          title: 'External access',
          type: 'boolean',
          'ui:title': 'External Access',
        },
        uniqueIdentifier: {
          default: '',
          description:
            'Add unique identifier to support multiple instances of a module.',
          title: 'Unique Identifier',
          type: 'string',
          'ui:title': 'Unique Identifier',
        },
        replicaCount: {
          default: 1,
          description: 'Number of replicas for the Azure Search service.',
          maximum: 12,
          minimum: 1,
          title: 'Replicacount',
          type: 'integer',
          'ui:title': 'Replica Count',
        },
        partitionCount: {
          anyOf: [
            {
              enum: [1, 2, 3, 4, 6, 7, 8, 9, 10, 11, 12],
              type: 'integer',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'Number of partitions for the Azure Search service.',
          title: 'Partitioncount',
          'ui:title': 'Partition Count',
        },
        managedIdentity: {
          anyOf: [
            {
              type: 'boolean',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'Whether to create system-assigned managed identity for the service.',
          title: 'Managedidentity',
          'ui:title': 'Managed Identity',
        },
      },
      required: ['kind'],
      title: 'AISearchServiceConfigStandard',
      type: 'object',
      'ui:options': {
        icon: 'cognitive-search',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/ai-analytics/ai-search-service/',
          },
        ],
        supportedTargets: ['L5'],
        tags: ['search', 'ai'],
        teaser: 'Standard tier Azure Cognitive Search service.',
      },
      'ui:title': 'AI Search',
    },
    AISearchSku: {
      description:
        'The Azure Search service SKU name to be used, see the [documentation] for details.\n\n[documentation]: https://learn.microsoft.com/en-us/azure/search/search-sku-tier',
      enum: ['basic', 'standard'],
      title: 'AISearchSku',
      type: 'string',
    },
    AIServices: {
      properties: {
        kind: {
          const: 'ai_services',
          default: 'ai_services',
          description: 'AIServices Resource Kind',
          title: 'AIServices Resource Kind',
          type: 'string',
        },
        Name: {
          description: 'AIServices Resource Name',
          title: 'AIServices Resource Name',
          type: 'string',
        },
      },
      required: ['Name'],
      title: 'AIServices',
      type: 'object',
    },
    AccessITGroupInfo: {
      description: 'Information about an AccessIT Azure AD group.',
      properties: {
        objectId: {
          description: 'The Azure Active Directory object ID of the group.',
          title: 'Azure AD Object ID',
          type: 'string',
        },
        displayName: {
          description:
            'The display name of the Azure AD group (e.g., DH_PD_PRODUCTNAME_ADMIN).',
          title: 'Group Display Name',
          type: 'string',
        },
        groupType: {
          default: 'default',
          description:
            'The type of AccessIT group indicating its purpose and permissions.',
          title: 'Group Type',
          type: 'string',
        },
        isDefault: {
          default: true,
          description:
            'Whether this is a default group created by the AccessIT platform.',
          title: 'Is Default Group',
          type: 'boolean',
        },
      },
      required: ['objectId', 'displayName'],
      title: 'AccessITGroupInfo',
      type: 'object',
    },
    AcrResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'acr',
          title: 'Kind',
          type: 'string',
        },
        location: {
          description: 'Azure region where the ACR is deployed.',
          title: 'Azure region',
          type: 'string',
        },
        sku: {
          description: 'The SKU of the ACR.',
          title: 'SKU',
          type: 'string',
        },
      },
      required: ['resourceId', 'kind', 'location', 'sku'],
      title: 'AcrResource',
      type: 'object',
    },
    Adf: {
      properties: {
        kind: {
          const: 'adf',
          default: 'adf',
          description: 'ADF Resource Kind',
          title: 'ADF Resource Kind',
          type: 'string',
        },
        Name: {
          description: 'ADF Resource Name',
          title: 'ADF Resource Name',
          type: 'string',
        },
        Repo: {
          default: 0,
          description: '# of repos for resource',
          maximum: 10,
          minimum: 0,
          title: '# of repos for resource',
          type: 'integer',
        },
        RepoFork: {
          default: false,
          description: 'Repo Fork option enabled',
          title: 'Repo Fork option enabled',
          type: 'boolean',
        },
        Vsts: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'vsts for ADF resource',
          title: 'vsts for ADF resource',
        },
        DiagnosticSettingsLaw: {
          default: false,
          description: 'Diagnostic Settings Law',
          title: 'Diagnostic Settings Law',
          type: 'boolean',
        },
        DiagnosticSettingsSta: {
          default: false,
          description: 'Diagnostic Settings Sta',
          title: 'Diagnostic Settings Sta',
          type: 'boolean',
        },
        ManagedVirtualNetworkEnabled: {
          default: false,
          description: 'Managed VN Enabled',
          title: 'Managed VN Enabled',
          type: 'boolean',
        },
        Logs: {
          default: [],
          description: 'Logs for ADF resource',
          items: {
            type: 'string',
          },
          title: 'Logs for ADF resource',
          type: 'array',
        },
        Metrics: {
          default: [],
          description: 'Metrics for ADF resource',
          items: {
            type: 'string',
          },
          title: 'Metrics for ADF resource',
          type: 'array',
        },
      },
      required: ['Name'],
      title: 'Adf',
      type: 'object',
    },
    Adls: {
      properties: {
        kind: {
          const: 'adls',
          default: 'adls',
          description: 'Adls Resource Kind',
          title: 'Adls Resource Kind',
          type: 'string',
        },
        Name: {
          description: 'Adls Resource Name',
          title: 'Adls Resource Name',
          type: 'string',
        },
      },
      required: ['Name'],
      title: 'Adls',
      type: 'object',
    },
    AdlsDataSharing: {
      properties: {
        kind: {
          const: 'adls-datasharing',
          default: 'adls-datasharing',
          description: 'The kind of datasharing.',
          title: 'Datasharing Kind',
          type: 'string',
        },
        path: {
          description: 'The path of the datasharing.',
          title: 'Path',
          type: 'string',
        },
        identityObjectId: {
          description: 'The identity object ID of the datasharing.',
          title: 'Identity Object ID',
          type: 'string',
        },
        containerName: {
          default: 'lab',
          description:
            "The container name of the datasharing (by default is 'lab').",
          title: 'Container Name',
          type: 'string',
        },
        permissions: {
          $ref: '#/$defs/AdlsDataSharingPermissionValues',
          description: 'The permissions of the datasharing.',
          title: 'Permissions',
        },
      },
      required: ['path', 'identityObjectId', 'permissions'],
      title: 'AdlsDataSharing',
      type: 'object',
    },
    AdlsDataSharingPermissionValues: {
      enum: ['read', 'read-write'],
      title: 'AdlsDataSharingPermissionValues',
      type: 'string',
    },
    AdlsFirewallRule: {
      properties: {
        AuthorEmail: {
          description: 'The email of the author of the firewall rule.',
          title: 'Author Email',
          type: 'string',
        },
        kind: {
          const: 'adls-firewall-rule',
          default: 'adls-firewall-rule',
          description: 'The kind of firewall rule.',
          title: 'Firewall Rule Kind',
          type: 'string',
        },
        IPAddressCIDR: {
          anyOf: [
            {
              format: 'ipvanyaddress',
              type: 'string',
            },
            {
              format: 'ipvanyinterface',
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'The IP address CIDR of the firewall rule.',
          title: 'IP Address CIDR',
        },
      },
      required: ['AuthorEmail'],
      title: 'AdlsFirewallRule',
      type: 'object',
    },
    AdlsPermission: {
      properties: {
        kind: {
          const: 'adls-permission',
          default: 'adls-permission',
          description: 'ADLS Permission Kind',
          title: 'ADLS Permission Kind',
          type: 'string',
        },
        Path: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          description: 'Only used for adls permissions',
          title: 'Path',
        },
        containerName: {
          default: 'lab',
          description: "Container name (by default is 'lab')",
          title: 'Container Name.',
          type: 'string',
        },
        Permissions: {
          $ref: '#/$defs/AdlsPermissionValues',
          description: 'The permissions assigned to the adls group.',
          title: 'Permissions',
        },
      },
      required: ['Path', 'Permissions'],
      title: 'AdlsPermission',
      type: 'object',
    },
    AdlsPermissionValues: {
      enum: ['Read', 'ReadWrite'],
      title: 'AdlsPermissionValues',
      type: 'string',
    },
    AgentReport: {
      properties: {
        objectId: {
          description: 'The object ID of the service principal.',
          title: 'Object ID',
          type: 'string',
        },
        repositories: {
          description: 'The repositories the service principal has access to.',
          items: {
            type: 'string',
          },
          title: 'Repositories',
          type: 'array',
        },
        environmentName: {
          description:
            'The name of the environment the service principal is used in.',
          title: 'Environment name',
          type: 'string',
        },
      },
      required: ['objectId', 'environmentName'],
      title: 'AgentReport',
      type: 'object',
    },
    Aml: {
      properties: {
        kind: {
          const: 'aml',
          default: 'aml',
          description: 'Aml Resource Kind',
          title: 'Aml Resource Kind',
          type: 'string',
        },
        Name: {
          description: 'Aml Resource Name',
          title: 'Aml Resource Name',
          type: 'string',
        },
        Repo: {
          default: 0,
          description: '# of repos for resource',
          maximum: 10,
          minimum: 0,
          title: '# of repos for resource',
          type: 'integer',
        },
        RepoFork: {
          default: false,
          description: 'Repo Fork option enabled',
          title: 'Repo Fork option enabled',
          type: 'boolean',
        },
      },
      required: ['Name'],
      title: 'Aml',
      type: 'object',
    },
    Annotations: {
      properties: {
        'devops.space': {
          description: 'The DevOps space associated with the metadata',
          title: 'DevOps Space',
          type: 'string',
        },
      },
      required: ['devops.space'],
      title: 'Annotations',
      type: 'object',
    },
    AnyCapabilityConfig: {
      discriminator: {
        mapping: {
          mysql: '#/$defs/MysqlConfig',
          'object-store': '#/$defs/StorageConfig',
          postgres: '#/$defs/PostgresConfig',
        },
        propertyName: 'kind',
      },
      oneOf: [
        {
          $ref: '#/$defs/MysqlConfig',
        },
        {
          $ref: '#/$defs/PostgresConfig',
        },
        {
          $ref: '#/$defs/StorageConfig',
        },
      ],
      title: 'Any Capability Config',
    },
    AnyDevHubModuleConfig: {
      description: 'Any DevHub Module Config',
      discriminator: {
        mapping: {
          'ai-foundry': '#/$defs/AIFoundryConfig',
          'ai-foundry-legacy': '#/$defs/AIFoundryLegacyConfig',
          'ai-search': '#/$defs/AISearchServiceConfigStandard',
          'ai-search-basic': '#/$defs/AISearchServiceConfigBasic',
          'bot-service': '#/$defs/BotServiceConfig',
          cloudbeaver: '#/$defs/CloudBeaverConfig',
          dagster: '#/$defs/DagsterConfig',
          'data-factory': '#/$defs/DataFactoryConfig',
          databricks: '#/$defs/DatabricksPIConfig',
          'databricks-in': '#/$defs/DatabricksINConfig',
          eventgrid: '#/$defs/EventgridConfig',
          jupyter: '#/$defs/JupyterHubConfig',
          'log-analytics': '#/$defs/LogAnalyticsConfig',
          memgraph: '#/$defs/MemgraphConfig',
          mlflow: '#/$defs/MlflowConfig',
          'mongodb-atlas': '#/$defs/MongoDBAtlasConfig',
          mysql: '#/$defs/MysqlConfig',
          'object-store': '#/$defs/DevHubStorageConfig',
          postgres: '#/$defs/PostgresConfig',
          rabbitmq: '#/$defs/RabbitMQConfig',
          'sql-server': '#/$defs/SqlServerConfig',
        },
        propertyName: 'kind',
      },
      oneOf: [
        {
          $ref: '#/$defs/AIFoundryConfig',
        },
        {
          $ref: '#/$defs/AIFoundryLegacyConfig',
        },
        {
          $ref: '#/$defs/AISearchServiceConfigBasic',
        },
        {
          $ref: '#/$defs/AISearchServiceConfigStandard',
        },
        {
          $ref: '#/$defs/BotServiceConfig',
        },
        {
          $ref: '#/$defs/CloudBeaverConfig',
        },
        {
          $ref: '#/$defs/DagsterConfig',
        },
        {
          $ref: '#/$defs/DataFactoryConfig',
        },
        {
          $ref: '#/$defs/DatabricksPIConfig',
        },
        {
          $ref: '#/$defs/DatabricksINConfig',
        },
        {
          $ref: '#/$defs/DevHubStorageConfig',
        },
        {
          $ref: '#/$defs/EventgridConfig',
        },
        {
          $ref: '#/$defs/JupyterHubConfig',
        },
        {
          $ref: '#/$defs/LogAnalyticsConfig',
        },
        {
          $ref: '#/$defs/MemgraphConfig',
        },
        {
          $ref: '#/$defs/MlflowConfig',
        },
        {
          $ref: '#/$defs/MysqlConfig',
        },
        {
          $ref: '#/$defs/PostgresConfig',
        },
        {
          $ref: '#/$defs/RabbitMQConfig',
        },
        {
          $ref: '#/$defs/SqlServerConfig',
        },
        {
          $ref: '#/$defs/MongoDBAtlasConfig',
        },
      ],
      title: 'Any Dev Hub Module Config',
    },
    AnyInfraCellSubscriptionRef: {
      description: 'Any infrastructure cell subscription reference',
      discriminator: {
        mapping: {
          DMZ: '#/$defs/InfraCellSubscriptionRefDMZ',
          PI: '#/$defs/InfraCellSubscriptionRefPI',
        },
        propertyName: 'networkArea',
      },
      oneOf: [
        {
          $ref: '#/$defs/InfraCellSubscriptionRefPI',
        },
        {
          $ref: '#/$defs/InfraCellSubscriptionRefDMZ',
        },
      ],
      title: 'Any Infra Cell Subscription Ref',
    },
    AnyMemgraphImage: {
      discriminator: {
        mapping: {
          'memgraph/memgraph': '#/$defs/MemgraphImage',
          'memgraph/memgraph-mage': '#/$defs/MemgraphMageImage',
        },
        propertyName: 'name',
      },
      oneOf: [
        {
          $ref: '#/$defs/MemgraphImage',
        },
        {
          $ref: '#/$defs/MemgraphMageImage',
        },
      ],
      title: 'Any Memgraph Image',
    },
    AnyModuleConfig: {
      description: 'Any Module Config',
      discriminator: {
        mapping: {
          'ai-multi-service': '#/$defs/AzureAIServicesConfig',
          'ai-search': '#/$defs/AISearchServiceConfigStandard',
          'ai-search-basic': '#/$defs/AISearchServiceConfigBasic',
          arango: '#/$defs/ArangoConfig',
          cloudbeaver: '#/$defs/CloudBeaverConfig',
          dagster: '#/$defs/DagsterConfig',
          jupyter: '#/$defs/JupyterHubConfig',
          memgraph: '#/$defs/MemgraphConfig',
          mlflow: '#/$defs/MlflowConfig',
          openai: '#/$defs/OpenAiConfig',
          superset: '#/$defs/SupersetConfig',
        },
        propertyName: 'kind',
      },
      oneOf: [
        {
          $ref: '#/$defs/DagsterConfig',
        },
        {
          $ref: '#/$defs/MlflowConfig',
        },
        {
          $ref: '#/$defs/MemgraphConfig',
        },
        {
          $ref: '#/$defs/JupyterHubConfig',
        },
        {
          $ref: '#/$defs/ArangoConfig',
        },
        {
          $ref: '#/$defs/SupersetConfig',
        },
        {
          $ref: '#/$defs/CloudBeaverConfig',
        },
        {
          $ref: '#/$defs/OpenAiConfig',
        },
        {
          $ref: '#/$defs/AzureAIServicesConfig',
        },
        {
          $ref: '#/$defs/AISearchServiceConfigBasic',
        },
        {
          $ref: '#/$defs/AISearchServiceConfigStandard',
        },
      ],
      title: 'Any Module Config',
    },
    AnyModuleConfigDMZ: {
      description: 'DMZ-restricted module configuration',
      discriminator: {
        mapping: {
          postgres: '#/$defs/PostgresConfig',
        },
        propertyName: 'kind',
      },
      oneOf: [
        {
          $ref: '#/$defs/PostgresConfig',
        },
      ],
      title: 'Any Module Config DMZ',
    },
    AnyProductSubscriptionBaseReport: {
      anyOf: [
        {
          $ref: '#/$defs/ProductSubscriptionBaseReportPE',
        },
        {
          $ref: '#/$defs/ProductSubscriptionBaseReportVnet',
        },
      ],
      description: 'Any infrastructure cell subscription reference',
      title: 'Any Infra Cell Subscription Ref',
    },
    AnyResource: {
      description: 'Any Resource',
      discriminator: {
        mapping: {
          acr: '#/$defs/AcrResource',
          'ai-foundry': '#/$defs/AIFoundryResource',
          'ai-search': '#/$defs/SearchServiceResource',
          'bot-service': '#/$defs/BotServiceResource',
          'cognitive-deployment': '#/$defs/CognitiveDeploymentsResource',
          'cognitive-services': '#/$defs/CognitiveAccountResource',
          'data-factory': '#/$defs/DataFactoryResource',
          'databricks-workspace': '#/$defs/DatabricksWorkspaceResource',
          'event-grid': '#/$defs/EventgridResource',
          'key-vault': '#/$defs/KeyVaultResource',
          'log-analytics': '#/$defs/LogAnalyticsResource',
          'mongodb-atlas-cluster': '#/$defs/MongoDBAtlasClusterResource',
          mysql: '#/$defs/MysqlServerResource',
          'mysql-db': '#/$defs/MysqlDatabaseResource',
          'nat-gateway-ip': '#/$defs/NatGatewayPublicIpResource',
          postgres: '#/$defs/PostgresServerResource',
          'postgres-db': '#/$defs/PostgresDatabaseResource',
          'resource-group': '#/$defs/ResourceGroupResource',
          'sql-server': '#/$defs/SqlServerResource',
          'sql-server-database': '#/$defs/SqlServerDatabaseResource',
          'sql-server-elastic-pool': '#/$defs/SqlServerElasticpoolResource',
          'storage-account': '#/$defs/StorageAccountResource',
          'storage-container': '#/$defs/StorageContainerResource',
          'unity-catalog': '#/$defs/UnityCatalogResource',
        },
        propertyName: 'kind',
      },
      oneOf: [
        {
          $ref: '#/$defs/AcrResource',
        },
        {
          $ref: '#/$defs/StorageAccountResource',
        },
        {
          $ref: '#/$defs/KeyVaultResource',
        },
        {
          $ref: '#/$defs/PostgresServerResource',
        },
        {
          $ref: '#/$defs/DataFactoryResource',
        },
        {
          $ref: '#/$defs/StorageContainerResource',
        },
        {
          $ref: '#/$defs/PostgresDatabaseResource',
        },
        {
          $ref: '#/$defs/MysqlDatabaseResource',
        },
        {
          $ref: '#/$defs/MysqlServerResource',
        },
        {
          $ref: '#/$defs/SqlServerDatabaseResource',
        },
        {
          $ref: '#/$defs/SqlServerElasticpoolResource',
        },
        {
          $ref: '#/$defs/SqlServerResource',
        },
        {
          $ref: '#/$defs/ResourceGroupResource',
        },
        {
          $ref: '#/$defs/CognitiveAccountResource',
        },
        {
          $ref: '#/$defs/CognitiveDeploymentsResource',
        },
        {
          $ref: '#/$defs/LogAnalyticsResource',
        },
        {
          $ref: '#/$defs/EventgridResource',
        },
        {
          $ref: '#/$defs/DatabricksWorkspaceResource',
        },
        {
          $ref: '#/$defs/UnityCatalogResource',
        },
        {
          $ref: '#/$defs/NatGatewayPublicIpResource',
        },
        {
          $ref: '#/$defs/MongoDBAtlasClusterResource',
        },
        {
          $ref: '#/$defs/SearchServiceResource',
        },
        {
          $ref: '#/$defs/AIFoundryResource',
        },
        {
          $ref: '#/$defs/BotServiceResource',
        },
      ],
      title: 'Any Resource',
    },
    AppStoreDeployment: {
      properties: {
        name: {
          description: 'Name of the appstore app to be linked',
          title: 'Name',
          type: 'string',
        },
      },
      required: ['name'],
      title: 'AppStoreDeployment',
      type: 'object',
    },
    ApplicationConfig: {
      properties: {
        requiredResourceAccess: {
          default: [],
          description: 'Additional permissions on the product application.',
          items: {
            discriminator: {
              mapping: {
                'azure-devops': '#/$defs/AzureDevOpsAccessConfig',
                'microsoft-graph': '#/$defs/MsGraphAccessConfig',
              },
              propertyName: 'resource',
            },
            oneOf: [
              {
                $ref: '#/$defs/MsGraphAccessConfig',
              },
              {
                $ref: '#/$defs/AzureDevOpsAccessConfig',
              },
            ],
          },
          title: 'Requiredresourceaccess',
          type: 'array',
        },
      },
      title: 'ApplicationConfig',
      type: 'object',
    },
    ApplicationInfo: {
      properties: {
        kind: {
          const: 'application',
          title: 'Kind',
          type: 'string',
        },
        clientId: {
          description:
            'The client ID of the application in Azure Active Directory.',
          title: 'Application client ID.',
          type: 'string',
        },
        objectId: {
          description:
            'The object ID of the application in Azure Active Directory.',
          title: 'Application object ID.',
          type: 'string',
        },
        displayName: {
          description:
            'The display name of the application in Azure Active Directory.',
          title: 'Application display name.',
          type: 'string',
        },
      },
      required: ['kind', 'clientId', 'objectId', 'displayName'],
      title: 'ApplicationInfo',
      type: 'object',
    },
    ArangoBackupPolicy: {
      description:
        'The BackupPolicy controls how Arango will create and retain backups.',
      properties: {
        enabled: {
          default: false,
          description:
            'Denotes if regular backups of data contained in arango are created.',
          title: 'Enabled',
          type: 'boolean',
          'ui:title': 'Enabled',
        },
        schedule: {
          default: '*/15 * * * *',
          description:
            'Defines the interval at which backups should be created.',
          pattern:
            '(@(annually|yearly|monthly|weekly|daily|hourly|reboot))|(@every ([0-9]+(ns|us|\u00b5s|ms|s|m|h))+)|(((([0-9]+,)+[0-9]+|([0-9]+(/|-)[0-9]+)|[0-9]+|\\*) ?){5,7})',
          title: 'Schedule',
          type: 'string',
          'ui:title': 'Schedule',
        },
        maxBackups: {
          default: 10,
          description: 'Maximum number of backups that will be kept.',
          title: 'Maxbackups',
          type: 'integer',
          'ui:title': 'Maximum Backups',
        },
      },
      title: 'ArangoBackupPolicy',
      type: 'object',
    },
    ArangoConfig: {
      description:
        'Scalable graph database for graph, document, and search functionality.',
      properties: {
        kind: {
          const: 'arango',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
        operatorImageVersion: {
          default: '1.2.36',
          description: 'ArangoDB operator image version',
          title: 'Operatorimageversion',
          type: 'string',
          'ui:title': 'Operator Image Version',
        },
        imageVersion: {
          default: '3.11.4',
          description: 'ArangoDB image version',
          title: 'Imageversion',
          type: 'string',
          'ui:title': 'Image Version',
        },
        deploymentChartVersion: {
          default: '1.3.2',
          description: 'Arango deployment Helm chart version',
          title: 'Deploymentchartversion',
          type: 'string',
          'ui:title': 'Deployment Chart Version',
        },
        mode: {
          $ref: '#/$defs/Mode',
          default: 'Single',
          description:
            'Controls if the database is deployed as single instance or cluster.',
          'ui:title': 'Mode',
        },
        metrics: {
          $ref: '#/$defs/ArangoMetrics',
          default: {
            enabled: false,
          },
          description:
            'Configuration on how metrics are emitted and collected.',
          'ui:title': 'Metrics',
        },
        backupPolicy: {
          $ref: '#/$defs/ArangoBackupPolicy',
          default: {
            enabled: false,
            schedule: '*/15 * * * *',
            maxBackups: 10,
          },
          description:
            'The backup policy controls creation and retention of database backups.',
          'ui:title': 'Backup Policy',
        },
        resources: {
          $ref: '#/$defs/K8sResourceConfig',
          default: {
            requests: {
              cpu: '1',
              memory: '1Gi',
            },
            limits: {
              cpu: '3',
              memory: '5Gi',
            },
          },
          description: 'Resource request and limit for the Arango workload.',
          'ui:title': 'K8s Resource configuration',
        },
        allowedGroups: {
          default: [],
          description: 'AAD groups allowed to access the Arango UI.',
          items: {
            $ref: '#/$defs/Group',
          },
          title: 'Allowedgroups',
          type: 'array',
          'ui:title': 'Allowed Groups',
          'vsc:action': 'array',
          'vsc:picker': 'aad-group',
        },
      },
      required: ['kind'],
      title: 'ArangoConfig',
      type: 'object',
      'ui:options': {
        icon: 'arangodb',
        supportedTargets: ['L5'],
        tags: ['multi-model', 'graph', 'database'],
        teaser:
          'Scalable graph database for graph, document, and search functionality.',
      },
      'ui:title': 'ArangoDB',
    },
    ArangoMetrics: {
      description: 'Metrics configuration controls how metrics are collected.',
      properties: {
        enabled: {
          default: false,
          description:
            'Denotes if metrics for the Arango deployment are collected.',
          title: 'Enabled',
          type: 'boolean',
          'ui:title': 'Enabled',
        },
      },
      title: 'ArangoMetrics',
      type: 'object',
    },
    ArtifactType: {
      description: 'Artifact type.',
      enum: ['python-library', 'npm-library', 'oci', 'workload', 'techdocs'],
      title: 'ArtifactType',
      type: 'string',
    },
    AzureAIServicesConfig: {
      properties: {
        kind: {
          const: 'ai-multi-service',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
        externalAccess: {
          default: true,
          description:
            'Enable external access to the service via the product Gateway.',
          title: 'External access',
          type: 'boolean',
          'ui:title': 'External Access',
        },
      },
      required: ['kind'],
      title: 'AzureAIServicesConfig',
      type: 'object',
      'ui:options': {
        icon: 'cognitive-services',
        supportedTargets: ['L5'],
        tags: ['ai', 'cognitive'],
        teaser: 'Azure AI services for cognitive capabilities.',
      },
      'ui:title': 'AI Multi Service',
    },
    AzureDevOpsAccessConfig: {
      properties: {
        resource: {
          const: 'azure-devops',
          description: 'The Azure resource for which the access is configured.',
          title: 'Azure resource',
          type: 'string',
        },
        resourceAccess: {
          description: 'Access configuration for the selected resource.',
          items: {
            $ref: '#/$defs/AzureDevOpsPermission',
          },
          title: 'Resource access',
          type: 'array',
          uniqueItems: true,
        },
      },
      required: ['resource'],
      title: 'AzureDevOpsAccessConfig',
      type: 'object',
    },
    AzureDevOpsPermission: {
      enum: ['delegated/user_impersonation'],
      title: 'AzureDevOpsPermission',
      type: 'string',
    },
    AzureDevOpsProject: {
      description:
        'Configuration for an Azure DevOps project integrated with the Argus platform.',
      properties: {
        name: {
          title: 'Name',
          type: 'string',
        },
        organization: {
          default: 'basf4dev',
          description:
            'The Azure DevOps organization where the project resides.',
          title: 'Azure DevOps organization',
          type: 'string',
        },
      },
      required: ['name'],
      title: 'AzureDevOpsProject',
      type: 'object',
    },
    AzureRegion: {
      enum: ['northeurope', 'westeurope'],
      title: 'AzureRegion',
      type: 'string',
    },
    AzureRegionDMZ: {
      enum: ['germanywestcentral'],
      title: 'AzureRegionDMZ',
      type: 'string',
    },
    BackendRef: {
      description:
        'BackendRef is a reference to a backend to forward matched requests to.',
      properties: {
        name: {
          description: 'Name of the backend.',
          title: 'Name',
          type: 'string',
        },
        namespace: {
          description: 'Namespace of the backend.',
          title: 'Namespace',
          type: 'string',
        },
        port: {
          description: 'Port of the backend.',
          title: 'Port',
          type: 'integer',
        },
      },
      required: ['name', 'namespace', 'port'],
      title: 'BackendRef',
      type: 'object',
    },
    BaseResourceInfo: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
      },
      required: ['resourceId'],
      title: 'BaseResourceInfo',
      type: 'object',
    },
    BotServiceConfig: {
      description:
        'Azure Bot Service with Microsoft Teams channel integration.\n\nThe [Bot Service] module provides an instance of Azure Bot Service\nwith integrated Microsoft Teams channel for building conversational AI applications.\n\n[Bot Service]: https://learn.microsoft.com/en-us/azure/bot-service/',
      properties: {
        kind: {
          const: 'bot-service',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
        sku: {
          $ref: '#/$defs/BotServiceSku',
          default: 'F0',
          description: 'The SKU of the Bot Service.',
          'ui:title': 'SKU',
        },
        teamsChannel: {
          default: true,
          description:
            'Indicates whether to enable the Microsoft Teams channel for the bot service.',
          title: 'Teamschannel',
          type: 'boolean',
          'ui:title': 'Teams Channel',
        },
      },
      required: ['kind'],
      title: 'BotServiceConfig',
      type: 'object',
      'ui:options': {
        icon: 'bot-services',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/messaging/azure-bot-service/',
          },
        ],
        supportedTargets: ['L5'],
        tags: ['bot', 'teams', 'ai', 'conversational-ai'],
        teaser: 'Azure Bot Service with Microsoft Teams channel integration.',
      },
      'ui:title': 'Bot Service',
    },
    BotServiceResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'bot-service',
          title: 'Kind',
          type: 'string',
        },
        location: {
          description: 'The location where the Bot Service is deployed.',
          title: 'Azure region',
          type: 'string',
        },
        sku: {
          description: 'The SKU of the Bot Service.',
          title: 'SKU',
          type: 'string',
        },
        endpoint: {
          description: 'The messaging endpoint for the bot.',
          title: 'Bot Endpoint',
          type: 'string',
        },
        msaAppId: {
          description: 'The Microsoft App ID of the bot.',
          title: 'Microsoft App ID',
          type: 'string',
        },
      },
      required: [
        'resourceId',
        'kind',
        'location',
        'sku',
        'endpoint',
        'msaAppId',
      ],
      title: 'BotServiceResource',
      type: 'object',
    },
    BotServiceSku: {
      description: 'The Bot Service SKU name to be used.',
      enum: ['F0', 'S1'],
      title: 'BotServiceSku',
      type: 'string',
    },
    CellAzureRegion: {
      enum: ['westeurope'],
      title: 'CellAzureRegion',
      type: 'string',
    },
    CellClusterReport: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        caCertificate: {
          description:
            'Kubernetes API server certificate authority (base64 encoded)',
          title: 'CA certificate',
          type: 'string',
        },
        oidcIssuerUrl: {
          description: 'URL of the OIDC issuer for the cluster',
          title: 'OIDC issuer URL',
          type: 'string',
        },
        apiServerUrl: {
          description: 'Base URL of the Kubernetes control plane',
          title: 'API server Url',
          type: 'string',
        },
        kubernetesVersion: {
          description: 'Version of k8s running on the cluster',
          title: 'Kubernetes version',
          type: 'string',
        },
      },
      required: [
        'resourceId',
        'caCertificate',
        'oidcIssuerUrl',
        'apiServerUrl',
        'kubernetesVersion',
      ],
      title: 'CellClusterReport',
      type: 'object',
    },
    CertManagerConfig: {
      properties: {
        config: {
          anyOf: [
            {
              $ref: '#/$defs/CertManagerConfigBase',
            },
            {
              $ref: '#/$defs/StagedConfigDict_CertManagerConfigBase_',
            },
          ],
          description: 'Configuration for the resource',
          title: 'Config',
        },
      },
      required: ['config'],
      title: 'CertManagerConfig',
      type: 'object',
    },
    CertManagerConfigBase: {
      properties: {
        enabled: {
          default: false,
          description: 'Enable cert manager.',
          title: 'Enabled',
          type: 'boolean',
        },
        chartVersion: {
          default: 'v1.16.1',
          description: 'The version of the cert-manager chart to install.',
          title: 'Chartversion',
          type: 'string',
        },
      },
      title: 'CertManagerConfigBase',
      type: 'object',
    },
    CloudBeaverConfig: {
      properties: {
        kind: {
          const: 'cloudbeaver',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
        uiPathPrefix: {
          $ref: '#/$defs/UrlPathSegment',
          default: '/dbeaver',
          description: 'Route where the main UI is served.',
          title: 'UI path prefix',
          'ui:title': 'UI Path Prefix',
        },
        chartVersion: {
          default: '1.5.0',
          description: 'Version of the service chart to deploy.',
          title: 'Helm chart version',
          type: 'string',
          'ui:title': 'Helm Chart Version',
        },
        allowedGroups: {
          default: [],
          description: 'AAD groups allowed to access the Cloudbeaver UI.',
          items: {
            $ref: '#/$defs/Group',
          },
          title: 'Allowedgroups',
          type: 'array',
          'ui:title': 'Allowed Groups',
          'vsc:action': 'array',
          'vsc:picker': 'aad-group',
        },
      },
      required: ['kind'],
      title: 'CloudBeaverConfig',
      type: 'object',
      'ui:options': {
        icon: 'cloudbeaver',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/storage/cloudbeaver/',
          },
        ],
        supportedTargets: ['L5'],
        tags: ['database-tool', 'management'],
        teaser: 'Universal database management tool for cloud.',
      },
      'ui:title': 'CloudBeaver',
    },
    ClusterSetup: {
      properties: {
        certManager: {
          $ref: '#/$defs/CertManagerConfig',
          default: {
            config: {
              chartVersion: 'v1.16.1',
              enabled: false,
            },
          },
          description: 'Cert manager configuration.',
        },
        opentelemetry: {
          $ref: '#/$defs/OpenTelemetryConfig',
          default: {
            config: {
              backend: {
                azureDataExplorer: {
                  enabled: false,
                },
              },
              enabled: false,
            },
          },
          description: 'OpenTelemetry configuration.',
        },
      },
      title: 'ClusterSetup',
      type: 'object',
    },
    CodeLocation: {
      properties: {
        repository: {
          description: 'Repository name.',
          title: 'Repository',
          type: 'string',
        },
        artifacts: {
          description: 'List of artifacts.',
          items: {
            $ref: '#/$defs/ArtifactType',
          },
          title: 'Artifacts',
          type: 'array',
        },
        workflow: {
          $ref: '#/$defs/Workflow',
          default: 'github-flow',
        },
        ruleset: {
          $ref: '#/$defs/RuleSet',
          default: 'base',
          description: 'Ruleset to apply to the code location.',
          title: 'Ruleset',
        },
        deployment: {
          $ref: '#/$defs/DeploymentTargetLevel',
          default: 'L5',
          title: 'Deployment Target Level',
        },
      },
      required: ['repository', 'artifacts'],
      title: 'CodeLocation',
      type: 'object',
    },
    CognitiveAccountResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'cognitive-services',
          title: 'Kind',
          type: 'string',
        },
        accountType: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'The type of the Cognitive Services account.',
          title: 'Account type',
        },
        location: {
          description: 'The location where the Cognitive Services is deployed.',
          title: 'Azure region',
          type: 'string',
        },
        endpoint: {
          description: 'The FQDN of the Azure Cognitive Services.',
          title: 'Cognitive Services URI',
          type: 'string',
        },
      },
      required: ['resourceId', 'kind', 'location', 'endpoint'],
      title: 'CognitiveAccountResource',
      type: 'object',
    },
    CognitiveDeploymentsResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'cognitive-deployment',
          title: 'Kind',
          type: 'string',
        },
        name: {
          description: 'The name of the Cognitive Services deployment.',
          title: 'Deployment name',
          type: 'string',
        },
        cogModelName: {
          description: 'The name of the Cognitive Services model.',
          title: 'Model name',
          type: 'string',
        },
        cogModelVersion: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'The version of the Cognitive Services model.',
          title: 'Model version',
        },
      },
      required: ['resourceId', 'kind', 'name', 'cogModelName'],
      title: 'CognitiveDeploymentsResource',
      type: 'object',
    },
    CognitiveSearch: {
      properties: {
        kind: {
          const: 'cognitive_search',
          default: 'cognitive_search',
          description: 'CognitiveSearch Resource Kind',
          title: 'CognitiveSearch Resource Kind',
          type: 'string',
        },
        Name: {
          description: 'CognitiveSearch Resource Name',
          title: 'CognitiveSearch Resource Name',
          type: 'string',
        },
      },
      required: ['Name'],
      title: 'CognitiveSearch',
      type: 'object',
    },
    CustomDomainConfig: {
      description: 'Custom domain configuration for a specific logical stage.',
      properties: {
        domains: {
          default: [],
          description:
            "Custom DNS domains for the product (e.g. 'myapp.fep.basf.net'). Maximum 10 domains to limit cert-manager load and Azure AD redirect URI count.",
          items: {
            $ref: '#/$defs/CustomDomainEntry',
          },
          maxItems: 10,
          title: 'Domains',
          type: 'array',
        },
      },
      title: 'CustomDomainConfig',
      type: 'object',
    },
    CustomDomainEntry: {
      description: 'A single custom DNS domain with its routing target.',
      properties: {
        fqdn: {
          pattern:
            '^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$',
          title: 'Fqdn',
          type: 'string',
        },
        targetDomain: {
          $ref: '#/$defs/Domain',
          default: 'app',
        },
      },
      required: ['fqdn'],
      title: 'CustomDomainEntry',
      type: 'object',
    },
    CustomDomainReport: {
      properties: {
        nodeId: {
          description: 'The ID of the node in the cluster.',
          title: 'Node ID',
          type: 'string',
        },
        cert: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Name of the certificate',
        },
        kvId: {
          anyOf: [
            {
              description: 'Canonical reference to resource.',
              title: 'Azure resource id.',
              type: 'string',
              'ui:title': 'Azure Resource ID',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Keyvault Resource ID',
        },
        role: {
          $ref: '#/$defs/InstanceCellKind',
          description: 'The role of the cell within the platform instance.',
          title: 'Role',
        },
        url: {
          description: 'The URL of the domain.',
          title: 'URL',
          type: 'string',
        },
        flavour: {
          $ref: '#/$defs/Domain',
          description: 'The flavour of the domain.',
          title: 'Flavour',
        },
        product: {
          description: 'Name of the Product this URL is used for',
          title: 'Product Name',
          type: 'string',
        },
      },
      required: ['nodeId', 'role', 'url', 'flavour', 'product'],
      title: 'CustomDomainReport',
      type: 'object',
    },
    CustomGroup: {
      properties: {
        kind: {
          const: 'custom-group',
          default: 'custom-group',
          description: 'The kind of group.',
          title: 'Group Kind',
          type: 'string',
        },
        Name: {
          description: 'The name of the group.',
          title: 'Group Name',
          type: 'string',
        },
        Description: {
          default: '',
          description: "A brief description of the group's purpose or role",
          title: 'Group Description',
          type: 'string',
        },
        Owner: {
          description: 'The owner or administrator of the group (user id).',
          title: 'Group Owner',
          type: 'string',
        },
        Permissions: {
          default: [],
          description: 'The permissions assigned to the group.',
          items: {
            discriminator: {
              mapping: {
                'adls-permission': '#/$defs/AdlsPermission',
                'databricks-uc-permission': '#/$defs/DatabricksUcPermission',
                'sql-permission': '#/$defs/SqlPermission',
              },
              propertyName: 'kind',
            },
            oneOf: [
              {
                $ref: '#/$defs/DatabricksUcPermission',
              },
              {
                $ref: '#/$defs/SqlPermission',
              },
              {
                $ref: '#/$defs/AdlsPermission',
              },
            ],
          },
          title: 'Permissions',
          type: 'array',
        },
      },
      required: ['Name', 'Owner'],
      title: 'CustomGroup',
      type: 'object',
    },
    DagsterApplyLimitPerUniqueValue: {
      properties: {
        applyLimitPerUniqueValue: {
          description: 'Apply concurrency limits to each unique value of a tag',
          title: 'Applylimitperuniquevalue',
          type: 'boolean',
          'ui:title': 'Apply limit per unique value',
        },
      },
      required: ['applyLimitPerUniqueValue'],
      title: 'DagsterApplyLimitPerUniqueValue',
      type: 'object',
    },
    DagsterConfig: {
      description:
        'Orchestrator for the whole development lifecycle, with integrated lineage and observability\n\nThe [Dagster] module provides an instance of the Dagster orchestrator to run\nyour data-engineering, or machine learning pipelines.\n\n[Dagster]: https://docs.dagster.io/\n\nNote:\n    This module will only provision the core orchestrator. Dagster code locations\n    where you define your pipelines are deployed separately.',
      properties: {
        kind: {
          const: 'dagster',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
        chartVersion: {
          default: '1.12.15',
          description: 'Version of the service chart to deploy.',
          title: 'Helm chart version',
          type: 'string',
          'ui:title': 'Helm Chart Version',
        },
        uiPathPrefix: {
          $ref: '#/$defs/UrlPathSegment',
          default: '/dagster',
          description: 'Route where the main UI is served.',
          title: 'UI path prefix',
          'ui:title': 'UI Path Prefix',
        },
        allowedGroups: {
          default: [],
          description: 'AAD groups allowed to access the Dagster UI.',
          items: {
            $ref: '#/$defs/Group',
          },
          title: 'Allowedgroups',
          type: 'array',
          'ui:title': 'Allowed Groups',
          'vsc:action': 'array',
          'vsc:picker': 'aad-group',
        },
        storeComputeLogsEnabled: {
          default: false,
          description: 'Compute logs in blob storage',
          title: 'Storecomputelogsenabled',
          type: 'boolean',
          'ui:title': 'Enable Store Compute Logs',
        },
        userDeployments: {
          default: [],
          description: 'Code location servers.',
          items: {
            $ref: '#/$defs/DagsterUserDeployment',
          },
          title: 'Userdeployments',
          type: 'array',
          'ui:title': 'User Deployments',
          'vsc:action': 'array',
        },
        maxConcurrentRuns: {
          default: 10,
          description: 'Maximum number of concurrent job execution runs.',
          title: 'Maxconcurrentruns',
          type: 'integer',
          'ui:title': 'Maximum Concurrent Runs',
        },
        tagConcurrencyLimits: {
          default: [],
          description:
            'List of tags and their corresponding concurrency limits.',
          items: {
            $ref: '#/$defs/DagsterTagConcurrencyLimits',
          },
          title: 'Tagconcurrencylimits',
          type: 'array',
          'ui:title': 'Tag Concurrency Limits',
          'vsc:action': 'array',
        },
        defaultJobResources: {
          $ref: '#/$defs/K8sResourceConfig',
          default: {
            requests: {
              cpu: '200m',
              memory: '512Mi',
            },
            limits: {
              cpu: '1000m',
              memory: '1Gi',
            },
          },
          description: 'Default resources applied to pipeline execution runs.',
          'ui:title': 'Default Job Resources',
        },
        extraHelmValues: {
          additionalProperties: {
            type: 'string',
          },
          default: {},
          description: 'Extra values to pass to dagster helm chart',
          title: 'Extrahelmvalues',
          type: 'object',
          'ui:title': 'Extra Helm Values',
        },
      },
      required: ['kind'],
      title: 'DagsterConfig',
      type: 'object',
      'ui:options': {
        icon: 'dagster',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/etl-tools/dagster/data-ml-orchestration-with-dagster/',
          },
        ],
        supportedTargets: ['L5'],
        tags: ['orchestration', 'data-pipeline'],
        teaser:
          'Orchestrator for the whole development lifecycle, with integrated lineage and observability.',
      },
      'ui:title': 'Dagster',
    },
    DagsterTagConcurrencyLimits: {
      properties: {
        key: {
          description: 'Job tag key',
          title: 'Key',
          type: 'string',
          'ui:title': 'Key',
        },
        value: {
          anyOf: [
            {
              type: 'string',
            },
            {
              $ref: '#/$defs/DagsterApplyLimitPerUniqueValue',
            },
            {
              type: 'null',
            },
          ],
          description: 'Job tag value',
          title: 'Value',
          'ui:title': 'Value',
        },
        limit: {
          description: 'Maximum number of concurrent runs',
          title: 'Limit',
          type: 'integer',
          'ui:title': 'Limit',
        },
      },
      required: ['key', 'value', 'limit'],
      title: 'DagsterTagConcurrencyLimits',
      type: 'object',
    },
    DagsterUserDeployment: {
      properties: {
        host: {
          $ref: '#/$defs/RFC1035Name',
          description: 'Host name of the code location server',
          'ui:title': 'Host Name',
        },
        port: {
          $ref: '#/$defs/PortNumber',
          default: 3030,
          description: 'Port number of the code location server',
          'ui:title': 'Port Number',
        },
      },
      required: ['host'],
      title: 'DagsterUserDeployment',
      type: 'object',
    },
    DataDogIntegrationInfo: {
      properties: {
        agentObjectId: {
          title: 'Agentobjectid',
          type: 'string',
        },
      },
      required: ['agentObjectId'],
      title: 'DataDogIntegrationInfo',
      type: 'object',
    },
    DataFactoryConfig: {
      description: 'Configuration for the Data Factory module.',
      properties: {
        kind: {
          const: 'data-factory',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
        managedVirtualNetworkEnabled: {
          default: false,
          description: 'Enable managed virtual network for the resource.',
          title: 'Managed virtual network',
          type: 'boolean',
          'ui:title': 'Enable Managed Virtual Network',
        },
        diagnosticSta: {
          anyOf: [
            {
              $ref: '#/$defs/DiagnosticSettings',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'ADF diagnostic settings for STA.',
          title: 'ADF diagnostic settings for STA',
          'ui:title': 'Diagnostic Settings STA',
        },
        diagnosticLaw: {
          anyOf: [
            {
              $ref: '#/$defs/DiagnosticSettings',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'ADF diagnostic settings for LAW.',
          title: 'ADF diagnostic settings for LAW',
          'ui:title': 'Diagnostic Settings LAW',
        },
        retentionPolicyDays: {
          default: 0,
          description: 'Retention policy days for the resource.',
          title: 'Retention policy days',
          type: 'integer',
          'ui:title': 'Retention Policy Days',
        },
        repoCount: {
          default: 1,
          description:
            'The number of repositories to create. By default, the repository count is set to 1.',
          maximum: 1,
          minimum: 1,
          title: 'Repo count',
          type: 'integer',
          'ui:title': 'Repository Count',
        },
      },
      required: ['kind'],
      title: 'DataFactoryConfig',
      type: 'object',
      'ui:options': {
        icon: 'data-factories',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/category/azure-data-factory/',
          },
        ],
        supportedTargets: ['L5'],
        tags: ['data-integration', 'etl', 'pipeline'],
        teaser: 'Azure Data Factory for data integration and transformation.',
      },
      'ui:title': 'Data Factory',
    },
    DataFactoryLogType: {
      description: 'Enum for Data Factory log types.',
      enum: [
        'ActivityRuns',
        'PipelineRuns',
        'TriggerRuns',
        'SandboxPipelineRuns',
        'SandboxActivityRuns',
        'SSISPackageEventMessages',
        'SSISPackageExecutableStatistics',
        'SSISPackageEventMessageContext',
        'SSISPackageExecutionComponentPhases',
        'SSISPackageExecutionDataStatistics',
        'SSISIntegrationRuntimeLogs',
        'AirflowTaskLogs',
        'AirflowDagRuns',
        'AirflowDagProcessingLogs',
        'AirflowSchedulerLogs',
        'AirflowWebLogs',
      ],
      title: 'DataFactoryLogType',
      type: 'string',
    },
    DataFactoryMetricType: {
      description: 'Enum for Data Factory metric types.',
      enum: ['AllMetrics'],
      title: 'DataFactoryMetricType',
      type: 'string',
    },
    DataFactoryResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'data-factory',
          title: 'Kind',
          type: 'string',
        },
        location: {
          description: 'Azure region where the data factory is deployed.',
          title: 'Azure region',
          type: 'string',
        },
      },
      required: ['resourceId', 'kind', 'location'],
      title: 'DataFactoryResource',
      type: 'object',
    },
    DatabaseName: {
      description: 'A valid name for a PostgreSQL database.',
      errorMessage: {
        pattern:
          'Must start with a lowercase letter, can contain lowercase letters, numbers, hyphens, and underscores.',
      },
      maxLength: 63,
      minLength: 1,
      pattern: '^[a-z]+(?:[-_]?[a-z0-9]+)+$',
      title: 'Database name',
      type: 'string',
    },
    Databricks: {
      properties: {
        kind: {
          const: 'databricks',
          default: 'databricks',
          description: 'Databricks Resource Kind',
          title: 'Databricks Resource Kind',
          type: 'string',
        },
        Name: {
          description: 'Databricks Resource Name',
          title: 'Databricks Resource Name',
          type: 'string',
        },
        NatGateway: {
          default: false,
          description: 'Enable the NAT Gateway in databricks',
          title: 'NAT Gateway',
          type: 'boolean',
        },
        Serverless: {
          default: false,
          description: 'Enable serverless for SQL Warehouse',
          title: 'Serverless for SQL Warehouse',
          type: 'boolean',
        },
        ServerlessConfiguration: {
          anyOf: [
            {
              $ref: '#/$defs/ServerlessConfiguration',
            },
            {
              type: 'null',
            },
          ],
          default: '',
          description: 'Serverless Configuration',
          title: 'Serverless Configuration',
        },
        DiagnosticSettingsLaw: {
          default: false,
          description: 'Diagnostic Settings Law',
          title: 'Diagnostic Settings Law',
          type: 'boolean',
        },
        DiagnosticSettingsSta: {
          default: false,
          description: 'Diagnostic Settings Sta',
          title: 'Diagnostic Settings Sta',
          type: 'boolean',
        },
        Logs: {
          default: [],
          description: 'Logs for databricks resource',
          items: {
            type: 'string',
          },
          title: 'Logs for databricks resource',
          type: 'array',
        },
        Metrics: {
          default: [],
          description: 'Metrics for databricks resource',
          items: {
            type: 'string',
          },
          title: 'Metrics for databricks resource',
          type: 'array',
        },
        Repo: {
          default: 0,
          description: '# of repos for resource',
          maximum: 10,
          minimum: 0,
          title: '# of repos for resource',
          type: 'integer',
        },
        RepoFork: {
          default: false,
          description: 'Repo Fork option enabled',
          title: 'Repo Fork option enabled',
          type: 'boolean',
        },
        MountingPoints: {
          default: false,
          description: 'Databricks mounting points enabled',
          title: 'Databricks mounting points enabled',
          type: 'boolean',
        },
        StorageCredential: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'Databricks mounting points enabled',
          title: 'Unity Catalog Storage Credential',
        },
        DbksAccessConnectorName: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'Azure Access Connector for databricks unity catalog',
          title: 'Unity Catalog Access Connector',
        },
        DbksAccessConnector: {
          default: false,
          description: 'Azure Access Connector for databricks unity catalog',
          title: 'Unity Catalog Access Connector',
          type: 'boolean',
        },
        UnityCatalog: {
          default: false,
          description: 'Unity Catalog feature',
          title: 'Unity Catalog feature',
          type: 'boolean',
        },
        Catalogs: {
          default: [],
          description: 'Unity Catalog list of catalogs',
          items: {
            $ref: '#/$defs/UcCatalog',
          },
          title: 'Unity Catalog list of catalogs',
          type: 'array',
        },
      },
      required: ['Name'],
      title: 'Databricks',
      type: 'object',
    },
    DatabricksINConfig: {
      properties: {
        kind: {
          const: 'databricks-in',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
        dataDistribution: {
          default: false,
          description: 'Enable data distribution for Databricks workspaces',
          title: 'Data Distribution',
          type: 'boolean',
          'ui:title': 'Data Distribution',
        },
      },
      required: ['kind'],
      title: 'DatabricksINConfig',
      type: 'object',
      'ui:options': {
        icon: 'databricks',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/etl-tools/databricks/',
          },
        ],
        tags: ['analytics', 'big-data', 'machine-learning'],
        teaser: 'Databricks analytics platform for big data and ML.',
      },
      'ui:title': 'Databricks IN',
    },
    DatabricksIn: {
      properties: {
        kind: {
          const: 'databricks_in',
          default: 'databricks_in',
          description: 'Databricks In Resource Kind',
          title: 'Databricks In Resource Kind',
          type: 'string',
        },
        Name: {
          description: 'The name of the Databricks workspace.',
          title: 'Workspace Name',
          type: 'string',
        },
        Subscription: {
          description: 'The subscription name associated with the Databricks.',
          title: 'Subscription Name',
          type: 'string',
        },
        SubscriptionID: {
          description: 'The subscription ID associated with the Databricks.',
          title: 'Subscription ID',
          type: 'string',
        },
        InPepSubnetName: {
          description: 'The name of the Public End Point (PEP) subnet.',
          title: 'PEP Subnet Name',
          type: 'string',
        },
        InPublicSubnetName: {
          description: 'The name of the public subnet.',
          title: 'Public Subnet Name',
          type: 'string',
        },
        InPrivateSubnetName: {
          description: 'The name of the private subnet.',
          title: 'Private Subnet Name',
          type: 'string',
        },
      },
      required: [
        'Name',
        'Subscription',
        'SubscriptionID',
        'InPepSubnetName',
        'InPublicSubnetName',
        'InPrivateSubnetName',
      ],
      title: 'DatabricksIn',
      type: 'object',
    },
    DatabricksModelCollectionName: {
      description:
        'Model collections are placeholders that help users discover available model\nfamilies and enable platform admins to track demand. All Databricks foundation models\nare always provisioned by default in the workspace.\n\n[documentation]: https://learn.microsoft.com/en-us/azure/databricks/machine-learning/model-serving/foundation-model-overview',
      enum: ['Anthropic-model-collection', 'Google-model-collection'],
      title: 'DatabricksModelCollectionName',
      type: 'string',
    },
    DatabricksPIConfig: {
      properties: {
        kind: {
          const: 'databricks',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
        natGateway: {
          default: false,
          description: 'Enable the NAT Gateway in databricks',
          title: 'NAT Gateway',
          type: 'boolean',
          'ui:title': 'NAT Gateway',
        },
        serverless: {
          default: false,
          description: 'Enable serverless for SQL Warehouse',
          title: 'Serverless for SQL Warehouse',
          type: 'boolean',
          'ui:title': 'Serverless for SQL Warehouse',
        },
        dataDistribution: {
          default: false,
          description: 'Enable data distribution for Databricks workspaces',
          title: 'Data Distribution',
          type: 'boolean',
          'ui:title': 'Data Distribution',
        },
        talk2data: {
          default: false,
          description: 'Enable Talk to Data feature in Databricks',
          title: 'Talk to Data',
          type: 'boolean',
          'ui:title': 'Talk to Data',
        },
        ask2sql: {
          default: false,
          description: 'Enable Ask to SQL feature in Databricks',
          title: 'Ask to SQL',
          type: 'boolean',
          'ui:title': 'Ask to SQL',
        },
        repoCount: {
          default: 1,
          description:
            'The number of repositories to create. By default, the repository count is set to 1.',
          maximum: 10,
          minimum: 1,
          title: 'Repo count',
          type: 'integer',
          'ui:title': 'Repository Count',
        },
      },
      required: ['kind'],
      title: 'DatabricksPIConfig',
      type: 'object',
      'ui:options': {
        icon: 'databricks',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/etl-tools/databricks/',
          },
        ],
        supportedTargets: ['L5'],
        tags: ['analytics', 'big-data', 'machine-learning'],
        teaser: 'Databricks analytics platform for big data and ML.',
      },
      'ui:title': 'Databricks',
    },
    DatabricksUcPermission: {
      properties: {
        kind: {
          const: 'databricks-uc-permission',
          default: 'databricks-uc-permission',
          description: 'Databricks Unified Catalog Permission Kind',
          title: 'Databricks Unified Catalog Permission Kind',
          type: 'string',
        },
        Catalogs: {
          default: [],
          description: 'The catalogs to which permissions are assigned',
          items: {
            type: 'string',
          },
          title: 'Catalogs',
          type: 'array',
        },
        Entitlements: {
          anyOf: [
            {
              items: {
                $ref: '#/$defs/DbksEntitlements',
              },
              type: 'array',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'The entitlements assigned to the group.',
          title: 'Entitlements',
        },
      },
      title: 'DatabricksUcPermission',
      type: 'object',
    },
    DatabricksWorkspaceResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'databricks-workspace',
          title: 'Kind',
          type: 'string',
        },
        managedResourceGroupId: {
          description:
            'The resource group ID where the Databricks workspace is managed.',
          title: 'Managed resource group ID',
          type: 'string',
        },
        workspaceId: {
          description: 'The ID of the Databricks workspace.',
          title: 'Workspace ID',
          type: 'string',
        },
        workspaceUrl: {
          description: 'The URL of the Databricks workspace.',
          title: 'Workspace URL',
          type: 'string',
        },
        publicSubnetName: {
          description:
            'The name of the public subnet associated with the Databricks workspace.',
          title: 'Public Subnet Name',
          type: 'string',
        },
        privateSubnetName: {
          description:
            'The name of the private subnet associated with the Databricks workspace.',
          title: 'Private Subnet Name',
          type: 'string',
        },
      },
      required: [
        'resourceId',
        'kind',
        'managedResourceGroupId',
        'workspaceId',
        'workspaceUrl',
        'publicSubnetName',
        'privateSubnetName',
      ],
      title: 'DatabricksWorkspaceResource',
      type: 'object',
    },
    DbksBindingType: {
      enum: ['BINDING_TYPE_READ_WRITE', 'BINDING_TYPE_READ_ONLY'],
      title: 'DbksBindingType',
      type: 'string',
    },
    DbksCatalogSource: {
      enum: ['DSP', 'EDL'],
      title: 'DbksCatalogSource',
      type: 'string',
    },
    DbksEntitlements: {
      description: 'Databricks entitlements enum',
      enum: [
        'workspace-access',
        'allow-cluster-create',
        'databricks-sql-access',
        'allow-instance-pool-create',
      ],
      title: 'DbksEntitlements',
      type: 'string',
    },
    DeployedInstanceLayout: {
      properties: {
        platformCell: {
          $ref: '#/$defs/InstanceCellRef',
          description: 'The cell that contains the platform services.',
          title: 'Platform cell',
        },
        productCells: {
          additionalProperties: {
            $ref: '#/$defs/InstanceCellRef',
          },
          description:
            'The cells that contain the product services, indexed by logical stage.',
          propertyNames: {
            $ref: '#/$defs/LogicalStage',
          },
          title: 'Product cells',
          type: 'object',
        },
      },
      required: ['platformCell', 'productCells'],
      title: 'DeployedInstanceLayout',
      type: 'object',
    },
    DeployedInstanceLayoutV2: {
      properties: {
        platformCell: {
          $ref: '#/$defs/InfraCellRefV2',
          description: 'The cell that contains the platform services.',
          title: 'Platform cell',
        },
        productCells: {
          description: 'The cells that contain the product services.',
          items: {
            $ref: '#/$defs/InfraCellRefV2',
          },
          title: 'Product cells',
          type: 'array',
        },
      },
      required: ['platformCell', 'productCells'],
      title: 'DeployedInstanceLayoutV2',
      type: 'object',
    },
    DeployedInstanceLayoutV3: {
      properties: {
        platformCell: {
          $ref: '#/$defs/InfraCellRefV3',
          description: 'The cell that contains the platform services.',
          title: 'Platform cell',
        },
        productCells: {
          description: 'The cells that contain the product services.',
          items: {
            $ref: '#/$defs/InfraCellRefV3',
          },
          title: 'Product cells',
          type: 'array',
        },
      },
      required: ['platformCell', 'productCells'],
      title: 'DeployedInstanceLayoutV3',
      type: 'object',
    },
    DeploymentTarget: {
      properties: {
        serviceConnection: {
          description:
            'The ID of the Azure DevOps service connection used for deployments.',
          title: 'Service Connection ID',
          type: 'string',
        },
        serviceConnectionId: {
          description:
            'The ID of the Azure DevOps service connection used for deployments.',
          title: 'Service Connection ID',
          type: 'string',
        },
        clusterName: {
          description:
            'The name of the Azure Kubernetes Service (AKS) cluster where the application is deployed.',
          title: 'Cluster Name',
          type: 'string',
        },
        clusterRg: {
          description:
            'The resource group of the Azure Kubernetes Service (AKS) cluster.',
          title: 'Cluster Resource Group',
          type: 'string',
        },
      },
      required: [
        'serviceConnection',
        'serviceConnectionId',
        'clusterName',
        'clusterRg',
      ],
      title: 'DeploymentTarget',
      type: 'object',
    },
    DeploymentTargetLevel: {
      description: 'Deployment target level.',
      enum: ['L4', 'L5', 'ALL'],
      title: 'DeploymentTargetLevel',
      type: 'string',
    },
    DevHubEntityName: {
      description: 'A valid name for a DevHub entity.',
      errorMessage: {
        pattern:
          'Must start with a lowercase letter, can contain lowercase letters, numbers, and hyphens, and hyphens must not be consecutive or at the start/end.',
      },
      maxLength: 32,
      minLength: 3,
      pattern: '^[a-z]+(?:-?[a-z0-9]+)+$',
      title: 'DevHub Entity Name',
      type: 'string',
    },
    DevHubManifestMetadata: {
      properties: {
        name: {
          $ref: '#/$defs/DevHubEntityName',
          description:
            'Name of the entity. May only contain lowercase letters and hyphens',
        },
        namespace: {
          $ref: '#/$defs/RFC1035Name',
          description: 'Namespace the entity is defined in.',
        },
        title: {
          default: '',
          description: 'A display name of the entity.',
          title: 'Title',
          type: 'string',
        },
        description: {
          default: '',
          description: 'A human readable description of the entity.',
          title: 'Description',
          type: 'string',
        },
        annotations: {
          additionalProperties: {
            type: 'string',
          },
          description: 'Annotations object regarding the resource.',
          title: 'Annotations',
          type: 'object',
        },
        labels: {
          additionalProperties: {
            type: 'string',
          },
          description: 'Labels object regarding the resource.',
          title: 'Labels',
          type: 'object',
        },
        role: {
          $ref: '#/$defs/ManifestRole',
          default: 'product',
          description: 'Role of the entity.',
          title: 'Role',
        },
      },
      required: ['name', 'namespace'],
      title: 'DevHubManifestMetadata',
      type: 'object',
    },
    DevHubProductBaseEntryDMZ: {
      properties: {
        stackVersion: {
          description:
            'The version of the infrastructure stack that provisioned the resources.',
          title: 'Stack Version',
          type: 'string',
        },
        signature: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Signature',
        },
        manifest: {
          $ref: '#/$defs/ProductManifestDevHubDMZV1Alpha1',
        },
        report: {
          $ref: '#/$defs/DevHubProductBaseReportDMZ',
        },
        policies: {
          $ref: '#/$defs/PolicyReport',
          title: 'Policies',
        },
        resources: {
          $ref: '#/$defs/ResourcesReport',
          title: 'Resources',
        },
      },
      required: ['stackVersion', 'manifest', 'report', 'policies', 'resources'],
      title: 'DevHubProductBaseEntryDMZ',
      type: 'object',
    },
    DevHubProductBaseReportDMZ: {
      description:
        'Instance-level (base) catalog report for a DMZ product.\n\nWritten to: products/{name}/base/info.json',
      properties: {
        apiVersion: {
          const: 'core.devhub.ai.dmz/v1alpha1',
          title: 'Apiversion',
          type: 'string',
        },
        kind: {
          const: 'ProductBaseCatalogEntry',
          default: 'ProductBaseCatalogEntry',
          title: 'Kind',
          type: 'string',
        },
        metadata: {
          $ref: '#/$defs/DevHubManifestMetadata',
          description: 'Basic metadata about the DMZ product.',
          title: 'Product metadata.',
        },
        accessitGroups: {
          default: [],
          description:
            'AccessIT Azure AD groups created by the instance stack for the DMZ product.',
          items: {
            $ref: '#/$defs/AccessITGroupInfo',
          },
          title: 'AccessIT Groups',
          type: 'array',
        },
        githubTeams: {
          default: [],
          description:
            'GitHub teams provisioned for the DMZ product (admin, developer, reader).',
          items: {
            $ref: '#/$defs/GitHubTeamInfo',
          },
          title: 'GitHub Teams',
          type: 'array',
        },
        codeLocations: {
          default: [],
          description:
            'Repository names that contribute workloads to this DMZ product.',
          items: {
            type: 'string',
          },
          title: 'Code Locations',
          type: 'array',
        },
      },
      required: ['apiVersion', 'metadata'],
      title: 'DevHubProductBaseReportDMZ',
      type: 'object',
    },
    DevHubProductEntry: {
      properties: {
        stackVersion: {
          description:
            'The version of the infrastructure stack that provisioned the resources.',
          title: 'Stack Version',
          type: 'string',
        },
        signature: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Signature',
        },
        manifest: {
          $ref: '#/$defs/ProductManifestDevHubV1Alpha1',
        },
        report: {
          $ref: '#/$defs/DevHubProductReport',
        },
        policies: {
          $ref: '#/$defs/PolicyReport',
          title: 'Policies',
        },
        resources: {
          $ref: '#/$defs/ResourcesReport',
          title: 'Resources',
        },
      },
      required: ['stackVersion', 'manifest', 'report', 'policies', 'resources'],
      title: 'DevHubProductEntry',
      type: 'object',
    },
    DevHubProductEntryDMZ: {
      properties: {
        stackVersion: {
          description:
            'The version of the infrastructure stack that provisioned the resources.',
          title: 'Stack Version',
          type: 'string',
        },
        signature: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Signature',
        },
        manifest: {
          $ref: '#/$defs/ProductManifestDevHubDMZV1Alpha1',
        },
        report: {
          $ref: '#/$defs/DevHubProductReportDMZ',
        },
        policies: {
          $ref: '#/$defs/PolicyReport',
          title: 'Policies',
        },
        resources: {
          $ref: '#/$defs/ResourcesReport',
          title: 'Resources',
        },
      },
      required: ['stackVersion', 'manifest', 'report', 'policies', 'resources'],
      title: 'DevHubProductEntryDMZ',
      type: 'object',
    },
    DevHubProductReport: {
      properties: {
        apiVersion: {
          const: 'core.devhub.ai/v1alpha1',
          title: 'Apiversion',
          type: 'string',
        },
        kind: {
          const: 'ProductCatalogEntry',
          title: 'Kind',
          type: 'string',
        },
        metadata: {
          $ref: '#/$defs/DevHubManifestMetadata',
          description: 'Basic metadata about the product.',
          title: 'Product metadata.',
        },
        identity: {
          anyOf: [
            {
              $ref: '#/$defs/IdentityInfo',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'Default identity to use for accessing product resources.',
          title: 'Product identity.',
        },
        k8sRelease: {
          anyOf: [
            {
              $ref: '#/$defs/K8sReleaseInfo',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'Basic information about K8s environment for the product.',
          title: 'K8s release info.',
        },
        resources: {
          default: [],
          description: 'Azure resources provisioned for the product.',
          items: {
            $ref: '#/$defs/AnyResource',
          },
          title: 'Product resources.',
          type: 'array',
        },
        routing: {
          $ref: '#/$defs/RoutingReport',
          description: 'List of exposed services.',
          title: 'Product routing.',
        },
        accessitGroups: {
          default: [],
          description:
            'Azure AD groups created by AccessIT platform for product access control and role assignments.',
          items: {
            $ref: '#/$defs/AccessITGroupInfo',
          },
          title: 'AccessIT Groups',
          type: 'array',
        },
      },
      required: ['apiVersion', 'kind', 'metadata', 'routing'],
      title: 'DevHubProductReport',
      type: 'object',
    },
    DevHubProductReportDMZ: {
      description:
        'Per-cell catalog report for a DMZ product.\n\nWritten to: products/{name}/instances/{cell}/info.json',
      properties: {
        apiVersion: {
          const: 'core.devhub.ai.dmz/v1alpha1',
          title: 'Apiversion',
          type: 'string',
        },
        kind: {
          const: 'ProductCatalogEntry',
          default: 'ProductCatalogEntry',
          title: 'Kind',
          type: 'string',
        },
        metadata: {
          $ref: '#/$defs/DevHubManifestMetadata',
          description: 'Basic metadata about the DMZ product.',
          title: 'Product metadata.',
        },
        cellId: {
          description:
            "The cell identifier (e.g. 'dev-dev', 'prod-prod') used to route the catalog entry to products/{name}/instances/{cell_id}/info.json. Set from ClusterReport.cell_id at synth time.",
          title: 'Cell ID',
          type: 'string',
        },
        identity: {
          anyOf: [
            {
              $ref: '#/$defs/IdentityInfo',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'Default managed identity for the DMZ product.',
          title: 'Product identity.',
        },
        k8sRelease: {
          anyOf: [
            {
              $ref: '#/$defs/K8sReleaseInfo',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'Kubernetes namespace and release information for the DMZ product.',
          title: 'K8s release info.',
        },
        resources: {
          default: [],
          description:
            'Azure resources provisioned for the DMZ product (RG, KV, modules).',
          items: {
            $ref: '#/$defs/AnyResource',
          },
          title: 'Product resources.',
          type: 'array',
        },
        routing: {
          anyOf: [
            {
              $ref: '#/$defs/RoutingReport',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'Front-proxy domain routing information for the DMZ product.',
          title: 'Product routing.',
        },
        accessitGroups: {
          default: [],
          description:
            'AccessIT Azure AD groups are managed separately for DMZ; left empty at deploy time.',
          items: {
            $ref: '#/$defs/AccessITGroupInfo',
          },
          title: 'AccessIT Groups',
          type: 'array',
        },
      },
      required: ['apiVersion', 'metadata', 'cellId'],
      title: 'DevHubProductReportDMZ',
      type: 'object',
    },
    DevHubProductSpec: {
      properties: {
        owner: {
          description:
            'AAD object ID or display name for the group owning the entity.',
          format: 'uuid',
          title: 'Owner group',
          type: 'string',
          'ui:field': 'AzureGroupSearch',
          'ui:title': 'AAD Group',
          'vsc:picker': 'aad-group',
        },
        primaryContact: {
          anyOf: [
            {
              format: 'uuid',
              type: 'string',
            },
            {
              format: 'email',
              type: 'string',
            },
          ],
          description: 'AAD object id or email of the primary contact person.',
          title: 'Primary contact',
          'ui:field': 'AzureUserSearch',
          'ui:title': 'AAD User',
          'vsc:picker': 'aad-user',
        },
        secondaryContact: {
          anyOf: [
            {
              format: 'uuid',
              type: 'string',
            },
            {
              format: 'email',
              type: 'string',
            },
          ],
          description:
            'AAD object id or email of the secondary contact person.',
          title: 'Secondary contact',
          'ui:field': 'AzureUserSearch',
          'ui:title': 'AAD User',
          'vsc:picker': 'aad-user',
        },
        uniqueKey: {
          $ref: '#/$defs/UniqueKeyType',
          description: 'A unique identifier for the entity.',
          title: 'Unique key.',
          'ui:disabled': true,
          'ui:title': 'Unique Key',
        },
        lifecycle: {
          $ref: '#/$defs/argus__provisioning__models__common__registry__Lifecycle',
          default: 'production',
          description: 'Lifecycle of the entity.',
          title: 'Entity lifecycle stage.',
        },
        featureFlags: {
          default: [],
          description:
            'Feature flags allow op-ting into experimental functionality in the stack',
          items: {
            $ref: '#/$defs/FeatureFlag',
          },
          title: 'Feature flags.',
          type: 'array',
          'vsc:action': 'array',
        },
        workspace: {
          description: 'Name of the workspace this product is managed by.',
          errorMessage: {
            pattern:
              'Must start with a lowercase letter, can contain lowercase letters, numbers, hyphens, and underscores.',
          },
          maxLength: 62,
          minLength: 1,
          pattern: '^[a-z]+(?:[-_]?[a-z0-9]+)+$',
          title: 'Workspace name',
          type: 'string',
        },
        resourceQuotaRequests: {
          $ref: '#/$defs/K8sResources',
          default: {
            cpu: '2000m',
            memory: '3Gi',
          },
          description: 'Requested Kubernetes resources for product namespace.',
          title: 'Resource requests',
        },
        resourceQuotaLimits: {
          $ref: '#/$defs/K8sResources',
          default: {
            cpu: '4000m',
            memory: '5Gi',
          },
          description:
            'Upper limit for Kubernetes resources for product namespace.',
          title: 'Resource limits',
        },
        location: {
          $ref: '#/$defs/AzureRegion',
          default: 'northeurope',
          description: 'Azure region where resources should be provisioned.',
          title: 'Location',
        },
        businessApplication: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'Business application for the product.',
          title: 'Business application',
        },
      },
      required: [
        'owner',
        'primaryContact',
        'secondaryContact',
        'uniqueKey',
        'workspace',
      ],
      title: 'DevHubProductSpec',
      type: 'object',
    },
    DevHubProductSpecDMZ: {
      description: 'DMZ-specific product specification.',
      properties: {
        owner: {
          description:
            'AAD object ID or display name for the group owning the entity.',
          format: 'uuid',
          title: 'Owner group',
          type: 'string',
          'ui:field': 'AzureGroupSearch',
          'ui:title': 'AAD Group',
          'vsc:picker': 'aad-group',
        },
        primaryContact: {
          anyOf: [
            {
              format: 'uuid',
              type: 'string',
            },
            {
              format: 'email',
              type: 'string',
            },
          ],
          description: 'AAD object id or email of the primary contact person.',
          title: 'Primary contact',
          'ui:field': 'AzureUserSearch',
          'ui:title': 'AAD User',
          'vsc:picker': 'aad-user',
        },
        secondaryContact: {
          anyOf: [
            {
              format: 'uuid',
              type: 'string',
            },
            {
              format: 'email',
              type: 'string',
            },
          ],
          description:
            'AAD object id or email of the secondary contact person.',
          title: 'Secondary contact',
          'ui:field': 'AzureUserSearch',
          'ui:title': 'AAD User',
          'vsc:picker': 'aad-user',
        },
        uniqueKey: {
          $ref: '#/$defs/UniqueKeyType',
          description: 'A unique identifier for the entity.',
          title: 'Unique key.',
          'ui:disabled': true,
          'ui:title': 'Unique Key',
        },
        lifecycle: {
          $ref: '#/$defs/argus__provisioning__models__common__registry__Lifecycle',
          default: 'production',
          description: 'Lifecycle of the entity.',
          title: 'Entity lifecycle stage.',
        },
        featureFlags: {
          default: [],
          description:
            'Feature flags allow op-ting into experimental functionality in the stack',
          items: {
            $ref: '#/$defs/FeatureFlag',
          },
          title: 'Feature flags.',
          type: 'array',
          'vsc:action': 'array',
        },
        workspace: {
          description: 'Name of the workspace this product is managed by.',
          errorMessage: {
            pattern:
              'Must start with a lowercase letter, can contain lowercase letters, numbers, hyphens, and underscores.',
          },
          maxLength: 62,
          minLength: 1,
          pattern: '^[a-z]+(?:[-_]?[a-z0-9]+)+$',
          title: 'Workspace name',
          type: 'string',
        },
        resourceQuotaRequests: {
          $ref: '#/$defs/K8sResources',
          default: {
            cpu: '2000m',
            memory: '3Gi',
          },
          description: 'Requested Kubernetes resources for product namespace.',
          title: 'Resource requests',
        },
        resourceQuotaLimits: {
          $ref: '#/$defs/K8sResources',
          default: {
            cpu: '4000m',
            memory: '5Gi',
          },
          description:
            'Upper limit for Kubernetes resources for product namespace.',
          title: 'Resource limits',
        },
        businessApplication: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'Business application for the product.',
          title: 'Business application',
        },
        location: {
          $ref: '#/$defs/AzureRegionDMZ',
          default: 'germanywestcentral',
          description: 'Azure region (DMZ-restricted to GermanyWestCentral).',
          title: 'Azure Region DMZ',
        },
      },
      required: [
        'owner',
        'primaryContact',
        'secondaryContact',
        'uniqueKey',
        'workspace',
      ],
      title: 'DevHubProductSpecDMZ',
      type: 'object',
    },
    DevHubStorageConfig: {
      properties: {
        config: {
          anyOf: [
            {
              $ref: '#/$defs/DevHubStorageConfigBase',
            },
            {
              $ref: '#/$defs/DevHubStorageConfigDict',
            },
          ],
          default: {
            externalAccess: false,
            hnsEnabled: true,
            location: 'northeurope',
            containers: [],
            accountDataContributors: [],
            containerRoleAssignments: [],
          },
          description: 'Configuration for the resource',
          title: 'Config',
          'ui:title': 'Select configuration type',
        },
        kind: {
          const: 'object-store',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
      },
      required: ['kind'],
      title: 'DevHubStorageConfig',
      type: 'object',
      'ui:options': {
        icon: 'blob-block',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/storage/blob-storage/',
          },
        ],
        supportedTargets: ['L5'],
        tags: ['storage', 'files'],
        teaser: 'Scalable object storage for data and files.',
      },
      'ui:title': 'Object Store',
    },
    DevHubStorageConfigBase: {
      description:
        'Allows provisioning storage containers within Azure storage accounts.\n\nThe `storage` capability provisions a [storage account] and enables users and\nmodules to request containers in that account.\n\n[storage account]: https://learn.microsoft.com/en-us/azure/storage/blobs/storage-blobs-introduction',
      properties: {
        externalAccess: {
          default: false,
          description:
            'Enable external access to the service via the product Gateway.',
          title: 'External access',
          type: 'boolean',
          'ui:title': 'External Access',
        },
        hnsEnabled: {
          default: true,
          description: 'Enable hierarchical namespace on the storage.',
          title: 'Hnsenabled',
          type: 'boolean',
          'ui:title': 'Hierarchical Namespace Enabled',
        },
        location: {
          default: 'northeurope',
          description: 'Specify where the resource is deployed.',
          title: 'Location',
          type: 'string',
          'ui:title': 'Location',
        },
        containers: {
          default: [],
          description:
            'Storage containers to be created in the account.\n\n### Warning: Reserved names\n\nDo not use the reserved container names `argus*`, `dagster`, `mlflow`, `databricks-data`.\nThis may lead to unexpected behavior until we have implemented conflict\nresolution on container names.',
          items: {
            $ref: '#/$defs/StorageContainerName',
          },
          title: 'Containers',
          type: 'array',
          'ui:title': 'Containers',
          'vsc:action': 'array',
        },
        accountDataContributors: {
          default: [],
          description:
            'Object IDs for all groups to get account level data access.',
          items: {
            $ref: '#/$defs/Principal',
          },
          title: 'Accountdatacontributors',
          type: 'array',
          'ui:title': 'Account Data Contributors',
          'vsc:action': 'array',
        },
        containerRoleAssignments: {
          default: [],
          description: 'Container specific role assignments.',
          items: {
            $ref: '#/$defs/DevHubStorageContainerRoleAssignment',
          },
          title: 'Containerroleassignments',
          type: 'array',
          'ui:title': 'Container Role Assignments',
          'vsc:action': 'array',
        },
      },
      title: 'DevHubStorageConfigBase',
      type: 'object',
      'ui:title': 'Base configuration',
    },
    DevHubStorageConfigDict: {
      description:
        'A staged configuration for the storage containers that belong to this cell.',
      properties: {
        development: {
          anyOf: [
            {
              $ref: '#/$defs/DevHubStorageConfigBase',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Development configuration',
        },
        production: {
          anyOf: [
            {
              $ref: '#/$defs/DevHubStorageConfigBase',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Production configuration',
        },
      },
      title: 'DevHubStorageConfigDict',
      type: 'object',
      'ui:title': 'Configuration per environment',
    },
    DevHubStorageContainerRole: {
      description:
        'Available storage roles to be assigned, see the [documentation] for details.\n\n[documentation]: https://learn.microsoft.com/en-us/azure/role-based-access-control/built-in-roles',
      enum: [
        'Storage Blob Data Reader',
        'Storage Blob Data Contributor',
        'ACL',
      ],
      title: 'DevHubStorageContainerRole',
      type: 'string',
    },
    DevHubStorageContainerRoleAssignment: {
      properties: {
        objectId: {
          description: 'AAD entity for which the role is to be assigned.',
          format: 'uuid',
          title: 'Objectid',
          type: 'string',
          'ui:title': 'Object ID',
        },
        containerName: {
          $ref: '#/$defs/StorageContainerName',
          description: 'The storage container this role applies to.',
          'ui:title': 'Container Name',
        },
        principalType: {
          $ref: '#/$defs/PrincipalType',
          'ui:title': 'Principal Type',
        },
        role: {
          $ref: '#/$defs/DevHubStorageContainerRole',
          description: 'Role to be assigned to principal.',
          'ui:title': 'Role',
        },
      },
      required: ['objectId', 'containerName', 'principalType', 'role'],
      title: 'DevHubStorageContainerRoleAssignment',
      type: 'object',
    },
    DevHubWorkspaceEntry: {
      properties: {
        stackVersion: {
          description:
            'The version of the infrastructure stack that provisioned the resources.',
          title: 'Stack Version',
          type: 'string',
        },
        signature: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Signature',
        },
        manifest: {
          $ref: '#/$defs/WorkspaceManifestDevHubV1Alpha1',
        },
        report: {
          $ref: '#/$defs/DevHubWorkspaceReport',
        },
        policies: {
          $ref: '#/$defs/PolicyReport',
          title: 'Policies',
        },
        resources: {
          $ref: '#/$defs/ResourcesReport',
          title: 'Resources',
        },
      },
      required: ['stackVersion', 'manifest', 'report', 'policies', 'resources'],
      title: 'DevHubWorkspaceEntry',
      type: 'object',
    },
    DevHubWorkspaceReport: {
      properties: {
        apiVersion: {
          const: 'core.devhub.ai/v1alpha1',
          title: 'Apiversion',
          type: 'string',
        },
        kind: {
          const: 'WorkspaceCatalogEntry',
          title: 'Kind',
          type: 'string',
        },
        metadata: {
          $ref: '#/$defs/DevHubManifestMetadata',
          description: 'Basic metadata about the workspace.',
          title: 'Workspace metadata.',
        },
        identity: {
          $ref: '#/$defs/IdentityInfo',
          description: 'Identity information for the workspace.',
          title: 'Workspace identity.',
        },
        application: {
          $ref: '#/$defs/ApplicationInfo',
          description: 'Application information for the workspace.',
          title: 'Workspace application.',
        },
        githubTeams: {
          default: [],
          description: 'GitHub teams associated with the workspace.',
          items: {
            $ref: '#/$defs/GitHubTeamInfo',
          },
          title: 'GitHub teams.',
          type: 'array',
        },
        resources: {
          default: [],
          description: 'Azure resources provisioned for the workspace.',
          items: {
            $ref: '#/$defs/AnyResource',
          },
          title: 'Workspace resources.',
          type: 'array',
        },
      },
      required: ['apiVersion', 'kind', 'metadata', 'identity', 'application'],
      title: 'DevHubWorkspaceReport',
      type: 'object',
    },
    DevOpsOutput: {
      properties: {
        projectUrl: {
          description:
            'The URL of the Azure DevOps project associated with the workspace.',
          title: 'Azure DevOps Project URL',
          type: 'string',
        },
        targetEnvironments: {
          additionalProperties: {
            $ref: '#/$defs/DeploymentTarget',
          },
          description:
            'Mapping of deployment targets for the workspace, keyed by environment name.',
          title: 'Deployment Targets',
          type: 'object',
        },
        variableGroups: {
          description:
            'List of Azure DevOps variable groups associated with the workspace.',
          items: {
            type: 'string',
          },
          title: 'Variable Groups',
          type: 'array',
        },
      },
      required: ['projectUrl', 'targetEnvironments', 'variableGroups'],
      title: 'DevOpsOutput',
      type: 'object',
    },
    DiagnosticSettings: {
      properties: {
        logs: {
          default: [],
          description: 'Logs of storage account.',
          items: {
            $ref: '#/$defs/DataFactoryLogType',
          },
          title: 'Logs of storage account',
          type: 'array',
          'ui:title': 'Logs',
        },
        metrics: {
          default: [],
          description: 'Metrics of storage account.',
          items: {
            $ref: '#/$defs/DataFactoryMetricType',
          },
          title: 'Metrics of storage account',
          type: 'array',
          'ui:title': 'Metrics',
        },
      },
      title: 'DiagnosticSettings',
      type: 'object',
    },
    DisplayName: {
      description: 'A string that is used to display the name in the UI.',
      minLength: 3,
      title: 'Display Name',
      type: 'string',
    },
    Domain: {
      enum: ['data', 'app', 'ai'],
      title: 'Domain',
      type: 'string',
    },
    DomainReport: {
      properties: {
        nodeId: {
          description: 'The ID of the node in the cluster.',
          title: 'Node ID',
          type: 'string',
        },
        cert: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Name of the certificate',
        },
        kvId: {
          anyOf: [
            {
              description: 'Canonical reference to resource.',
              title: 'Azure resource id.',
              type: 'string',
              'ui:title': 'Azure Resource ID',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Keyvault Resource ID',
        },
        role: {
          $ref: '#/$defs/InstanceCellKind',
          description: 'The role of the cell within the platform instance.',
          title: 'Role',
        },
        url: {
          description: 'The URL of the domain.',
          title: 'URL',
          type: 'string',
        },
        flavour: {
          $ref: '#/$defs/Domain',
          description: 'The flavour of the domain.',
          title: 'Flavour',
        },
      },
      required: ['nodeId', 'role', 'url', 'flavour'],
      title: 'DomainReport',
      type: 'object',
    },
    DspConfiguration: {
      properties: {
        LinkedProducts: {
          default: [],
          description:
            'Linked products associated with the DSP Product Manifest.',
          items: {
            $ref: '#/$defs/DspLinkedProduct',
          },
          title: 'Linked Products',
          type: 'array',
        },
        FirewallRules: {
          default: [],
          description:
            'Firewall rules associated with the DSP Product Manifest.',
          items: {
            discriminator: {
              mapping: {
                'adls-firewall-rule': '#/$defs/AdlsFirewallRule',
                'sql-firewall-rule': '#/$defs/SqlFirewallRule',
                'user-keyvault-firewall-rule': '#/$defs/KeyVaultFirewallRule',
              },
              propertyName: 'kind',
            },
            oneOf: [
              {
                $ref: '#/$defs/SqlFirewallRule',
              },
              {
                $ref: '#/$defs/KeyVaultFirewallRule',
              },
              {
                $ref: '#/$defs/AdlsFirewallRule',
              },
            ],
          },
          title: 'Firewall Rules',
          type: 'array',
        },
        DataSharings: {
          anyOf: [
            {
              items: {
                discriminator: {
                  mapping: {
                    'adls-datasharing': '#/$defs/AdlsDataSharing',
                    'sql-datasharing': '#/$defs/SqlDataSharing',
                  },
                  propertyName: 'kind',
                },
                oneOf: [
                  {
                    $ref: '#/$defs/AdlsDataSharing',
                  },
                  {
                    $ref: '#/$defs/SqlDataSharing',
                  },
                ],
              },
              type: 'array',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'Data sharing associated with the DSP Product Manifest.',
          title: 'Data Sharing',
        },
      },
      title: 'DspConfiguration',
      type: 'object',
    },
    DspGroup: {
      properties: {
        kind: {
          const: 'dsp-group',
          default: 'dsp-group',
          description: 'The kind of group.',
          title: 'Group Kind',
          type: 'string',
        },
        Name: {
          description: 'The name of the group.',
          title: 'Group Name',
          type: 'string',
        },
      },
      required: ['Name'],
      title: 'DspGroup',
      type: 'object',
    },
    DspIntegrationConfig: {
      properties: {
        name: {
          description: 'Name of the DSP to be linked',
          title: 'Name',
          type: 'string',
        },
      },
      required: ['name'],
      title: 'DspIntegrationConfig',
      type: 'object',
    },
    DspLinkedProduct: {
      properties: {
        Name: {
          description: 'The name of the linked product.',
          title: 'Linked Product Name',
          type: 'string',
        },
        ObjectID: {
          description: 'The object ID of the linked product',
          format: 'uuid',
          title: 'Object ID',
          type: 'string',
        },
      },
      required: ['Name', 'ObjectID'],
      title: 'DspLinkedProduct',
      type: 'object',
    },
    DspManifest: {
      description: 'DspManifest model.',
      properties: {
        apiVersion: {
          const: 'dsp/v1',
          default: 'dsp/v1',
          description: 'The version of the API for the DSP Product Manifest.',
          title: 'API Version',
          type: 'string',
        },
        kind: {
          const: 'DspManifest',
          default: 'DspManifest',
          description: 'The kind of the DSP Product Manifest.',
          title: 'Kind',
          type: 'string',
        },
        metadata: {
          $ref: '#/$defs/DspMetadata',
          description: 'Metadata associated with the DSP Product Manifest.',
          title: 'Metadata',
        },
        spec: {
          $ref: '#/$defs/DspSpec',
          description:
            'The specification manifest detailing the configuration and deployment details',
          title: 'Specification Manifest',
        },
        identities: {
          description: 'Identities associated with the DSP Product Manifest.',
          items: {
            discriminator: {
              mapping: {
                'custom-group': '#/$defs/CustomGroup',
                'dsp-group': '#/$defs/DspGroup',
                'functional-user': '#/$defs/FunctionalUser',
                'service-principal':
                  '#/$defs/dsp_deployment__models__manifests__identities__identities__ServicePrincipal',
              },
              propertyName: 'kind',
            },
            oneOf: [
              {
                $ref: '#/$defs/DspGroup',
              },
              {
                $ref: '#/$defs/CustomGroup',
              },
              {
                $ref: '#/$defs/dsp_deployment__models__manifests__identities__identities__ServicePrincipal',
              },
              {
                $ref: '#/$defs/FunctionalUser',
              },
            ],
          },
          title: 'Identities',
          type: 'array',
        },
        configuration: {
          $ref: '#/$defs/DspConfiguration',
          default: {},
          description: 'Configuration associated with the DSP Product Manifest',
          title: 'Configuration',
        },
        modules: {
          default: [],
          description: 'Modules associated with the DSP Product Manifest.',
          items: {
            discriminator: {
              mapping: {
                adf: '#/$defs/Adf',
                adls: '#/$defs/Adls',
                ai_box: '#/$defs/AIBox',
                ai_services: '#/$defs/AIServices',
                aml: '#/$defs/Aml',
                cognitive_search: '#/$defs/CognitiveSearch',
                databricks: '#/$defs/Databricks',
                databricks_in: '#/$defs/DatabricksIn',
                eventgrid: '#/$defs/EventGrid',
                log_analytics: '#/$defs/LogAnalytics',
                open_ai: '#/$defs/OpenAi',
                sql_server: '#/$defs/SqlServer',
                synapse: '#/$defs/Synapse',
              },
              propertyName: 'kind',
            },
            oneOf: [
              {
                $ref: '#/$defs/Databricks',
              },
              {
                $ref: '#/$defs/DatabricksIn',
              },
              {
                $ref: '#/$defs/Adf',
              },
              {
                $ref: '#/$defs/Synapse',
              },
              {
                $ref: '#/$defs/SqlServer',
              },
              {
                $ref: '#/$defs/Aml',
              },
              {
                $ref: '#/$defs/Adls',
              },
              {
                $ref: '#/$defs/OpenAi',
              },
              {
                $ref: '#/$defs/EventGrid',
              },
              {
                $ref: '#/$defs/CognitiveSearch',
              },
              {
                $ref: '#/$defs/AIBox',
              },
              {
                $ref: '#/$defs/AIServices',
              },
              {
                $ref: '#/$defs/LogAnalytics',
              },
            ],
          },
          title: 'Modules',
          type: 'array',
        },
      },
      required: ['kind', 'metadata', 'spec', 'identities', 'modules'],
      title: 'DspManifest',
      type: 'object',
    },
    DspMetadata: {
      properties: {
        name: {
          description: 'The name of the metadata entry',
          maxLength: 62,
          minLength: 1,
          pattern: '^[a-z]+(?:[-_]?[a-z0-9]+)+$',
          title: 'Name',
          type: 'string',
        },
        namespace: {
          description: 'The namespace of the metadata entry.',
          maxLength: 62,
          minLength: 1,
          pattern: '^[a-z]+(?:[-_]?[a-z0-9]+)+$',
          title: 'Namespace',
          type: 'string',
        },
        title: {
          default: '',
          description: 'A display name of the entity.',
          title: 'Title',
          type: 'string',
        },
        description: {
          default: '',
          description: 'A human readable description of the entity.',
          title: 'Description',
          type: 'string',
        },
        annotations: {
          $ref: '#/$defs/Annotations',
          description: 'Additional annotations for the metadata entry.',
        },
        labels: {
          additionalProperties: {
            type: 'string',
          },
          default: {},
          description: 'Labels object regarding the resource.',
          title: 'Labels',
          type: 'object',
        },
      },
      required: ['name', 'namespace', 'annotations'],
      title: 'DspMetadata',
      type: 'object',
    },
    DspSpec: {
      properties: {
        lifecycle: {
          $ref: '#/$defs/dsp_deployment__models__manifests___spec_choices__Lifecycle',
          description:
            'The lifecycle stage of the deployment (e.g., production, development).',
        },
        blueprintVersion: {
          description:
            'The version of the blueprint associated with the metadata',
          title: 'Blueprint Version',
          type: 'string',
        },
        environment: {
          $ref: '#/$defs/Environments',
          description:
            'Environment of the deployment (e.g., PROD, QUAL, DEV, TEST).',
          title: 'Environment',
        },
        reposDeployment: {
          description:
            'Flag indicating whether repositories deployment is enabled.',
          title: 'Repositories Deployment',
          type: 'boolean',
        },
        name: {
          description: 'The name of the deployment.',
          title: 'Name',
          type: 'string',
        },
        owner: {
          description: 'The owner (email) of the deployment.',
          title: 'Owner',
          type: 'string',
        },
        ownerUID: {
          description: 'The unique identifier of the owner.',
          title: 'Owner UID',
          type: 'string',
        },
        costCenter: {
          description: 'The cost center associated with the deployment.',
          maxLength: 20,
          minLength: 1,
          pattern: '^[a-zA-Z0-9\\.]*$',
          title: 'Cost Center',
          type: 'string',
        },
        companyCode: {
          description: 'The company code associated with the deployment.',
          maxLength: 20,
          minLength: 1,
          pattern: '^[a-zA-Z0-9]*$',
          title: 'Company Code',
          type: 'string',
        },
        companyName: {
          description:
            'The name of the company associated with the deployment.',
          maxLength: 60,
          minLength: 1,
          title: 'Company Name',
          type: 'string',
        },
        purpose: {
          description: 'The purpose of the deployment.',
          title: 'Purpose',
          type: 'string',
        },
        configItem: {
          description: 'The configuration item associated with the deployment.',
          title: 'Configuration Item',
          type: 'string',
        },
        serviceLevelAgreement: {
          $ref: '#/$defs/ServiceLevelAgreement',
          description: 'The service level agreement for the deployment.',
          title: 'Service Level Agreement',
        },
        type: {
          $ref: '#/$defs/DspType',
          description: 'The type of deployment (e.g., External, Internal).',
          title: 'Type',
        },
        serviceLevel: {
          $ref: '#/$defs/ServiceLevel',
          description: 'The service level for the deployment.',
          title: 'Service Level',
        },
        region: {
          $ref: '#/$defs/Region',
          description: 'The region where the deployment is located.',
        },
        description: {
          description: 'A description of the deployment.',
          title: 'Description',
          type: 'string',
        },
        subscriptionID: {
          $ref: '#/$defs/SubscriptionID',
          description: 'The subscription ID associated with the deployment.',
          title: 'Subscription ID',
        },
        executionPlanConfiguration: {
          $ref: '#/$defs/ExecutionPlanConfiguration',
          description: 'The configuration for the execution plan.',
          title: 'Execution Plan Configuration',
        },
      },
      required: [
        'lifecycle',
        'blueprintVersion',
        'environment',
        'reposDeployment',
        'name',
        'owner',
        'ownerUID',
        'costCenter',
        'companyCode',
        'companyName',
        'purpose',
        'configItem',
        'serviceLevelAgreement',
        'type',
        'serviceLevel',
        'region',
        'description',
        'subscriptionID',
        'executionPlanConfiguration',
      ],
      title: 'DspSpec',
      type: 'object',
    },
    DspType: {
      description: 'Type of deployment',
      enum: ['Regular', 'External', 'Internal'],
      title: 'DspType',
      type: 'string',
    },
    EgressListenerConfig: {
      properties: {
        name: {
          description: 'Name of the listener.',
          title: 'Name',
          type: 'string',
        },
        route: {
          $ref: '#/$defs/HttpRoute',
          description: 'HTTP route configuration.',
          title: 'Route',
        },
        port: {
          description: 'Port for the listener.',
          title: 'Port',
          type: 'integer',
        },
        credential: {
          $ref: '#/$defs/UpstreamCredential',
          description: 'Credential for the upstream.',
          title: 'Credential',
        },
      },
      required: ['name', 'route', 'port', 'credential'],
      title: 'EgressListenerConfig',
      type: 'object',
    },
    EntityName: {
      description: 'A valid entity name.',
      errorMessage: {
        pattern:
          'Must start with a lowercase letter, can contain lowercase letters, numbers, and hyphens.',
      },
      maxLength: 253,
      minLength: 3,
      pattern: '^[a-z]+(?:-?[a-z0-9]+)+$',
      title: 'Entity name',
      type: 'string',
    },
    Environments: {
      description: 'Lifecycle of the model',
      enum: ['PROD', 'QUAL', 'DEV', 'TEST'],
      title: 'Environments',
      type: 'string',
    },
    EnvoyConfigBase: {
      properties: {},
      title: 'EnvoyConfigBase',
      type: 'object',
    },
    EventGrid: {
      properties: {
        kind: {
          const: 'eventgrid',
          default: 'eventgrid',
          description: 'EventGrid Resource Kind',
          title: 'EventGrid Resource Kind',
          type: 'string',
        },
        Name: {
          description: 'EventGrid Resource Name',
          title: 'EventGrid Resource Name',
          type: 'string',
        },
      },
      required: ['Name'],
      title: 'EventGrid',
      type: 'object',
    },
    EventgridConfig: {
      description: 'Configuration for the Eventgrid module.',
      properties: {
        kind: {
          const: 'eventgrid',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
      },
      required: ['kind'],
      title: 'EventgridConfig',
      type: 'object',
      'ui:options': {
        icon: 'event-grid',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/etl-tools/eventgrid/',
          },
        ],
        supportedTargets: ['L5'],
        tags: ['events', 'messaging', 'integration'],
        teaser: 'Azure Event Grid for event-driven architectures.',
      },
      'ui:title': 'Event Grid',
    },
    EventgridResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'event-grid',
          title: 'Kind',
          type: 'string',
        },
        location: {
          description: 'Azure region where the event grid is deployed.',
          title: 'Azure region',
          type: 'string',
        },
      },
      required: ['resourceId', 'kind', 'location'],
      title: 'EventgridResource',
      type: 'object',
    },
    ExecutionPlanConfiguration: {
      properties: {
        Infra: {
          $ref: '#/$defs/InfraConfigurationPlan',
          description: 'Configuration plan for infrastructure deployments.',
          title: 'Infrastructure Configuration Plan',
        },
      },
      required: ['Infra'],
      title: 'ExecutionPlanConfiguration',
      type: 'object',
    },
    ExposedServiceConfig: {
      properties: {
        name: {
          description: 'Name af the K8s service inside the product namespace.',
          errorMessage: {
            pattern: 'Must match RFC1035 or be in the allowed exceptions list.',
          },
          examples: ['k8s-service-name'],
          maxLength: 62,
          minLength: 1,
          pattern:
            'prodai_ada|ai_store|digital_twin|prodai_nasa|competitor_intelligence|app_dev_enablement|scott_solutions|spinal_os|dob_aisol|advanced_imaging|forecasting_platform|data_and_ai_academy|ai_solutions|gb_gtu|^[a-z]+(?:-?[a-z0-9]+)+$',
          title: 'RFC1035 name with exceptions',
          type: 'string',
        },
        port: {
          $ref: '#/$defs/PortNumber',
          default: 80,
          description: 'Target port of the service.',
        },
        domains: {
          default: ['app'],
          description: 'The domains this service is available at.',
          items: {
            $ref: '#/$defs/Domain',
          },
          title: 'Domains',
          type: 'array',
        },
        routes: {
          description: 'Routes exposed on the service.',
          items: {
            $ref: '#/$defs/RoutingRule',
          },
          title: 'Routes',
          type: 'array',
        },
      },
      required: ['name', 'routes'],
      title: 'ExposedServiceConfig',
      type: 'object',
    },
    ExposedServiceEntry: {
      properties: {
        name: {
          description: 'Name of the service in Kubernetes.',
          title: 'Name',
          type: 'string',
        },
        urls: {
          default: [],
          description: 'URLs for the service.',
          items: {
            $ref: '#/$defs/ExposedServiceUrl',
          },
          title: 'Urls',
          type: 'array',
        },
      },
      required: ['name'],
      title: 'ExposedServiceEntry',
      type: 'object',
    },
    ExposedServiceUrl: {
      properties: {
        url: {
          description: 'URL for the service.',
          title: 'Url',
          type: 'string',
        },
        allowedGroups: {
          default: [],
          description: 'Groups that have access to this URL',
          items: {
            $ref: '#/$defs/Group',
          },
          title: 'Allowedgroups',
          type: 'array',
          'ui:title': 'Groups',
          'vsc:action': 'array',
          'vsc:picker': 'aad-group',
        },
        allowedServicePrincipals: {
          anyOf: [
            {
              description: 'Service principals that have access to this URL',
              items: {
                $ref: '#/$defs/argus__provisioning__models__types__fields__ServicePrincipal',
              },
              type: 'array',
              'ui:title': 'Service Principals',
              'vsc:action': 'array',
              'vsc:picker': 'aad-serviceprincipal',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Allowedserviceprincipals',
        },
        domain: {
          $ref: '#/$defs/Domain',
          default: 'app',
          description: 'The domain this URL is available at.',
        },
      },
      required: ['url'],
      title: 'ExposedServiceUrl',
      type: 'object',
    },
    ExtensionConfig: {
      properties: {
        appstore: {
          default: [],
          description: 'AppStore deployment configuration',
          items: {
            $ref: '#/$defs/AppStoreDeployment',
          },
          title: 'Appstore',
          type: 'array',
        },
        dsp: {
          anyOf: [
            {
              $ref: '#/$defs/DspIntegrationConfig',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'DSP integration configuration',
        },
        aigateway: {
          anyOf: [
            {
              $ref: '#/$defs/AIGatewayConfig',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'AI Gateway extension configuration',
        },
      },
      title: 'ExtensionConfig',
      type: 'object',
    },
    FeatureFlag: {
      enum: [
        'github-env-next',
        'managed-report-types',
        'structured-logging',
        'appstore-deployment',
        'nsg-migration',
        'accessit-dmz-migration',
        'keyvault-rbac-authorization',
      ],
      title: 'FeatureFlag',
      type: 'string',
    },
    FunctionalUser: {
      properties: {
        kind: {
          const: 'functional-user',
          default: 'functional-user',
          description: 'The kind of identity.',
          title: 'Service Principal Kind',
          type: 'string',
        },
        Name: {
          description: 'The name of the Functional user.',
          title: 'User Name',
          type: 'string',
        },
        Keyvault: {
          description: 'The keyvault to store the secrets.',
          title: 'Keyvault',
          type: 'string',
        },
        Databases: {
          description: 'The list of databases the user has access to.',
          items: {
            type: 'string',
          },
          minItems: 1,
          title: 'Databases',
          type: 'array',
        },
      },
      required: ['Name', 'Keyvault', 'Databases'],
      title: 'FunctionalUser',
      type: 'object',
    },
    GatewayEntry: {
      properties: {
        stackVersion: {
          description:
            'The version of the infrastructure stack that provisioned the resources.',
          title: 'Stack Version',
          type: 'string',
        },
        signature: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'A signature that can be used to validate the integrity of the data.',
          title: 'Signature',
        },
        output: {
          $ref: '#/$defs/GatewayReport',
        },
      },
      required: ['stackVersion', 'output'],
      title: 'GatewayEntry',
      type: 'object',
    },
    GatewayReport: {
      properties: {
        kind: {
          description: 'The kind of the gateway.',
          title: 'Kind',
          type: 'string',
        },
        domains: {
          description: 'List of domains exposed by the gateway.',
          items: {
            $ref: '#/$defs/DomainReport',
          },
          title: 'Domains',
          type: 'array',
        },
        customDomains: {
          default: [],
          description: 'List of additional custom domains',
          items: {
            $ref: '#/$defs/CustomDomainReport',
          },
          title: 'Customdomains',
          type: 'array',
        },
      },
      required: ['kind', 'domains'],
      title: 'GatewayReport',
      type: 'object',
    },
    GitHubEnterpriseEntry: {
      properties: {
        stackVersion: {
          description:
            'The version of the infrastructure stack that provisioned the resources.',
          title: 'Stack Version',
          type: 'string',
        },
        signature: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'A signature that can be used to validate the integrity of the data.',
          title: 'Signature',
        },
        output: {
          $ref: '#/$defs/GitHubEnterpriseReport',
        },
        input: {
          $ref: '#/$defs/GitHubEnterpriseManifest',
        },
        policies: {
          $ref: '#/$defs/PolicyReport',
          title: 'Policies.',
        },
      },
      required: ['stackVersion', 'output', 'input', 'policies'],
      title: 'GitHubEnterpriseEntry',
      type: 'object',
    },
    GitHubEnterpriseManifest: {
      properties: {
        apiVersion: {
          const: 'core.argus.ai/v1alpha1',
          default: 'core.argus.ai/v1alpha1',
          title: 'Apiversion',
          type: 'string',
        },
        kind: {
          const: 'GitHubEnterpriseManifest',
          default: 'GitHubEnterpriseManifest',
          title: 'Kind',
          type: 'string',
        },
        metadata: {
          $ref: '#/$defs/Metadata',
        },
        spec: {
          $ref: '#/$defs/GitHubEnterpriseSpec',
        },
      },
      required: ['spec', 'metadata', 'apiVersion', 'kind'],
      title: 'GitHubEnterpriseManifest',
      type: 'object',
    },
    GitHubEnterpriseReport: {
      properties: {
        registered: {
          $ref: '#/$defs/GitHubMembership',
        },
        idNameMap: {
          description: 'A list of tuples containing the group id and name',
          items: {
            maxItems: 2,
            minItems: 2,
            prefixItems: [
              {
                type: 'string',
              },
              {
                type: 'string',
              },
            ],
            type: 'array',
          },
          title: 'Idnamemap',
          type: 'array',
        },
      },
      required: ['registered', 'idNameMap'],
      title: 'GitHubEnterpriseReport',
      type: 'object',
    },
    GitHubEnterpriseSpec: {
      properties: {
        teams: {
          description: 'The teams in the organization.',
          items: {
            $ref: '#/$defs/GitHubTeamConfig',
          },
          title: 'Teams',
          type: 'array',
        },
      },
      required: ['teams'],
      title: 'GitHubEnterpriseSpec',
      type: 'object',
    },
    GitHubMembership: {
      properties: {
        users: {
          items: {
            format: 'uuid',
            type: 'string',
          },
          title: 'Users',
          type: 'array',
        },
        groups: {
          items: {
            format: 'uuid',
            type: 'string',
          },
          title: 'Groups',
          type: 'array',
        },
      },
      required: ['users', 'groups'],
      title: 'GitHubMembership',
      type: 'object',
    },
    GitHubTeamConfig: {
      properties: {
        name: {
          description: 'The name of the team.',
          title: 'Team name',
          type: 'string',
        },
        description: {
          description: 'The description of the team.',
          title: 'Description',
          type: 'string',
        },
        objectId: {
          description:
            'The object ID of the group that represents the team in AAD.',
          format: 'uuid',
          title: 'Objectid',
          type: 'string',
        },
        role: {
          $ref: '#/$defs/TeamRole',
          default: 'developers',
          description: 'The role of the team.',
        },
      },
      required: ['name', 'description', 'objectId'],
      title: 'GitHubTeamConfig',
      type: 'object',
    },
    GitHubTeamInfo: {
      properties: {
        ghId: {
          description: 'The GitHub ID of the team.',
          title: 'GitHub team ID',
          type: 'string',
        },
        slug: {
          description: 'The slug of the team.',
          title: 'GitHub team slug',
          type: 'string',
        },
        mappedObjectId: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'The object ID of the group that represents the team in AAD.',
          title: 'Mapped object ID',
        },
        ghEmuId: {
          anyOf: [
            {
              type: 'integer',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'The GitHub Enterprise Managed Users ID of the team.',
          title: 'GitHub EMU team ID',
        },
        role: {
          $ref: '#/$defs/TeamRole',
          default: 'developers',
          description: 'The role of the team.',
          title: 'Team role',
        },
      },
      required: ['ghId', 'slug'],
      title: 'GitHubTeamInfo',
      type: 'object',
    },
    Group: {
      format: 'uuid',
      type: 'string',
    },
    GroupInfo: {
      properties: {
        objectId: {
          description:
            'The unique identifier for the group in Azure Active Directory.',
          title: 'Group object ID.',
          type: 'string',
        },
        displayName: {
          description:
            'The display name of the group in Azure Active Directory.',
          title: 'Group display name',
          type: 'string',
        },
      },
      required: ['objectId', 'displayName'],
      title: 'GroupInfo',
      type: 'object',
    },
    HTTPRouteTimeouts: {
      properties: {
        request: {
          description: 'Requests timeout in seconds.',
          title: 'Requests',
          type: 'integer',
        },
        backend_request: {
          description: 'Backend request timeout in seconds.',
          title: 'Backend Request',
          type: 'integer',
        },
      },
      required: ['request', 'backend_request'],
      title: 'HTTPRouteTimeouts',
      type: 'object',
    },
    HttpHeaderMatch: {
      properties: {
        type: {
          $ref: '#/$defs/HttpHeaderMatchType',
          description:
            'Type specifies how to match against the value of the header.',
          title: 'Type',
        },
        name: {
          description:
            'Name is the name of the HTTP Header to be matched. Name matching MUST be case insensitive.',
          title: 'Name',
          type: 'string',
        },
        value: {
          description: 'VValue is the value of HTTP Header to be matched.',
          title: 'Value',
          type: 'string',
        },
      },
      required: ['type', 'name', 'value'],
      title: 'HttpHeaderMatch',
      type: 'object',
    },
    HttpHeaderMatchType: {
      enum: ['exact'],
      title: 'HttpHeaderMatchType',
      type: 'string',
    },
    HttpPathMatch: {
      properties: {
        type: {
          $ref: '#/$defs/HttpPathMatchType',
          description: 'Type specifies how to match against the path Value.',
          title: 'Type',
        },
        value: {
          description: 'Value of the HTTP path to match against.',
          title: 'Value',
          type: 'string',
        },
      },
      required: ['type', 'value'],
      title: 'HttpPathMatch',
      type: 'object',
    },
    HttpPathMatchType: {
      description:
        'PathMatchType specifies the semantics of how HTTP paths should be compared',
      enum: ['exact', 'path-prefix'],
      title: 'HttpPathMatchType',
      type: 'string',
    },
    HttpRoute: {
      properties: {
        hostnames: {
          description: 'List of hostnames.',
          items: {
            type: 'string',
          },
          title: 'Hostnames',
          type: 'array',
        },
        rules: {
          description: 'List of rules.',
          items: {
            $ref: '#/$defs/HttpRouteRule',
          },
          title: 'Rules',
          type: 'array',
        },
      },
      required: ['hostnames', 'rules'],
      title: 'HttpRoute',
      type: 'object',
    },
    HttpRouteMatch: {
      description:
        'HTTPRouteMatch defines the predicate used to match requests to a given action.\n\nMultiple match types are ANDed together, i.e. the match will evaluate to true only\nif all conditions are satisfied.',
      properties: {
        path: {
          $ref: '#/$defs/HttpPathMatch',
          default: {
            type: 'path-prefix',
            value: '/',
          },
          title: 'Path',
        },
        headers: {
          anyOf: [
            {
              items: {
                $ref: '#/$defs/HttpHeaderMatch',
              },
              type: 'array',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Headers',
        },
        typed_per_filter_config: {
          anyOf: [
            {
              additionalProperties: {
                $ref: '#/$defs/EnvoyConfigBase',
              },
              type: 'object',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Typed Per Filter Config',
        },
      },
      title: 'HttpRouteMatch',
      type: 'object',
    },
    HttpRouteRule: {
      description:
        'A routing rule to route HTTP requests to a backend.\n\nHTTPRouteRule defines semantics for matching an HTTP request based on conditions (matches),\nprocessing it (filters), and forwarding the request to an API object (backend_refs).\n\nBased on The k8s Gateway APIs [HTTPRouteRule].\n\n[HTTPRouteRule]: https://gateway-api.sigs.k8s.io/reference/spec/#gateway.networking.k8s.io/v1.HTTPRouteRule',
      properties: {
        name: {
          description: 'Name of the rule.',
          title: 'Rule name',
          type: 'string',
        },
        matches: {
          items: {
            $ref: '#/$defs/HttpRouteMatch',
          },
          title: 'Matches',
          type: 'array',
        },
        backend_refs: {
          description: 'List of backends.',
          items: {
            anyOf: [
              {
                $ref: '#/$defs/BackendRef',
              },
              {
                $ref: '#/$defs/UpstreamRef',
              },
            ],
          },
          title: 'Backends',
          type: 'array',
        },
        timeouts: {
          anyOf: [
            {
              $ref: '#/$defs/HTTPRouteTimeouts',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'Timeout in seconds.',
          title: 'Timeout',
        },
        auto_host_rewrite: {
          anyOf: [
            {
              type: 'boolean',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'If True, the host header will be swapped with the hostname of the upstream host.',
          title: 'Auto Host Rewrite',
        },
      },
      required: ['name', 'backend_refs'],
      title: 'HttpRouteRule',
      type: 'object',
    },
    IdentityInfo: {
      properties: {
        kind: {
          const: 'identity',
          title: 'Kind',
          type: 'string',
        },
        clientId: {
          description:
            'The client ID of the identity in Azure Active Directory.',
          title: 'Identity client ID',
          type: 'string',
        },
        objectId: {
          description:
            'The object ID of the identity in Azure Active Directory.',
          title: 'Identity object ID',
          type: 'string',
        },
        displayName: {
          description:
            'The display name of the identity in Azure Active Directory.',
          title: 'Identity display name.',
          type: 'string',
        },
      },
      required: ['kind', 'clientId', 'objectId', 'displayName'],
      title: 'IdentityInfo',
      type: 'object',
    },
    InfraCellRefV2: {
      description:
        'A reference to a infrastructure cell associated with a logical stage, a network area and a azure region.',
      properties: {
        stage: {
          $ref: '#/$defs/LogicalStage',
          description:
            'The logical stage describes the order of a cell within a deployment sequence.',
          title: 'logical stage',
        },
        networkArea: {
          $ref: '#/$defs/SubscriptionNetworkType',
          description: 'The network area of the cell.',
        },
        azureRegion: {
          $ref: '#/$defs/CellAzureRegion',
          description: 'The azure region the cell is deployed to.',
        },
        name: {
          description: 'The globally unique name of the cell.',
          title: 'name',
          type: 'string',
        },
      },
      required: ['stage', 'networkArea', 'azureRegion', 'name'],
      title: 'InfraCellRefV2',
      type: 'object',
    },
    InfraCellRefV3: {
      description:
        'A reference to an infrastructure cell including stage, network area and azure region.\n\nThis class inherits the identifying triple from `InfraCellIdentifier` so the\n(stage, network_area, azure_region) identity is consistent across versions.',
      properties: {
        stage: {
          $ref: '#/$defs/LogicalStage',
          description:
            'The logical stage describes the order of a cell within a deployment sequence.',
          title: 'logical stage',
        },
        networkArea: {
          $ref: '#/$defs/SubscriptionNetworkType',
          description: 'The network area of the cell.',
        },
        azureRegion: {
          $ref: '#/$defs/CellAzureRegion',
          description: 'The azure region the cell is deployed to.',
        },
        name: {
          description: 'The globally unique name of the cell.',
          title: 'name',
          type: 'string',
        },
        kubernetesCluster: {
          anyOf: [
            {
              $ref: '#/$defs/KubernetesClusterRef',
            },
            {
              type: 'null',
            },
          ],
          default: null,
        },
      },
      required: ['stage', 'networkArea', 'azureRegion', 'name'],
      title: 'InfraCellRefV3',
      type: 'object',
    },
    InfraCellSubscriptionRefDMZ: {
      properties: {
        stage: {
          $ref: '#/$defs/LogicalStage',
          description:
            'The logical stage describes the order of a cell within a deployment sequence.',
          title: 'logical stage',
        },
        networkArea: {
          const: 'DMZ',
          description: 'The network area of the cell.',
          title: 'Networkarea',
          type: 'string',
        },
        azureRegion: {
          $ref: '#/$defs/CellAzureRegion',
          description: 'The azure region the cell is deployed to.',
        },
        name: {
          description: 'The globally unique name of the cell.',
          title: 'name',
          type: 'string',
        },
        kubernetesCluster: {
          anyOf: [
            {
              $ref: '#/$defs/KubernetesClusterRef',
            },
            {
              type: 'null',
            },
          ],
          default: null,
        },
        subscriptions: {
          description: 'The azure DMZ subscriptions associated with the cell.',
          items: {
            $ref: '#/$defs/ProductSubscriptionRefPE',
          },
          title: 'subscriptions',
          type: 'array',
        },
        subscriptionsIntranet: {
          description: 'The azure IN subscriptions associated with the cell.',
          items: {
            $ref: '#/$defs/ProductSubscriptionRefVnet',
          },
          title: 'intranet subscriptions',
          type: 'array',
        },
      },
      required: [
        'stage',
        'networkArea',
        'azureRegion',
        'name',
        'subscriptions',
        'subscriptionsIntranet',
      ],
      title: 'InfraCellSubscriptionRefDMZ',
      type: 'object',
    },
    InfraCellSubscriptionRefPI: {
      properties: {
        stage: {
          $ref: '#/$defs/LogicalStage',
          description:
            'The logical stage describes the order of a cell within a deployment sequence.',
          title: 'logical stage',
        },
        networkArea: {
          const: 'PI',
          description: 'The network area of the cell.',
          title: 'Networkarea',
          type: 'string',
        },
        azureRegion: {
          $ref: '#/$defs/CellAzureRegion',
          description: 'The azure region the cell is deployed to.',
        },
        name: {
          description: 'The globally unique name of the cell.',
          title: 'name',
          type: 'string',
        },
        kubernetesCluster: {
          anyOf: [
            {
              $ref: '#/$defs/KubernetesClusterRef',
            },
            {
              type: 'null',
            },
          ],
          default: null,
        },
        subscriptions: {
          description: 'The azure PI subscriptions associated with the cell.',
          items: {
            $ref: '#/$defs/ProductSubscriptionRefVnet',
          },
          title: 'subscriptions',
          type: 'array',
        },
        subscriptionsIntranet: {
          description: 'The azure IN subscriptions associated with the cell.',
          items: {
            $ref: '#/$defs/ProductSubscriptionRefVnet',
          },
          title: 'intranet subscriptions',
          type: 'array',
        },
      },
      required: [
        'stage',
        'networkArea',
        'azureRegion',
        'name',
        'subscriptions',
        'subscriptionsIntranet',
      ],
      title: 'InfraCellSubscriptionRefPI',
      type: 'object',
    },
    InfraCellSubscriptionRefV3: {
      properties: {
        stage: {
          $ref: '#/$defs/LogicalStage',
          description:
            'The logical stage describes the order of a cell within a deployment sequence.',
          title: 'logical stage',
        },
        networkArea: {
          $ref: '#/$defs/SubscriptionNetworkType',
          description: 'The network area of the cell.',
        },
        azureRegion: {
          $ref: '#/$defs/CellAzureRegion',
          description: 'The azure region the cell is deployed to.',
        },
        name: {
          description: 'The globally unique name of the cell.',
          title: 'name',
          type: 'string',
        },
        kubernetesCluster: {
          anyOf: [
            {
              $ref: '#/$defs/KubernetesClusterRef',
            },
            {
              type: 'null',
            },
          ],
          default: null,
        },
        subscriptions: {
          description:
            'The azure PI/AG subscriptions associated with the cell.',
          items: {
            $ref: '#/$defs/ProductSubscriptionRefV2',
          },
          title: 'subscriptions',
          type: 'array',
        },
        subscriptionsIntranet: {
          description: 'The azure IN subscriptions associated with the cell.',
          items: {
            $ref: '#/$defs/ProductSubscriptionRefV2',
          },
          title: 'in subscriptions',
          type: 'array',
        },
      },
      required: [
        'stage',
        'networkArea',
        'azureRegion',
        'name',
        'subscriptions',
        'subscriptionsIntranet',
      ],
      title: 'InfraCellSubscriptionRefV3',
      type: 'object',
    },
    InfraConfigurationPlan: {
      properties: {
        DatabricksDeployment: {
          default: false,
          description:
            'Flag indicating whether Databricks deployment is enabled.',
          title: 'Databricks Deployment',
          type: 'boolean',
        },
        DatabricksINDeployment: {
          default: false,
          description:
            'Flag indicating whether Databricks IN deployment is enabled.',
          title: 'Databricks IN Deployment',
          type: 'boolean',
        },
        ADFDeployment: {
          default: false,
          description:
            'Flag indicating whether Azure Data Factory deployment is enabled.',
          title: 'ADF Deployment',
          type: 'boolean',
        },
        SQLServerDeployment: {
          default: false,
          description:
            'Flag indicating whether SQL Server deployment is enabled.',
          title: 'SQL Server Deployment',
          type: 'boolean',
        },
        SynapseDeployment: {
          default: false,
          description: 'Flag indicating whether Synapse deployment is enabled.',
          title: 'Synapse Deployment',
          type: 'boolean',
        },
        ADLSDeployment: {
          default: false,
          description:
            'Flag indicating whether Azure Data Lake Storage deployment is enabled.',
          title: 'ADLS Deployment',
          type: 'boolean',
        },
        AMLDeployment: {
          default: false,
          description:
            'Flag indicating whether Azure Machine Learning deployment is enabled.',
          title: 'AML Deployment',
          type: 'boolean',
        },
        OpenAIDeployment: {
          default: false,
          description: 'Flag indicating whether OpenAI deployment is enabled.',
          title: 'OpenAI Deployment',
          type: 'boolean',
        },
        EventgridDeployment: {
          default: false,
          description:
            'Flag indicating whether Event Grid deployment is enabled.',
          title: 'Event Grid Deployment',
          type: 'boolean',
        },
        CognitiveSearchDeployment: {
          default: false,
          description:
            'Flag indicating whether Cognitive Search deployment is enabled.',
          title: 'Cognitive Search Deployment',
          type: 'boolean',
        },
        LogAnalyticsDeployment: {
          default: false,
          description:
            'Flag indicating whether Log Analytics deployment is enabled.',
          title: 'Log Analytics Deployment',
          type: 'boolean',
        },
        AiBoxDeployment: {
          default: false,
          description:
            "Indicates whether the AI Box is deployed. Set to 'True' if AI Box deployment is required.",
          title: 'AI Box Deployment',
          type: 'boolean',
        },
        AIServicesDeployment: {
          default: false,
          description:
            "Indicates whether AI Services are deployed. Set to 'True' if AI Services deployment is required.",
          title: 'AI Services Deployment',
          type: 'boolean',
        },
      },
      title: 'InfraConfigurationPlan',
      type: 'object',
    },
    InfrastructureCellEntry: {
      properties: {
        stackVersion: {
          description:
            'The version of the infrastructure stack that provisioned the resources.',
          title: 'Stack Version',
          type: 'string',
        },
        signature: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'A signature that can be used to validate the integrity of the data.',
          title: 'Signature',
        },
        output: {
          $ref: '#/$defs/InfrastructureCellReport',
        },
      },
      required: ['stackVersion', 'output'],
      title: 'InfrastructureCellEntry',
      type: 'object',
    },
    InfrastructureCellReport: {
      properties: {
        cellId: {
          description: 'The ID of the node in the instance.',
          title: 'Cell ID',
          type: 'string',
        },
        kind: {
          $ref: '#/$defs/InstanceCellKind',
          description: 'The role of the cell within the platform instance.',
          title: 'Role',
        },
        logicalStage: {
          $ref: '#/$defs/LogicalStage',
          description: 'The logical stage of the cell.',
          title: 'Logical stage',
        },
        cluster: {
          $ref: '#/$defs/K8sClusterResource',
          description: 'The cluster information.',
        },
        network: {
          anyOf: [
            {
              $ref: '#/$defs/NetworkReport',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            "The network information. None for AirGap/DMZ clusters that don't have public networking.",
        },
      },
      required: ['cellId', 'kind', 'logicalStage', 'cluster'],
      title: 'InfrastructureCellReport',
      type: 'object',
    },
    InstanceCellEntry: {
      properties: {
        stackVersion: {
          description:
            'The version of the infrastructure stack that provisioned the resources.',
          title: 'Stack Version',
          type: 'string',
        },
        signature: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Signature',
        },
        manifest: {
          $ref: '#/$defs/ProductCellManifest',
        },
        report: {
          $ref: '#/$defs/InstanceCellReport',
        },
        policies: {
          $ref: '#/$defs/PolicyReport',
          title: 'Policies',
        },
        resources: {
          $ref: '#/$defs/ResourcesReport',
          title: 'Resources',
        },
      },
      required: ['stackVersion', 'manifest', 'report', 'policies', 'resources'],
      title: 'InstanceCellEntry',
      type: 'object',
    },
    InstanceCellKind: {
      enum: ['platform', 'product'],
      title: 'InstanceCellKind',
      type: 'string',
    },
    InstanceCellRef: {
      properties: {
        kind: {
          $ref: '#/$defs/InstanceCellKind',
          description: 'The kind of the cell.',
          title: 'Cell kind',
        },
        cellId: {
          description: 'The unique identifier of the cell.',
          title: 'Cell ID',
          type: 'string',
        },
      },
      required: ['kind', 'cellId'],
      title: 'InstanceCellRef',
      type: 'object',
    },
    InstanceCellReport: {
      properties: {
        cellId: {
          description: 'The ID of the node in the instance.',
          title: 'Cell ID',
          type: 'string',
        },
        kind: {
          $ref: '#/$defs/InstanceCellKind',
          description: 'The role of the cell within the platform instance.',
          title: 'Role',
        },
        logicalStage: {
          $ref: '#/$defs/LogicalStage',
          description: 'The logical stage of the cell.',
          title: 'Logical stage',
        },
        subscriptions: {
          description: 'Subscriptions the cell is deployed to.',
          items: {
            $ref: '#/$defs/InstanceCellSubscription',
          },
          title: 'Subscriptions',
          type: 'array',
        },
        clusters: {
          description: 'Clusters deployed to instance cell.',
          items: {
            $ref: '#/$defs/CellClusterReport',
          },
          title: 'Cluster',
          type: 'array',
        },
      },
      required: ['cellId', 'kind', 'logicalStage', 'subscriptions', 'clusters'],
      title: 'InstanceCellReport',
      type: 'object',
    },
    InstanceCellSubscription: {
      properties: {
        id: {
          title: 'Id',
          type: 'string',
        },
        name: {
          title: 'Name',
          type: 'string',
        },
        kind: {
          $ref: '#/$defs/SubscriptionKind',
          description: 'The kind of the subscription.',
          title: 'Subscription kind',
        },
      },
      required: ['id', 'name', 'kind'],
      title: 'InstanceCellSubscription',
      type: 'object',
    },
    InstanceConfigV3: {
      properties: {
        config: {
          anyOf: [
            {
              $ref: '#/$defs/InstanceSpecV3',
            },
            {
              $ref: '#/$defs/StagedConfigDict_InstanceSpecV3_',
            },
          ],
          description: 'Configuration for the resource',
          title: 'Config',
        },
        stages: {
          default: [],
          description: 'Globally unique names for the instances.',
          items: {
            $ref: '#/$defs/InfraCellRefV2',
          },
          title: 'Instance names',
          type: 'array',
        },
      },
      required: ['config'],
      title: 'InstanceConfigV3',
      type: 'object',
    },
    InstanceConfigV4: {
      properties: {
        config: {
          anyOf: [
            {
              $ref: '#/$defs/InstanceSpecV4',
            },
            {
              $ref: '#/$defs/StagedConfigDict_InstanceSpecV4_',
            },
          ],
          description: 'Configuration for the resource',
          title: 'Config',
        },
        stages: {
          default: [],
          description: 'Globally unique names for the instances.',
          items: {
            $ref: '#/$defs/InfraCellRefV3',
          },
          title: 'Instance names',
          type: 'array',
        },
      },
      required: ['config'],
      title: 'InstanceConfigV4',
      type: 'object',
    },
    InstanceConfigV5: {
      properties: {
        config: {
          anyOf: [
            {
              $ref: '#/$defs/InstanceSpecV5',
            },
            {
              $ref: '#/$defs/StagedConfigDict_InstanceSpecV5_',
            },
          ],
          description: 'Configuration for the resource',
          title: 'Config',
        },
        stages: {
          default: [],
          description: 'Globally unique names for the instances.',
          items: {
            $ref: '#/$defs/InfraCellRefV3',
          },
          title: 'Instance names',
          type: 'array',
        },
      },
      required: ['config'],
      title: 'InstanceConfigV5',
      type: 'object',
    },
    InstanceEntryV3: {
      properties: {
        stackVersion: {
          description:
            'The version of the infrastructure stack that provisioned the resources.',
          title: 'Stack Version',
          type: 'string',
        },
        signature: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Signature',
        },
        manifest: {
          $ref: '#/$defs/PlatformManifestDevhubV1',
        },
        report: {
          $ref: '#/$defs/InstanceReportV2',
        },
        policies: {
          $ref: '#/$defs/PolicyReport',
          title: 'Policies',
        },
        resources: {
          $ref: '#/$defs/ResourcesReport',
          title: 'Resources',
        },
      },
      required: ['stackVersion', 'manifest', 'report', 'policies', 'resources'],
      title: 'InstanceEntryV3',
      type: 'object',
    },
    InstanceEntryV4: {
      properties: {
        stackVersion: {
          description:
            'The version of the infrastructure stack that provisioned the resources.',
          title: 'Stack Version',
          type: 'string',
        },
        signature: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Signature',
        },
        manifest: {
          $ref: '#/$defs/PlatformManifestDevhubV2',
        },
        report: {
          $ref: '#/$defs/InstanceReportV3',
        },
        policies: {
          $ref: '#/$defs/PolicyReport',
          title: 'Policies',
        },
        resources: {
          $ref: '#/$defs/ResourcesReport',
          title: 'Resources',
        },
      },
      required: ['stackVersion', 'manifest', 'report', 'policies', 'resources'],
      title: 'InstanceEntryV4',
      type: 'object',
    },
    InstanceEntryV5: {
      properties: {
        stackVersion: {
          description:
            'The version of the infrastructure stack that provisioned the resources.',
          title: 'Stack Version',
          type: 'string',
        },
        signature: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Signature',
        },
        manifest: {
          $ref: '#/$defs/PlatformManifestDevhubV3',
        },
        report: {
          $ref: '#/$defs/InstanceReportV4',
        },
        policies: {
          $ref: '#/$defs/PolicyReport',
          title: 'Policies',
        },
        resources: {
          $ref: '#/$defs/ResourcesReport',
          title: 'Resources',
        },
      },
      required: ['stackVersion', 'manifest', 'report', 'policies', 'resources'],
      title: 'InstanceEntryV5',
      type: 'object',
    },
    InstanceGroups: {
      description:
        'Entry ID groups that are used across cells within a platform instance.',
      properties: {
        workspaces: {
          $ref: '#/$defs/GroupInfo',
        },
        workspacePrincipals: {
          $ref: '#/$defs/GroupInfo',
          description:
            "Group containing all service principals for the instance's workspaces.",
          title: 'Workspace principals',
        },
        products: {
          $ref: '#/$defs/GroupInfo',
        },
        productPrincipals: {
          $ref: '#/$defs/GroupInfo',
          description:
            "Group containing all service principals for the instance's products.",
          title: 'Product principals',
        },
        kubelets: {
          $ref: '#/$defs/GroupInfo',
        },
        subscriptionDefaultPrincipals: {
          $ref: '#/$defs/GroupInfo',
          description:
            "Group containing all contributors to the instance's product/workspace subscriptions.",
          title: 'Subscription contributors',
        },
        portalGroupWrapper: {
          $ref: '#/$defs/GroupInfo',
        },
        sqlAgent: {
          $ref: '#/$defs/GroupInfo',
        },
      },
      required: [
        'workspaces',
        'workspacePrincipals',
        'products',
        'productPrincipals',
        'kubelets',
        'subscriptionDefaultPrincipals',
        'portalGroupWrapper',
        'sqlAgent',
      ],
      title: 'InstanceGroups',
      type: 'object',
    },
    InstanceLayoutV3: {
      properties: {
        platform: {
          $ref: '#/$defs/InfraCellRefV2',
        },
        products: {
          items: {
            $ref: '#/$defs/InfraCellSubscriptionRefV3',
          },
          title: 'Products',
          type: 'array',
        },
      },
      required: ['platform', 'products'],
      title: 'InstanceLayoutV3',
      type: 'object',
    },
    InstanceLayoutV4: {
      description:
        'Report layout that supports DMZ cells through the union in common.registry.',
      properties: {
        platform: {
          $ref: '#/$defs/InfraCellRefV3',
        },
        products: {
          items: {
            $ref: '#/$defs/AnyInfraCellSubscriptionRef',
          },
          title: 'Products',
          type: 'array',
        },
      },
      required: ['platform', 'products'],
      title: 'InstanceLayoutV4',
      type: 'object',
    },
    InstanceRegistryReport: {
      properties: {
        repository: {
          description: 'The name of the git repository.',
          title: 'Repository',
          type: 'string',
        },
        catalogRepository: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'The name of the git repository for the catalog.',
          title: 'Catalog repository',
        },
        agentsGroupObjectId: {
          description: 'The object ID of the service principal group.',
          title: 'Agents group object ID',
          type: 'string',
        },
        workspaceAgent: {
          $ref: '#/$defs/AgentReport',
          description: 'The service principal acting as the workspace agent.',
          title: 'Workspace agent',
        },
        agents: {
          description:
            'The service principals acting as agents to deploy into instance cells.',
          items: {
            $ref: '#/$defs/RegistryAgentReport',
          },
          title: 'Agents',
          type: 'array',
        },
        productSubscriptions: {
          description:
            'The base information about the product PI subscriptions.',
          items: {
            $ref: '#/$defs/ProductSubscriptionBaseReport',
          },
          title: 'Product subscriptions PI',
          type: 'array',
        },
        productSubscriptionsIntranet: {
          description:
            'The base information about the product IN subscriptions.',
          items: {
            $ref: '#/$defs/ProductSubscriptionBaseReport',
          },
          title: 'Product subscriptions IN',
          type: 'array',
        },
      },
      required: [
        'repository',
        'agentsGroupObjectId',
        'workspaceAgent',
        'agents',
        'productSubscriptions',
        'productSubscriptionsIntranet',
      ],
      title: 'InstanceRegistryReport',
      type: 'object',
    },
    InstanceRegistryReportV2: {
      properties: {
        repository: {
          description: 'The name of the git repository.',
          title: 'Repository',
          type: 'string',
        },
        catalogRepository: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'The name of the git repository for the catalog.',
          title: 'Catalog repository',
        },
        agentsGroupObjectId: {
          description: 'The object ID of the service principal group.',
          title: 'Agents group object ID',
          type: 'string',
        },
        workspaceAgent: {
          $ref: '#/$defs/AgentReport',
          description: 'The service principal acting as the workspace agent.',
          title: 'Workspace agent',
        },
        agents: {
          description:
            'The service principals acting as agents to deploy into instance cells.',
          items: {
            $ref: '#/$defs/RegistryAgentReport',
          },
          title: 'Agents',
          type: 'array',
        },
        productSubscriptions: {
          description:
            'The base information about the product PI/AG subscriptions.',
          items: {
            $ref: '#/$defs/ProductSubscriptionBaseReportV2',
          },
          title: 'Product subscriptions PI/AG',
          type: 'array',
        },
        productSubscriptionsIntranet: {
          description:
            'The base information about the product IN subscriptions.',
          items: {
            $ref: '#/$defs/ProductSubscriptionBaseReportV2',
          },
          title: 'Product subscriptions IN',
          type: 'array',
        },
      },
      required: [
        'repository',
        'agentsGroupObjectId',
        'workspaceAgent',
        'agents',
        'productSubscriptions',
        'productSubscriptionsIntranet',
      ],
      title: 'InstanceRegistryReportV2',
      type: 'object',
    },
    InstanceRegistryReportV3: {
      properties: {
        repository: {
          description: 'The name of the git repository.',
          title: 'Repository',
          type: 'string',
        },
        catalogRepository: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'The name of the git repository for the catalog.',
          title: 'Catalog repository',
        },
        agentsGroupObjectId: {
          description: 'The object ID of the service principal group.',
          title: 'Agents group object ID',
          type: 'string',
        },
        workspaceAgent: {
          $ref: '#/$defs/AgentReport',
          description: 'The service principal acting as the workspace agent.',
          title: 'Workspace agent',
        },
        agents: {
          description:
            'The service principals acting as agents to deploy into instance cells.',
          items: {
            $ref: '#/$defs/RegistryAgentReport',
          },
          title: 'Agents',
          type: 'array',
        },
        productSubscriptions: {
          description:
            'The base information about the product PI/AG subscriptions.',
          items: {
            $ref: '#/$defs/AnyProductSubscriptionBaseReport',
          },
          title: 'Product subscriptions',
          type: 'array',
        },
        productSubscriptionsIntranet: {
          description:
            'The base information about the product IN subscriptions.',
          items: {
            $ref: '#/$defs/AnyProductSubscriptionBaseReport',
          },
          title: 'Product subscription intranet',
          type: 'array',
        },
      },
      required: [
        'repository',
        'agentsGroupObjectId',
        'workspaceAgent',
        'agents',
        'productSubscriptions',
        'productSubscriptionsIntranet',
      ],
      title: 'InstanceRegistryReportV3',
      type: 'object',
    },
    InstanceRegistryReportV4: {
      properties: {
        repository: {
          description: 'The name of the git repository.',
          title: 'Repository',
          type: 'string',
        },
        catalogRepository: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'The name of the git repository for the catalog.',
          title: 'Catalog repository',
        },
        agentsGroupObjectId: {
          description: 'The object ID of the service principal group.',
          title: 'Agents group object ID',
          type: 'string',
        },
        workspaceAgent: {
          $ref: '#/$defs/AgentReport',
          description: 'The service principal acting as the workspace agent.',
          title: 'Workspace agent',
        },
        agents: {
          description:
            'The service principals acting as agents to deploy into instance cells.',
          items: {
            $ref: '#/$defs/RegistryAgentReport',
          },
          title: 'Agents',
          type: 'array',
        },
        workspaceSubscriptions: {
          description:
            'The base information about the workspace subscriptions.',
          items: {
            $ref: '#/$defs/WorkspaceSubscriptionBaseReport',
          },
          title: 'Workspace subscriptions',
          type: 'array',
        },
        productSubscriptions: {
          description:
            'The base information about the product PI/AG subscriptions.',
          items: {
            $ref: '#/$defs/AnyProductSubscriptionBaseReport',
          },
          title: 'Product subscriptions',
          type: 'array',
        },
        productSubscriptionsIntranet: {
          description:
            'The base information about the product IN subscriptions.',
          items: {
            $ref: '#/$defs/AnyProductSubscriptionBaseReport',
          },
          title: 'Product subscription intranet',
          type: 'array',
        },
      },
      required: [
        'repository',
        'agentsGroupObjectId',
        'workspaceAgent',
        'agents',
        'workspaceSubscriptions',
        'productSubscriptions',
        'productSubscriptionsIntranet',
      ],
      title: 'InstanceRegistryReportV4',
      type: 'object',
    },
    InstanceReportV2: {
      properties: {
        name: {
          title: 'Name',
          type: 'string',
        },
        logicalStage: {
          $ref: '#/$defs/LogicalStage',
          description: 'The logical stage of the platform instance.',
          title: 'Logical stage',
        },
        layout: {
          $ref: '#/$defs/DeployedInstanceLayoutV2',
          title: 'Instance layout',
        },
        platformCells: {
          default: [],
          description: 'The cells deployed in the platform instance.',
          items: {
            $ref: '#/$defs/InstanceCellReport',
          },
          title: 'Platform cells',
          type: 'array',
        },
        registry: {
          $ref: '#/$defs/InstanceRegistryReportV2',
        },
        groups: {
          $ref: '#/$defs/InstanceGroups',
        },
        resources: {
          $ref: '#/$defs/InstanceResources',
        },
      },
      required: [
        'name',
        'logicalStage',
        'layout',
        'registry',
        'groups',
        'resources',
      ],
      title: 'InstanceReportV2',
      type: 'object',
    },
    InstanceReportV3: {
      properties: {
        name: {
          title: 'Name',
          type: 'string',
        },
        logicalStage: {
          $ref: '#/$defs/LogicalStage',
          description: 'The logical stage of the platform instance.',
          title: 'Logical stage',
        },
        layout: {
          $ref: '#/$defs/DeployedInstanceLayoutV3',
          title: 'Instance layout',
        },
        platformCells: {
          default: [],
          description: 'The cells deployed in the platform instance.',
          items: {
            $ref: '#/$defs/InstanceCellReport',
          },
          title: 'Platform cells',
          type: 'array',
        },
        registry: {
          $ref: '#/$defs/InstanceRegistryReportV3',
        },
        groups: {
          $ref: '#/$defs/InstanceGroups',
        },
        resources: {
          $ref: '#/$defs/InstanceResources',
        },
      },
      required: [
        'name',
        'logicalStage',
        'layout',
        'registry',
        'groups',
        'resources',
      ],
      title: 'InstanceReportV3',
      type: 'object',
    },
    InstanceReportV4: {
      properties: {
        name: {
          title: 'Name',
          type: 'string',
        },
        logicalStage: {
          $ref: '#/$defs/LogicalStage',
          description: 'The logical stage of the platform instance.',
          title: 'Logical stage',
        },
        layout: {
          $ref: '#/$defs/DeployedInstanceLayoutV3',
          title: 'Instance layout',
        },
        platformCells: {
          default: [],
          description: 'The cells deployed in the platform instance.',
          items: {
            $ref: '#/$defs/InstanceCellReport',
          },
          title: 'Platform cells',
          type: 'array',
        },
        registry: {
          $ref: '#/$defs/InstanceRegistryReportV4',
        },
        groups: {
          $ref: '#/$defs/InstanceGroups',
        },
        resources: {
          $ref: '#/$defs/InstanceResources',
        },
      },
      required: [
        'name',
        'logicalStage',
        'layout',
        'registry',
        'groups',
        'resources',
      ],
      title: 'InstanceReportV4',
      type: 'object',
    },
    InstanceResources: {
      properties: {
        ociRegistry: {
          $ref: '#/$defs/AcrResource',
          description:
            'An Azure Container Registry shared across the platform instance.',
          title: 'Container registry',
        },
        portal: {
          anyOf: [
            {
              $ref: '#/$defs/PortalResources',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Portal resources',
        },
        legacy: {
          $ref: '#/$defs/LegacyResources',
          title: 'Legacy resources',
        },
      },
      required: ['ociRegistry', 'legacy'],
      title: 'InstanceResources',
      type: 'object',
    },
    InstanceSpecV3: {
      properties: {
        name: {
          description: 'Globally unique name for the instance.',
          title: 'Instance name',
          type: 'string',
        },
        location: {
          $ref: '#/$defs/AzureRegion',
          default: 'westeurope',
          description: 'Azure region where resources should be provisioned.',
          title: 'Location',
        },
        workspaceSubscription: {
          $ref: '#/$defs/SubscriptionRefV2',
          description: 'Reference to the workspace subscription.',
        },
        productCells: {
          default: [],
          description: 'List of product cells.',
          items: {
            $ref: '#/$defs/InfraCellSubscriptionRefV3',
          },
          title: 'Product cells',
          type: 'array',
        },
        teams: {
          default: [],
          description:
            'Teams that are assigned permissions on the platform instance.',
          items: {
            $ref: '#/$defs/PlatformInstanceTeam',
          },
          title: 'Platform teams',
          type: 'array',
        },
      },
      required: ['name', 'workspaceSubscription'],
      title: 'InstanceSpecV3',
      type: 'object',
    },
    InstanceSpecV4: {
      properties: {
        name: {
          description: 'Globally unique name for the instance.',
          title: 'Instance name',
          type: 'string',
        },
        location: {
          $ref: '#/$defs/AzureRegion',
          default: 'westeurope',
          description: 'Azure region where resources should be provisioned.',
          title: 'Location',
        },
        workspaceSubscription: {
          $ref: '#/$defs/SubscriptionRefV2',
          description: 'Reference to the workspace subscription.',
        },
        platformCell: {
          $ref: '#/$defs/PlatformCell',
          description: 'Reference to the platform cell.',
          title: 'Platform cell',
        },
        productCells: {
          default: [],
          description: 'List of product cells.',
          items: {
            $ref: '#/$defs/AnyInfraCellSubscriptionRef',
          },
          title: 'Product cells',
          type: 'array',
        },
        teams: {
          default: [],
          description:
            'Teams that are assigned permissions on the platform instance.',
          items: {
            $ref: '#/$defs/PlatformInstanceTeam',
          },
          title: 'Platform teams',
          type: 'array',
        },
      },
      required: ['name', 'workspaceSubscription', 'platformCell'],
      title: 'InstanceSpecV4',
      type: 'object',
    },
    InstanceSpecV5: {
      properties: {
        name: {
          description: 'Globally unique name for the instance.',
          title: 'Instance name',
          type: 'string',
        },
        location: {
          $ref: '#/$defs/AzureRegion',
          default: 'westeurope',
          description: 'Azure region where resources should be provisioned.',
          title: 'Location',
        },
        workspaceSubscriptions: {
          description: 'References to the workspace subscriptions.',
          items: {
            $ref: '#/$defs/SubscriptionRefV2',
          },
          title: 'Workspacesubscriptions',
          type: 'array',
        },
        platformCell: {
          $ref: '#/$defs/PlatformCell',
          description: 'Reference to the platform cell.',
          title: 'Platform cell',
        },
        productCells: {
          default: [],
          description: 'List of product cells.',
          items: {
            $ref: '#/$defs/AnyInfraCellSubscriptionRef',
          },
          title: 'Product cells',
          type: 'array',
        },
        teams: {
          default: [],
          description:
            'Teams that are assigned permissions on the platform instance.',
          items: {
            $ref: '#/$defs/PlatformInstanceTeam',
          },
          title: 'Platform teams',
          type: 'array',
        },
      },
      required: ['name', 'workspaceSubscriptions', 'platformCell'],
      title: 'InstanceSpecV5',
      type: 'object',
    },
    JupyterHubConfig: {
      description:
        'Web-based interactive development environment for notebooks, code, and data.',
      properties: {
        kind: {
          const: 'jupyter',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
        imageTag: {
          default: '1.2.2',
          description: 'Jupyter hub docker image tag to be used',
          title: 'Imagetag',
          type: 'string',
          'ui:title': 'Image Tag',
        },
        environments: {
          default: [
            {
              image: 'jupyter-environment-datascience',
              tag: 'latest',
              displayName: 'default data science notebook',
              description:
                'Default notebook containing various popular data science libraries.',
            },
          ],
          description:
            'List of user environments users can choose to run their notebook in.',
          items: {
            $ref: '#/$defs/UserEnvironment',
          },
          title: 'Environments',
          type: 'array',
          'ui:title': 'User Environments',
        },
      },
      required: ['kind'],
      title: 'JupyterHubConfig',
      type: 'object',
      'ui:options': {
        icon: 'jupyter',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/ai-analytics/jupyterhub/',
          },
        ],
        supportedTargets: ['L5'],
        tags: ['notebook', 'data-science'],
        teaser:
          'Web-based interactive development environment for notebooks, code, and data.',
      },
      'ui:title': 'Jupyter',
    },
    K8sClusterResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        location: {
          description: 'Azure Region where the cluster is provisioned.',
          title: 'Azure region',
          type: 'string',
        },
        kubernetesVersion: {
          description: 'Kubernetes version installed in the cluster.',
          title: 'Kubernetes version',
          type: 'string',
        },
        kubernetesOrchestratorVersion: {
          description: 'Kubernetes orchestrator version',
          title: 'Kubernetes orchestrator version',
          type: 'string',
        },
        fqdn: {
          description:
            'The fully qualified domain name of the Kubernetes cluster.',
          title: 'FQDN',
          type: 'string',
        },
        oidcIssuerUrl: {
          description: 'The OpenID Connect issuer URL for the cluster.',
          title: 'OIDC issuer URL',
          type: 'string',
        },
        kubeletObjectId: {
          description:
            'The object ID of the kubelet identity associated with the cluster.',
          title: 'Kubelet object ID',
          type: 'string',
        },
      },
      required: [
        'resourceId',
        'location',
        'kubernetesVersion',
        'kubernetesOrchestratorVersion',
        'fqdn',
        'oidcIssuerUrl',
        'kubeletObjectId',
      ],
      title: 'K8sClusterResource',
      type: 'object',
    },
    K8sCpuType: {
      errorMessage: {
        pattern: 'Must be a valid Kubernetes CPU value (e.g., 100m, 1, 2.5).',
      },
      pattern: '^(([0-9]+[.])?[0-9]+)(m)?$',
      type: 'string',
    },
    K8sMemoryType: {
      errorMessage: {
        pattern:
          'Must be a valid Kubernetes memory value (e.g., 512Mi, 2Gi, 1000).',
      },
      pattern: '^([0-9]+)(Ki|Ei|Pi|Ti|Gi|Mi|[EPTGMKmk])?$',
      type: 'string',
    },
    K8sReleaseInfo: {
      properties: {
        namespace: {
          title: 'Namespace',
          type: 'string',
        },
      },
      required: ['namespace'],
      title: 'K8sReleaseInfo',
      type: 'object',
    },
    K8sResourceConfig: {
      properties: {
        requests: {
          $ref: '#/$defs/K8sResources',
          description: 'Default resource requests.',
        },
        limits: {
          $ref: '#/$defs/K8sResources',
          description: 'Default resource limits.',
        },
      },
      required: ['requests', 'limits'],
      title: 'K8sResourceConfig',
      type: 'object',
    },
    K8sResources: {
      properties: {
        cpu: {
          $ref: '#/$defs/K8sCpuType',
          description: 'Specifies the number of physical or virtual cores.',
          title: 'CPU resource units',
          'ui:title': 'CPU Resource Units',
        },
        memory: {
          $ref: '#/$defs/K8sMemoryType',
          description: 'Specifies the number of bytes of physical memory.',
          title: 'Memory resource units',
          'ui:title': 'Memory Resource Units',
        },
      },
      required: ['cpu', 'memory'],
      title: 'K8sResources',
      type: 'object',
      'ui:title': 'K8s Resources',
    },
    KeyVaultFirewallRule: {
      properties: {
        AuthorEmail: {
          description: 'The email of the author of the firewall rule.',
          title: 'Author Email',
          type: 'string',
        },
        kind: {
          const: 'user-keyvault-firewall-rule',
          default: 'user-keyvault-firewall-rule',
          description: 'The kind of firewall rule.',
          title: 'Firewall Rule Kind',
          type: 'string',
        },
        IPAddressCIDR: {
          anyOf: [
            {
              format: 'ipvanyaddress',
              type: 'string',
            },
            {
              format: 'ipvanyinterface',
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'The IP address CIDR of the firewall rule.',
          title: 'IP Address CIDR',
        },
      },
      required: ['AuthorEmail'],
      title: 'KeyVaultFirewallRule',
      type: 'object',
    },
    KeyVaultResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'key-vault',
          title: 'Kind',
          type: 'string',
        },
        location: {
          description: 'Azure region where the key vault is deployed.',
          title: 'Azure region',
          type: 'string',
        },
        uri: {
          description: 'The URI of the key vault.',
          title: 'URI',
          type: 'string',
        },
      },
      required: ['resourceId', 'kind', 'location', 'uri'],
      title: 'KeyVaultResource',
      type: 'object',
    },
    KubernetesClusterRef: {
      properties: {
        name: {
          description: 'Kubernetes cluster report name',
          title: 'Name',
          type: 'string',
        },
        subscription: {
          description: 'Azure subscription name for the cluster',
          title: 'Subscription',
          type: 'string',
        },
      },
      required: ['name', 'subscription'],
      title: 'KubernetesClusterRef',
      type: 'object',
    },
    LegacyResources: {
      properties: {
        sharedGroups: {
          $ref: '#/$defs/SharedGroupsReport',
        },
      },
      required: ['sharedGroups'],
      title: 'LegacyResources',
      type: 'object',
    },
    LinkedGitHubTeamConfig: {
      properties: {
        slug: {
          description: 'The slug of the GitHub team.',
          title: 'Team name',
          type: 'string',
        },
        role: {
          $ref: '#/$defs/TeamRole',
          default: 'developers',
          description: 'The role of the team.',
        },
      },
      required: ['slug'],
      title: 'LinkedGitHubTeamConfig',
      type: 'object',
    },
    LogAnalytics: {
      properties: {
        kind: {
          const: 'log_analytics',
          default: 'log_analytics',
          description: 'LogAnalytics Resource Kind',
          title: 'LogAnalytics Resource Kind',
          type: 'string',
        },
        Name: {
          description: 'LogAnalytics Resource Name',
          title: 'LogAnalytics Resource Name',
          type: 'string',
        },
        RetentionPolicyDays: {
          default: 30,
          description: 'Retention Policy Days',
          maximum: 365,
          minimum: 1,
          title: 'Retention Policy Days',
          type: 'integer',
        },
      },
      required: ['Name'],
      title: 'LogAnalytics',
      type: 'object',
    },
    LogAnalyticsConfig: {
      description:
        'Azure Log Analytics workspace for monitoring and logging.\nThe [Log Analytics] module provides an instance of the Azure Log Analytics\nworkspace for monitoring and logging.\n[Log Analytics]: https://learn.microsoft.com/en-us/azure/azure-monitor/logs/log-analytics-overview',
      properties: {
        kind: {
          const: 'log-analytics',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
        sku: {
          $ref: '#/$defs/LogAnalyticsSku',
          default: 'PerGB2018',
          description: 'The SKU of the Log Analytics workspace.',
          'ui:title': 'SKU',
        },
        retentionInDays: {
          default: 30,
          description:
            'The number of days to retain data in the Log Analytics workspace.',
          maximum: 730,
          minimum: 30,
          title: 'Retentionindays',
          type: 'integer',
          'ui:title': 'Retention Days',
        },
      },
      required: ['kind'],
      title: 'LogAnalyticsConfig',
      type: 'object',
      'ui:options': {
        icon: 'log-analytics-workspaces',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/monitoring/log-analytics-workspace/',
          },
        ],
        supportedTargets: ['L5'],
        tags: ['logging', 'monitoring', 'observability'],
        teaser: 'Azure Log Analytics for monitoring and observability.',
      },
      'ui:title': 'Log Analytics',
    },
    LogAnalyticsResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'log-analytics',
          title: 'Kind',
          type: 'string',
        },
        location: {
          description:
            'Azure region where the log analytics workspace is deployed.',
          title: 'Azure region',
          type: 'string',
        },
      },
      required: ['resourceId', 'kind', 'location'],
      title: 'LogAnalyticsResource',
      type: 'object',
    },
    LogAnalyticsSku: {
      description:
        'The Log Analytics SKU name to be used, see the [documentation] for details.',
      enum: ['PerGB2018'],
      title: 'LogAnalyticsSku',
      type: 'string',
    },
    LogicalStage: {
      enum: ['development', 'staging', 'production'],
      title: 'LogicalStage',
      type: 'string',
    },
    ManifestMetadata: {
      properties: {
        name: {
          description:
            'Name of the entity. May only contain lowercase letters and hyphens',
          errorMessage: {
            pattern:
              'Must start with a lowercase letter, can contain lowercase letters, numbers, hyphens, and underscores.',
          },
          maxLength: 62,
          minLength: 1,
          pattern: '^[a-z]+(?:[-_]?[a-z0-9]+)+$',
          title: 'RFC1035 name',
          type: 'string',
        },
        namespace: {
          description: 'Namespace the entity is defined in.',
          errorMessage: {
            pattern:
              'Must start with a lowercase letter, can contain lowercase letters, numbers, hyphens, and underscores.',
          },
          maxLength: 62,
          minLength: 1,
          pattern: '^[a-z]+(?:[-_]?[a-z0-9]+)+$',
          title: 'RFC1035 name',
          type: 'string',
        },
        title: {
          default: '',
          description: 'A display name of the entity.',
          title: 'Title',
          type: 'string',
        },
        description: {
          default: '',
          description: 'A human readable description of the entity.',
          title: 'Description',
          type: 'string',
        },
        annotations: {
          additionalProperties: {
            type: 'string',
          },
          description: 'Annotations object regarding the resource.',
          title: 'Annotations',
          type: 'object',
        },
        labels: {
          additionalProperties: {
            type: 'string',
          },
          description: 'Labels object regarding the resource.',
          title: 'Labels',
          type: 'object',
        },
        role: {
          $ref: '#/$defs/ManifestRole',
          default: 'product',
          description: 'Role of the entity.',
          title: 'Role',
        },
      },
      required: ['name', 'namespace'],
      title: 'ManifestMetadata',
      type: 'object',
    },
    ManifestRole: {
      enum: ['platform', 'product'],
      title: 'ManifestRole',
      type: 'string',
    },
    MemgraphConfig: {
      description: 'A high-performance, in-memory graph database',
      properties: {
        kind: {
          const: 'memgraph',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
        chartVersion: {
          default: '2.5.3',
          description: 'Version of the service chart to deploy.',
          title: 'Helm chart version',
          type: 'string',
          'ui:title': 'Helm Chart Version',
        },
        image: {
          $ref: '#/$defs/AnyMemgraphImage',
          default: {
            name: 'memgraph/memgraph',
            tag: '3.8.0',
          },
          description: 'Memgraph docker image to be used',
          'ui:title': 'Image',
        },
        deployments: {
          default: [],
          description: 'Deployments of unique Memgraph instances.',
          items: {
            $ref: '#/$defs/MemgraphDeployment',
          },
          title: 'Deployments',
          type: 'array',
          'ui:title': 'Deployments',
          'vsc:action': 'array',
        },
      },
      required: ['kind'],
      title: 'MemgraphConfig',
      type: 'object',
      'ui:options': {
        icon: 'memgraph',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/storage/memgraph/',
          },
        ],
        supportedTargets: ['L5'],
        tags: ['graph-database', 'analytics'],
        teaser: 'In-memory graph database for real-time analytics.',
      },
      'ui:title': 'Memgraph',
    },
    MemgraphDeployment: {
      properties: {
        name: {
          default: 'memgraph',
          description: 'Deployment name',
          title: 'Deployment name',
          type: 'string',
          'ui:title': 'Name',
        },
        resourcesRequests: {
          $ref: '#/$defs/K8sResources',
          default: {
            cpu: '1000m',
            memory: '5000',
          },
          description: 'Request Kubernetes resources for Memgraph instance',
          'ui:title': 'K8s Resource requests',
        },
        resourcesLimits: {
          $ref: '#/$defs/K8sResources',
          default: {
            cpu: '2000m',
            memory: '10000',
          },
          description: 'Limits on Kubernetes resources for Memgraph instance',
          'ui:title': 'K8s Resource limits',
        },
        extraArgs: {
          default: [],
          description: 'Extra arguments to pass to Memgraph',
          items: {
            type: 'string',
          },
          title: 'Extraargs',
          type: 'array',
          'ui:title': 'Extra Arguments',
        },
        env: {
          default: [],
          description: 'Environment variables',
          items: {
            additionalProperties: {
              type: 'string',
            },
            type: 'object',
          },
          title: 'Env',
          type: 'array',
          'ui:title': 'Environment Variables',
        },
      },
      title: 'MemgraphDeployment',
      type: 'object',
    },
    MemgraphImage: {
      properties: {
        name: {
          const: 'memgraph/memgraph',
          description: 'Image name',
          title: 'Name',
          type: 'string',
          'ui:title': 'Name',
        },
        tag: {
          $ref: '#/$defs/MemgraphImageTag',
          default: '3.8.0',
          description: 'Image tag',
          'ui:title': 'Tag',
        },
      },
      required: ['name'],
      title: 'MemgraphImage',
      type: 'object',
      'ui:title': 'Memgraph Image',
    },
    MemgraphImageTag: {
      enum: ['3.3.0', '3.5.2', '3.6.2', '3.7.0', '3.8.0'],
      title: 'MemgraphImageTag',
      type: 'string',
    },
    MemgraphMageImage: {
      properties: {
        name: {
          const: 'memgraph/memgraph-mage',
          description: 'Image name',
          title: 'Name',
          type: 'string',
          'ui:title': 'Name',
        },
        tag: {
          $ref: '#/$defs/MemgraphMageImageTag',
          default: '3.8.0',
          description: 'Image tag',
          'ui:title': 'Tag',
        },
      },
      required: ['name'],
      title: 'MemgraphMageImage',
      type: 'object',
    },
    MemgraphMageImageTag: {
      enum: ['3.3', '3.5.2', '3.6.2', '3.7', '3.8.0'],
      title: 'MemgraphMageImageTag',
      type: 'string',
    },
    Metadata: {
      description: 'Metadata fields describing the resource',
      properties: {
        name: {
          description:
            'Name of the entity. May only contain lowercase letters and hyphens',
          errorMessage: {
            pattern:
              'Must start with a lowercase letter, can contain lowercase letters, numbers, hyphens, and underscores.',
          },
          maxLength: 62,
          minLength: 1,
          pattern: '^[a-z]+(?:[-_]?[a-z0-9]+)+$',
          title: 'RFC1035 name',
          type: 'string',
        },
        namespace: {
          description: 'Namespace the entity is defined in.',
          errorMessage: {
            pattern:
              'Must start with a lowercase letter, can contain lowercase letters, numbers, hyphens, and underscores.',
          },
          maxLength: 62,
          minLength: 1,
          pattern: '^[a-z]+(?:[-_]?[a-z0-9]+)+$',
          title: 'RFC1035 name',
          type: 'string',
        },
        title: {
          default: '',
          description: 'A display name of the entity.',
          title: 'Title',
          type: 'string',
        },
        description: {
          default: '',
          description: 'A human readable description of the entity.',
          title: 'Description',
          type: 'string',
        },
        annotations: {
          additionalProperties: {
            type: 'string',
          },
          description: 'Annotations object regarding the resource.',
          title: 'Annotations',
          type: 'object',
        },
        labels: {
          additionalProperties: {
            type: 'string',
          },
          description: 'Labels object regarding the resource.',
          title: 'Labels',
          type: 'object',
        },
      },
      required: ['name', 'namespace'],
      title: 'Metadata',
      type: 'object',
    },
    MlflowConfig: {
      description:
        'An open source platform for the machine learning lifecycle\n\nThe [MLflow] module provides an instance of the MLflow platform\nfor machine learning lifecycle management.\n\n[MLflow]: https://mlflow.org/',
      properties: {
        kind: {
          const: 'mlflow',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
        imageTag: {
          $ref: '#/$defs/MlflowImageTag',
          default: '3.11.1',
          description: 'Image tag',
          'ui:title': 'Tag',
        },
        chartVersion: {
          default: '1.17.2',
          description: 'Version of the service chart to deploy.',
          title: 'Helm chart version',
          type: 'string',
          'ui:title': 'Helm Chart Version',
        },
        uiPathPrefix: {
          $ref: '#/$defs/UrlPathSegment',
          default: '/mlflow',
          description: 'Route where the main UI is served.',
          title: 'UI path prefix',
          'ui:title': 'UI Path Prefix',
        },
        allowedGroups: {
          default: [],
          description: 'AAD groups allowed to access the MLflow UI.',
          items: {
            $ref: '#/$defs/Group',
          },
          title: 'Allowedgroups',
          type: 'array',
          'ui:title': 'Allowed Groups',
          'vsc:action': 'array',
          'vsc:picker': 'aad-group',
        },
        resourcesRequests: {
          $ref: '#/$defs/K8sResources',
          default: {
            cpu: '600m',
            memory: '850Mi',
          },
          description: 'Request Kubernetes resources for MLflow containers',
          'ui:title': 'K8s Resource Requests',
        },
        resourcesLimits: {
          $ref: '#/$defs/K8sResources',
          default: {
            cpu: '1000m',
            memory: '1250Mi',
          },
          description: 'Limits on Kubernetes resources for MLflow containers',
          'ui:title': 'K8s Resource Limits',
        },
      },
      required: ['kind'],
      title: 'MlflowConfig',
      type: 'object',
      'ui:options': {
        icon: 'mlflow',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/ai-analytics/mlflow/',
          },
        ],
        supportedTargets: ['L5'],
        tags: ['machine-learning', 'ml-ops'],
        teaser: 'An open source platform for the machine learning lifecycle.',
      },
      'ui:title': 'MLflow',
    },
    MlflowImageTag: {
      description:
        'Custom MLflow image tag to use, based on the corresponding MLflow version.\nNOTE: This is a custom DevHub image, not the official MLflow image.',
      enum: ['3.11.1', '3.9.1', '3.8.1', '3.5.1', '3.4.0'],
      title: 'MlflowImageTag',
      type: 'string',
    },
    Mode: {
      description:
        'The type of deployment to be created.\n\nSee the [documentation] for details.\n\n[documentation]: https://docs.arangodb.com/stable/deploy/deployment/kubernetes/deployment-resource-reference/#specmode-string',
      enum: ['Single', 'Cluster', 'ActiveFailover'],
      title: 'Mode',
      type: 'string',
    },
    ModelUpdatePolicy: {
      description: 'Policy for updating the model.',
      enum: [
        'OnceNewDefaultVersionAvailable',
        'OnceCurrentVersionExpired',
        'NoAutoUpgrade',
      ],
      title: 'ModelUpdatePolicy',
      type: 'string',
    },
    MongoAtlasIntegrationInfo: {
      properties: {
        workforceIdentityProviderId: {
          title: 'Workforceidentityproviderid',
          type: 'string',
        },
        workloadIdentityProviderId: {
          title: 'Workloadidentityproviderid',
          type: 'string',
        },
      },
      required: ['workforceIdentityProviderId', 'workloadIdentityProviderId'],
      title: 'MongoAtlasIntegrationInfo',
      type: 'object',
    },
    MongoDBAtlasClusterResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'mongodb-atlas-cluster',
          title: 'Kind',
          type: 'string',
        },
        version: {
          description: 'The MongoDB version of the cluster.',
          title: 'MongoDB Version',
          type: 'string',
        },
        clusterTier: {
          description: 'The MongoDB Atlas cluster tier/instance size.',
          title: 'Cluster Tier',
          type: 'string',
        },
        clusterName: {
          description: 'The name of the MongoDB Atlas cluster.',
          title: 'Cluster Name',
          type: 'string',
        },
        projectId: {
          description:
            'The MongoDB Atlas project ID associated with the cluster.',
          title: 'Project ID',
          type: 'string',
        },
      },
      required: [
        'resourceId',
        'kind',
        'version',
        'clusterTier',
        'clusterName',
        'projectId',
      ],
      title: 'MongoDBAtlasClusterResource',
      type: 'object',
    },
    MongoDBAtlasConfig: {
      properties: {
        config: {
          anyOf: [
            {
              $ref: '#/$defs/MongoDBAtlasConfigBase',
            },
            {
              $ref: '#/$defs/MongoDBAtlasConfigDict',
            },
          ],
          default: {
            version: '8.0',
            clusterTier: 'M10',
          },
          description: 'Configuration for the resource',
          title: 'Config',
          'ui:title': 'Select configuration type',
        },
        kind: {
          const: 'mongodb-atlas',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
      },
      required: ['kind'],
      title: 'MongoDBAtlasConfig',
      type: 'object',
      'ui:options': {
        icon: 'mongodb',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/storage/mongoatlas/',
          },
        ],
        supportedTargets: ['L5'],
        tags: ['nosql', 'document-database', 'atlas', 'database'],
        teaser: 'NoSQL document database for modern applications.',
      },
      'ui:title': 'MongoDB Atlas',
    },
    MongoDBAtlasConfigBase: {
      description:
        'Allows provisioning MongoDB Atlas clusters for use in product services.',
      properties: {
        version: {
          $ref: '#/$defs/MongoDBMajorVersion',
          default: '8.0',
          description: 'Version of MongoDB to use.',
          'ui:title': 'MongoDB Version',
        },
        clusterTier: {
          $ref: '#/$defs/MongoDBAtlasTier',
          default: 'M10',
          description: 'MongoDB Atlas cluster tier.',
          'ui:title': 'Cluster Tier',
        },
      },
      title: 'MongoDBAtlasConfigBase',
      type: 'object',
      'ui:title': 'Base configuration',
    },
    MongoDBAtlasConfigDict: {
      description:
        'A staged configuration for the MongoDB Atlas clusters that belong to this cell.',
      properties: {
        development: {
          anyOf: [
            {
              $ref: '#/$defs/MongoDBAtlasConfigBase',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Development configuration',
        },
        production: {
          anyOf: [
            {
              $ref: '#/$defs/MongoDBAtlasConfigBase',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Production configuration',
        },
      },
      title: 'MongoDBAtlasConfigDict',
      type: 'object',
      'ui:title': 'Configuration per environment',
    },
    MongoDBAtlasTier: {
      description:
        'The MongoDB Atlas cluster tier to be used, see the [documentation] for details.\n\n[documentation]: https://www.mongodb.com/docs/atlas/sizing-tier-selection/',
      enum: ['M10', 'M20', 'M30'],
      title: 'MongoDBAtlasTier',
      type: 'string',
    },
    MongoDBMajorVersion: {
      description:
        'The MongoDB version to be used, see the [documentation] for details.\n\n[documentation]: https://www.mongodb.com/docs/atlas/reference/faq/database/#which-versions-of-mongodb-do-service-clusters-use-',
      enum: ['8.0', '7.0'],
      title: 'MongoDBMajorVersion',
      type: 'string',
    },
    MsGraphAccessConfig: {
      properties: {
        resource: {
          const: 'microsoft-graph',
          description: 'The Azure resource for which the access is configured.',
          title: 'Azure resource',
          type: 'string',
        },
        resourceAccess: {
          description: 'Access configuration for the selected resource.',
          items: {
            $ref: '#/$defs/MsGraphPermission',
          },
          title: 'Resource access',
          type: 'array',
          uniqueItems: true,
        },
      },
      required: ['resource'],
      title: 'MsGraphAccessConfig',
      type: 'object',
    },
    MsGraphPermission: {
      description:
        'Microsoft Graph permissions\n\nNOTE: For SharePoint/OneDrive access, consider more secure alternatives:\n- Sites.Selected (application/sites.selected) - Access only specific sites configured in SharePoint admin\n- Files.SelectedOperations.Selected (application/files.selectedoperations.selected) - Specific file operations\n- Resource-Specific Consent (RSC) - Per-resource permissions (configured in app manifest, not here)\n\nThese are not yet implemented as enum values but can be added if needed.\nSee documentation: https://learn.microsoft.com/en-us/graph/permissions-reference#sitesselected',
      enum: [
        'delegated/user.read.all',
        'application/user.read.all',
        'delegated/group.read.all',
        'application/group.read.all',
        'delegated/sites.read.all',
        'delegated/files.read.all',
        'application/mail.send',
      ],
      title: 'MsGraphPermission',
      type: 'string',
    },
    MysqlConfig: {
      properties: {
        config: {
          anyOf: [
            {
              $ref: '#/$defs/MysqlConfigBase',
            },
            {
              $ref: '#/$defs/MysqlConfigDict',
            },
          ],
          default: {
            version: '8.0.21',
            skuName: 'B_Standard_B1ms',
            storageSizeGb: 32,
            backupRetentionDays: 7,
            databases: [],
            serverParameters: null,
          },
          description: 'Configuration for the resource',
          title: 'Config',
          'ui:title': 'Select configuration type',
        },
        kind: {
          const: 'mysql',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
      },
      required: ['kind'],
      title: 'MysqlConfig',
      type: 'object',
      'ui:options': {
        icon: 'mysql',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/storage/mysql/',
          },
        ],
        supportedTargets: ['L5'],
        tags: ['database', 'sql'],
        teaser: 'Popular open source relational database.',
      },
      'ui:title': 'MySQL',
    },
    MysqlConfigBase: {
      description:
        'Allows provisioning Mysql databases for use in product services.',
      properties: {
        version: {
          $ref: '#/$defs/MysqlVersion',
          default: '8.0.21',
          description: 'Version of Mysql server to use.',
          'ui:title': 'MySQL Version',
        },
        skuName: {
          $ref: '#/$defs/MysqlSku',
          default: 'B_Standard_B1ms',
          description:
            'SKU name to be used as base model for the Mysql server.',
          'ui:title': 'SKU Name',
        },
        storageSizeGb: {
          default: 32,
          description: 'Size of the Mysql storage in GB',
          title: 'Storagesizegb',
          type: 'integer',
          'ui:title': 'Storage Size (GB)',
        },
        backupRetentionDays: {
          default: 7,
          description: 'Number of days the automatic Mysql backup is kept',
          maximum: 35,
          minimum: 7,
          title: 'Backupretentiondays',
          type: 'integer',
          'ui:title': 'Backup Retention Days',
        },
        databases: {
          default: [],
          description: 'Additional databases to be created in the service.',
          items: {
            $ref: '#/$defs/DatabaseName',
          },
          title: 'Databases',
          type: 'array',
          'ui:title': 'Databases',
          'vsc:action': 'array',
        },
        serverParameters: {
          anyOf: [
            {
              $ref: '#/$defs/MysqlServerParameters',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'Configurable server parameters.',
          'ui:title': 'Select server parameters',
        },
      },
      title: 'MysqlConfigBase',
      type: 'object',
      'ui:title': 'Base configuration',
    },
    MysqlConfigDict: {
      description:
        'A staged configuration for the Mysql databases that belong to this cell.',
      properties: {
        development: {
          anyOf: [
            {
              $ref: '#/$defs/MysqlConfigBase',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Development configuration',
        },
        production: {
          anyOf: [
            {
              $ref: '#/$defs/MysqlConfigBase',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Production configuration',
        },
      },
      title: 'MysqlConfigDict',
      type: 'object',
      'ui:title': 'Configuration per environment',
    },
    MysqlDatabaseResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'mysql-db',
          title: 'Kind',
          type: 'string',
        },
        charset: {
          description: 'The character set to use for the database.',
          title: 'Character set',
          type: 'string',
        },
        collation: {
          description: 'The collation to use for the database.',
          title: 'Collation',
          type: 'string',
        },
      },
      required: ['resourceId', 'kind', 'charset', 'collation'],
      title: 'MysqlDatabaseResource',
      type: 'object',
    },
    MysqlServerParameters: {
      description:
        'The Mysql server parameters.\n\n[documentation]: https://dev.mysql.com/doc/refman/8.4/en/server-system-variables.html',
      properties: {
        maxConnections: {
          anyOf: [
            {
              maximum: 5000,
              minimum: 25,
              type: 'integer',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'The maximum number of concurrent connections to the database server.',
          title: 'Maxconnections',
          'ui:title': 'Maximum Connections',
        },
        sqlGenerateInvisiblePrimaryKey: {
          anyOf: [
            {
              enum: ['ON', 'OFF'],
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'Whether to generate invisible primary keys.',
          title: 'Sqlgenerateinvisibleprimarykey',
          'ui:title': 'GIPK',
        },
      },
      title: 'MysqlServerParameters',
      type: 'object',
      'ui:title': 'MySQL server parameters',
    },
    MysqlServerResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'mysql',
          title: 'Kind',
          type: 'string',
        },
        location: {
          description: 'Azure region where the Mysql server is deployed.',
          title: 'Azure region',
          type: 'string',
        },
        fqdn: {
          description: 'Fully qualified domain name of the server.',
          title: 'FQDN',
          type: 'string',
        },
        skuName: {
          description: 'The SKU of the Mysql server.',
          title: 'SKU name',
          type: 'string',
        },
      },
      required: ['resourceId', 'kind', 'location', 'fqdn', 'skuName'],
      title: 'MysqlServerResource',
      type: 'object',
    },
    MysqlSku: {
      description:
        'The Mysql server SKU name to be used, see the [documentation] for details.\n\n[documentation]: https://learn.microsoft.com/en-us/azure/mysql/flexible-server/concepts-service-tiers-storage',
      enum: ['B_Standard_B1ms', 'GP_Standard_D2ads_v5', 'MO_Standard_E2ds_v4'],
      title: 'MysqlSku',
      type: 'string',
    },
    MysqlVersion: {
      description:
        'The Mysql server version to be used, see the [documentation] for details.\n\n[documentation]: https://learn.microsoft.com/en-us/azure/mysql/concepts-version-policy',
      enum: ['8.0.21'],
      title: 'MysqlVersion',
      type: 'string',
    },
    NatGatewayPublicIpResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'nat-gateway-ip',
          title: 'Kind',
          type: 'string',
        },
        publicIp: {
          description: 'Public IP address of the network.',
          title: 'Public IP',
          type: 'string',
        },
        allocationMethod: {
          description:
            'The allocation method for the public IP (Static or Dynamic).',
          title: 'Allocation Method',
          type: 'string',
        },
        sku: {
          description: 'The SKU of the public IP (Basic or Standard).',
          title: 'SKU',
          type: 'string',
        },
      },
      required: ['resourceId', 'kind', 'publicIp', 'allocationMethod', 'sku'],
      title: 'NatGatewayPublicIpResource',
      type: 'object',
    },
    NetworkReport: {
      properties: {
        pip: {
          $ref: '#/$defs/PublicIpResource',
          description: 'Public IP address of the network.',
        },
        vnet: {
          $ref: '#/$defs/BaseResourceInfo',
          description: 'The virtual network information.',
        },
      },
      required: ['pip', 'vnet'],
      title: 'NetworkReport',
      type: 'object',
    },
    OpenAIDeploymentConfig: {
      properties: {
        name: {
          description: 'Name of the deployment',
          title: 'Name',
          type: 'string',
          'ui:title': 'Name',
        },
        model: {
          $ref: '#/$defs/OpenAIModel',
          description: 'Model to deploy',
          'ui:title': 'Model',
        },
        updatePolicy: {
          $ref: '#/$defs/ModelUpdatePolicy',
          default: 'OnceNewDefaultVersionAvailable',
          description: 'Policy for updating the model',
          'ui:title': 'Update Policy',
        },
        maxTokensPerMinuteThousands: {
          default: 10,
          description: 'Tokens-per-Minute (TPM) measured in thousands',
          maximum: 2000,
          minimum: 1,
          title: 'tokens per minute (thousands)',
          type: 'integer',
          'ui:title': 'Maximum Tokens Per Minute',
        },
        sku: {
          $ref: '#/$defs/OpenAISku',
          default: 'Standard',
          description: 'SKU of the deployment',
          'ui:title': 'SKU',
        },
      },
      required: ['name', 'model'],
      title: 'OpenAIDeploymentConfig',
      type: 'object',
    },
    OpenAIEmbeddingConfig: {
      properties: {
        name: {
          description: 'Name of the deployment',
          title: 'Name',
          type: 'string',
          'ui:title': 'Name',
        },
        model: {
          $ref: '#/$defs/OpenAIEmbeddingModel',
          description: 'Model to deploy',
          'ui:title': 'Model',
        },
        updatePolicy: {
          $ref: '#/$defs/ModelUpdatePolicy',
          default: 'NoAutoUpgrade',
          description: 'Policy for updating the model',
          'ui:title': 'Update Policy',
        },
        maxTokensPerMinuteThousands: {
          default: 5,
          description: 'Tokens-per-Minute (TPM) measured in thousands',
          maximum: 500,
          minimum: 1,
          title: 'Maxtokensperminutethousands',
          type: 'integer',
          'ui:title': 'Maximum Tokens Per Minute',
        },
        sku: {
          $ref: '#/$defs/OpenAISku',
          default: 'Standard',
          description: 'SKU of the deployment',
          'ui:title': 'SKU',
        },
      },
      required: ['name', 'model'],
      title: 'OpenAIEmbeddingConfig',
      type: 'object',
    },
    OpenAIEmbeddingModel: {
      enum: ['text-embedding-ada-002', 'text-embedding-3-large'],
      title: 'OpenAIEmbeddingModel',
      type: 'string',
    },
    OpenAILocation: {
      description:
        'The Azure OpenAI service location to be used, see the [documentation] for details.\nModel availability may vary by Azure Region and Azure OpenAI SKU.\n[documentation]: https://learn.microsoft.com/en-us/azure/ai-services/openai/concepts/models?tabs=datazone-standard',
      enum: ['swedencentral', 'francecentral', 'polandcentral', 'westeurope'],
      title: 'OpenAILocation',
      type: 'string',
    },
    OpenAIModel: {
      enum: [
        'gpt-5.1-2025-11-13',
        'gpt-5-2025-08-07',
        'gpt-5-mini-2025-08-07',
        'gpt-5-nano-2025-08-07',
        'o4-mini-2025-04-16',
        'o3-mini-2025-01-31',
        'o1-2024-12-17',
        'gpt-4.1-2025-04-14',
        'gpt-4.1-nano-2025-04-14',
        'gpt-4.1-mini-2025-04-14',
        'gpt-4o-2024-11-20',
        'whisper',
      ],
      title: 'OpenAIModel',
      type: 'string',
    },
    OpenAISku: {
      enum: ['Standard', 'DataZoneStandard', 'DataZoneBatch', 'GlobalBatch'],
      title: 'OpenAISku',
      type: 'string',
    },
    OpenAi: {
      properties: {
        kind: {
          const: 'open_ai',
          default: 'open_ai',
          description: 'OpenAi Resource Kind.',
          title: 'OpenAi Resource Kind',
          type: 'string',
        },
        Name: {
          description: 'OpenAi Resource Name.',
          title: 'OpenAi Resource Name',
          type: 'string',
        },
        Location: {
          description: 'OpenAi Resource Location.',
          title: 'OpenAi Resource Location',
          type: 'string',
        },
        CustomManagedKey: {
          default: false,
          description: 'Custom Managed Key Option for Open AI.',
          title: 'Custom Managed Key Option for Open AI',
          type: 'boolean',
        },
      },
      required: ['Name', 'Location'],
      title: 'OpenAi',
      type: 'object',
    },
    OpenAiConfig: {
      properties: {
        kind: {
          const: 'openai',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
        chatDeployments: {
          default: [],
          description: 'Chat models to deploy',
          items: {
            $ref: '#/$defs/OpenAIDeploymentConfig',
          },
          title: 'Chatdeployments',
          type: 'array',
          'ui:title': 'Chat Deployments',
        },
        embeddingDeployments: {
          default: [],
          description: 'Embedding models to deploy',
          items: {
            $ref: '#/$defs/OpenAIEmbeddingConfig',
          },
          title: 'Embeddingdeployments',
          type: 'array',
          'ui:title': 'Embedding Deployments',
        },
        location: {
          $ref: '#/$defs/OpenAILocation',
          default: 'swedencentral',
          description:
            'Azure Region where OpenAI service should be provisioned.',
          'ui:title': 'Location',
        },
        externalAccess: {
          default: true,
          description:
            'Enable external access to the service via the product Gateway.',
          title: 'External access',
          type: 'boolean',
          'ui:title': 'External Access',
        },
      },
      required: ['kind'],
      title: 'OpenAiConfig',
      type: 'object',
      'ui:options': {
        supportedTargets: ['L5'],
        tags: ['ai', 'gpt'],
        teaser: 'Access to OpenAI GPT models and services.',
      },
      'ui:title': 'OpenAI',
    },
    OpenTelemetryBackend: {
      properties: {
        azureDataExplorer: {
          $ref: '#/$defs/OpenTelemetryBackendConfigBase',
          default: {
            enabled: false,
          },
          description: 'Azure Data Explorer backend configuration.',
        },
      },
      title: 'OpenTelemetryBackend',
      type: 'object',
    },
    OpenTelemetryBackendConfigBase: {
      properties: {
        enabled: {
          default: false,
          description: 'Enable OpenTelemetry backend.',
          title: 'Enabled',
          type: 'boolean',
        },
      },
      title: 'OpenTelemetryBackendConfigBase',
      type: 'object',
    },
    OpenTelemetryConfig: {
      properties: {
        config: {
          anyOf: [
            {
              $ref: '#/$defs/OpenTelemetryConfigBase',
            },
            {
              $ref: '#/$defs/StagedConfigDict_OpenTelemetryConfigBase_',
            },
          ],
          description: 'Configuration for the resource',
          title: 'Config',
        },
      },
      required: ['config'],
      title: 'OpenTelemetryConfig',
      type: 'object',
    },
    OpenTelemetryConfigBase: {
      properties: {
        enabled: {
          default: false,
          description: 'Enable OpenTelemetry.',
          title: 'Enabled',
          type: 'boolean',
        },
        backend: {
          $ref: '#/$defs/OpenTelemetryBackend',
          default: {
            azureDataExplorer: {
              enabled: false,
            },
          },
          description: 'OpenTelemetry backend configuration.',
        },
      },
      title: 'OpenTelemetryConfigBase',
      type: 'object',
    },
    PathMatchType: {
      enum: ['prefix', 'exact'],
      title: 'PathMatchType',
      type: 'string',
    },
    PlatformCell: {
      properties: {
        kubernetesCluster: {
          anyOf: [
            {
              $ref: '#/$defs/KubernetesClusterRef',
            },
            {
              type: 'null',
            },
          ],
          default: null,
        },
      },
      title: 'PlatformCell',
      type: 'object',
    },
    PlatformEntryV4: {
      properties: {
        stackVersion: {
          description:
            'The version of the infrastructure stack that provisioned the resources.',
          title: 'Stack Version',
          type: 'string',
        },
        signature: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Signature',
        },
        manifest: {
          $ref: '#/$defs/PlatformManifestDevhubV1',
        },
        report: {
          $ref: '#/$defs/PlatformReportV4',
        },
        policies: {
          $ref: '#/$defs/PolicyReport',
          title: 'Policies',
        },
        resources: {
          $ref: '#/$defs/ResourcesReport',
          title: 'Resources',
        },
      },
      required: ['stackVersion', 'manifest', 'report', 'policies', 'resources'],
      title: 'PlatformEntryV4',
      type: 'object',
    },
    PlatformEntryV5: {
      properties: {
        stackVersion: {
          description:
            'The version of the infrastructure stack that provisioned the resources.',
          title: 'Stack Version',
          type: 'string',
        },
        signature: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Signature',
        },
        manifest: {
          $ref: '#/$defs/PlatformManifestDevhubV2',
        },
        report: {
          $ref: '#/$defs/PlatformReportV5',
        },
        policies: {
          $ref: '#/$defs/PolicyReport',
          title: 'Policies',
        },
        resources: {
          $ref: '#/$defs/ResourcesReport',
          title: 'Resources',
        },
      },
      required: ['stackVersion', 'manifest', 'report', 'policies', 'resources'],
      title: 'PlatformEntryV5',
      type: 'object',
    },
    PlatformEntryV6: {
      properties: {
        stackVersion: {
          description:
            'The version of the infrastructure stack that provisioned the resources.',
          title: 'Stack Version',
          type: 'string',
        },
        signature: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Signature',
        },
        manifest: {
          $ref: '#/$defs/PlatformManifestDevhubV3',
        },
        report: {
          $ref: '#/$defs/PlatformReportV5',
        },
        policies: {
          $ref: '#/$defs/PolicyReport',
          title: 'Policies',
        },
        resources: {
          $ref: '#/$defs/ResourcesReport',
          title: 'Resources',
        },
      },
      required: ['stackVersion', 'manifest', 'report', 'policies', 'resources'],
      title: 'PlatformEntryV6',
      type: 'object',
    },
    PlatformGroups: {
      properties: {
        contributors: {
          $ref: '#/$defs/GroupInfo',
        },
        readers: {
          $ref: '#/$defs/GroupInfo',
        },
        portalIngestion: {
          $ref: '#/$defs/GroupInfo',
        },
      },
      required: ['contributors', 'readers', 'portalIngestion'],
      title: 'PlatformGroups',
      type: 'object',
    },
    PlatformIdentities: {
      properties: {
        airgapAgent: {
          $ref: '#/$defs/IdentityInfo',
        },
        mongoatlasWorkforceApplication: {
          $ref: '#/$defs/IdentityInfo',
        },
        mongoatlasWorkloadApplication: {
          $ref: '#/$defs/IdentityInfo',
        },
        mongoatlasWorkforceServicePrincipal: {
          $ref: '#/$defs/IdentityInfo',
        },
        mongoatlasWorkloadServicePrincipal: {
          $ref: '#/$defs/IdentityInfo',
        },
      },
      required: [
        'airgapAgent',
        'mongoatlasWorkforceApplication',
        'mongoatlasWorkloadApplication',
        'mongoatlasWorkforceServicePrincipal',
        'mongoatlasWorkloadServicePrincipal',
      ],
      title: 'PlatformIdentities',
      type: 'object',
    },
    PlatformInstanceTeam: {
      properties: {
        name: {
          title: 'Name',
          type: 'string',
        },
        description: {
          title: 'Description',
          type: 'string',
        },
        role: {
          $ref: '#/$defs/PlatformInstanceTeamRole',
        },
        objectId: {
          description: 'Object ID of the team in Azure AD.',
          format: 'uuid',
          title: 'Object ID',
          type: 'string',
        },
      },
      required: ['name', 'description', 'role', 'objectId'],
      title: 'PlatformInstanceTeam',
      type: 'object',
    },
    PlatformInstanceTeamRole: {
      enum: ['acr-reader'],
      title: 'PlatformInstanceTeamRole',
      type: 'string',
    },
    PlatformManifestDevhubV1: {
      properties: {
        apiVersion: {
          const: 'core.devhub.ai/v1',
          description: 'Api version',
          title: 'Apiversion',
          type: 'string',
        },
        kind: {
          const: 'PlatformManifest',
          default: 'PlatformManifest',
          title: 'Kind',
          type: 'string',
        },
        metadata: {
          $ref: '#/$defs/ManifestMetadata',
          description: 'Metadata for the entity.',
          title: 'Metadata',
        },
        spec: {
          $ref: '#/$defs/PlatformSpec',
          description: 'Platform specification.',
          title: 'Platform specification',
        },
        instances: {
          $ref: '#/$defs/InstanceConfigV3',
          description: 'The instances of the platform.',
          title: 'Instances',
        },
        clusterSetup: {
          $ref: '#/$defs/ClusterSetup',
          default: {
            certManager: {
              config: {
                chartVersion: 'v1.16.1',
                enabled: false,
              },
            },
            opentelemetry: {
              config: {
                backend: {
                  azureDataExplorer: {
                    enabled: false,
                  },
                },
                enabled: false,
              },
            },
          },
          description: 'The setup of the cluster.',
          title: 'Cluster setup',
        },
        portal: {
          $ref: '#/$defs/PortalConfig',
          default: {
            config: {
              location: 'westeurope',
              postgres: {
                additionalAdminGroup: {},
                backupRetentionDays: 7,
                databases: [],
                serverParameters: null,
                skuName: 'B_Standard_B2s',
                storageAutoGrowEnabled: true,
                storageSize: 32768,
                version: '16',
              },
            },
            repositories: {
              portal: 'dh-developer-portal',
              templates: 'dh-scaffolder-templates',
            },
          },
          description: 'Configuration for the portal.',
          'ui:title': 'Portal configuration',
        },
      },
      required: ['apiVersion', 'metadata', 'spec', 'instances'],
      title: 'PlatformManifestDevhubV1',
      type: 'object',
    },
    PlatformManifestDevhubV2: {
      properties: {
        apiVersion: {
          const: 'core.devhub.ai/v2',
          description: 'Api version',
          title: 'Apiversion',
          type: 'string',
        },
        kind: {
          const: 'PlatformManifest',
          default: 'PlatformManifest',
          title: 'Kind',
          type: 'string',
        },
        metadata: {
          $ref: '#/$defs/ManifestMetadata',
          description: 'Metadata for the entity.',
          title: 'Metadata',
        },
        spec: {
          $ref: '#/$defs/PlatformSpec',
          description: 'Platform specification.',
          title: 'Platform specification',
        },
        instances: {
          $ref: '#/$defs/InstanceConfigV4',
          description: 'The instances of the platform.',
          title: 'Instances',
        },
        clusterSetup: {
          $ref: '#/$defs/ClusterSetup',
          default: {
            certManager: {
              config: {
                chartVersion: 'v1.16.1',
                enabled: false,
              },
            },
            opentelemetry: {
              config: {
                backend: {
                  azureDataExplorer: {
                    enabled: false,
                  },
                },
                enabled: false,
              },
            },
          },
          description: 'The setup of the cluster.',
          title: 'Cluster setup',
        },
        portal: {
          $ref: '#/$defs/PortalConfig',
          default: {
            config: {
              location: 'westeurope',
              postgres: {
                additionalAdminGroup: {},
                backupRetentionDays: 7,
                databases: [],
                serverParameters: null,
                skuName: 'B_Standard_B2s',
                storageAutoGrowEnabled: true,
                storageSize: 32768,
                version: '16',
              },
            },
            repositories: {
              portal: 'dh-developer-portal',
              templates: 'dh-scaffolder-templates',
            },
          },
          description: 'Configuration for the portal.',
          'ui:title': 'Portal configuration',
        },
      },
      required: ['apiVersion', 'metadata', 'spec', 'instances'],
      title: 'PlatformManifestDevhubV2',
      type: 'object',
    },
    PlatformManifestDevhubV3: {
      properties: {
        apiVersion: {
          const: 'core.devhub.ai/v3',
          description: 'Api version',
          title: 'Apiversion',
          type: 'string',
        },
        kind: {
          const: 'PlatformManifest',
          default: 'PlatformManifest',
          title: 'Kind',
          type: 'string',
        },
        metadata: {
          $ref: '#/$defs/ManifestMetadata',
          description: 'Metadata for the entity.',
          title: 'Metadata',
        },
        spec: {
          $ref: '#/$defs/PlatformSpec',
          description: 'Platform specification.',
          title: 'Platform specification',
        },
        instances: {
          $ref: '#/$defs/InstanceConfigV5',
          description: 'The instances of the platform.',
          title: 'Instances',
        },
        clusterSetup: {
          $ref: '#/$defs/ClusterSetup',
          default: {
            certManager: {
              config: {
                chartVersion: 'v1.16.1',
                enabled: false,
              },
            },
            opentelemetry: {
              config: {
                backend: {
                  azureDataExplorer: {
                    enabled: false,
                  },
                },
                enabled: false,
              },
            },
          },
          description: 'The setup of the cluster.',
          title: 'Cluster setup',
        },
        portal: {
          $ref: '#/$defs/PortalConfig',
          default: {
            config: {
              location: 'westeurope',
              postgres: {
                additionalAdminGroup: {},
                backupRetentionDays: 7,
                databases: [],
                serverParameters: null,
                skuName: 'B_Standard_B2s',
                storageAutoGrowEnabled: true,
                storageSize: 32768,
                version: '16',
              },
            },
            repositories: {
              portal: 'dh-developer-portal',
              templates: 'dh-scaffolder-templates',
            },
          },
          description: 'Configuration for the portal.',
          'ui:title': 'Portal configuration',
        },
      },
      required: ['apiVersion', 'metadata', 'spec', 'instances'],
      title: 'PlatformManifestDevhubV3',
      type: 'object',
    },
    PlatformReportV4: {
      properties: {
        identities: {
          $ref: '#/$defs/PlatformIdentities',
        },
        groups: {
          $ref: '#/$defs/PlatformGroups',
        },
        instances: {
          items: {
            $ref: '#/$defs/InstanceLayoutV3',
          },
          title: 'Instances',
          type: 'array',
        },
        portal: {
          $ref: '#/$defs/PortalInfo',
        },
        datadog: {
          anyOf: [
            {
              $ref: '#/$defs/DataDogIntegrationInfo',
            },
            {
              type: 'null',
            },
          ],
          default: null,
        },
        mongoatlas: {
          anyOf: [
            {
              $ref: '#/$defs/MongoAtlasIntegrationInfo',
            },
            {
              type: 'null',
            },
          ],
          default: null,
        },
      },
      required: ['identities', 'groups', 'instances', 'portal'],
      title: 'PlatformReportV4',
      type: 'object',
    },
    PlatformReportV5: {
      properties: {
        identities: {
          $ref: '#/$defs/PlatformIdentities',
        },
        groups: {
          $ref: '#/$defs/PlatformGroups',
        },
        instances: {
          items: {
            $ref: '#/$defs/InstanceLayoutV4',
          },
          title: 'Instances',
          type: 'array',
        },
        portal: {
          $ref: '#/$defs/PortalInfo',
        },
        datadog: {
          anyOf: [
            {
              $ref: '#/$defs/DataDogIntegrationInfo',
            },
            {
              type: 'null',
            },
          ],
          default: null,
        },
        mongoatlas: {
          anyOf: [
            {
              $ref: '#/$defs/MongoAtlasIntegrationInfo',
            },
            {
              type: 'null',
            },
          ],
          default: null,
        },
      },
      required: ['identities', 'groups', 'instances', 'portal'],
      title: 'PlatformReportV5',
      type: 'object',
    },
    PlatformSpec: {
      properties: {
        owner: {
          description:
            'AAD object ID or display name for the group owning the entity.',
          format: 'uuid',
          title: 'Owner group',
          type: 'string',
          'ui:field': 'AzureGroupSearch',
          'ui:title': 'AAD Group',
          'vsc:picker': 'aad-group',
        },
        primaryContact: {
          anyOf: [
            {
              format: 'uuid',
              type: 'string',
            },
            {
              format: 'email',
              type: 'string',
            },
          ],
          description: 'AAD object id or email of the primary contact person.',
          title: 'Primary contact',
          'ui:field': 'AzureUserSearch',
          'ui:title': 'AAD User',
          'vsc:picker': 'aad-user',
        },
        secondaryContact: {
          anyOf: [
            {
              format: 'uuid',
              type: 'string',
            },
            {
              format: 'email',
              type: 'string',
            },
          ],
          description:
            'AAD object id or email of the secondary contact person.',
          title: 'Secondary contact',
          'ui:field': 'AzureUserSearch',
          'ui:title': 'AAD User',
          'vsc:picker': 'aad-user',
        },
        uniqueKey: {
          $ref: '#/$defs/UniqueKeyType',
          description: 'A unique identifier for the entity.',
          title: 'Unique key.',
          'ui:disabled': true,
          'ui:title': 'Unique Key',
        },
        lifecycle: {
          $ref: '#/$defs/argus__provisioning__models__common__registry__Lifecycle',
          default: 'production',
          description: 'Lifecycle of the entity.',
          title: 'Entity lifecycle stage.',
        },
        featureFlags: {
          default: [],
          description:
            'Feature flags allow op-ting into experimental functionality in the stack',
          items: {
            $ref: '#/$defs/FeatureFlag',
          },
          title: 'Feature flags.',
          type: 'array',
          'vsc:action': 'array',
        },
        devTeams: {
          description: 'Platform teams assigned platform level roles.',
          items: {
            $ref: '#/$defs/GitHubTeamConfig',
          },
          title: 'Development teams',
          type: 'array',
        },
        teams: {
          description:
            'Teams that are assigned permissions on the platform instance.',
          items: {
            $ref: '#/$defs/PlatformTeam',
          },
          title: 'Platform teams',
          type: 'array',
        },
      },
      required: ['owner', 'primaryContact', 'secondaryContact', 'uniqueKey'],
      title: 'PlatformSpec',
      type: 'object',
    },
    PlatformTeam: {
      properties: {
        name: {
          title: 'Name',
          type: 'string',
        },
        description: {
          title: 'Description',
          type: 'string',
        },
        role: {
          $ref: '#/$defs/PlatformTeamRole',
        },
        objectId: {
          format: 'uuid',
          title: 'Object ID',
          type: 'string',
        },
      },
      required: ['name', 'description', 'role', 'objectId'],
      title: 'PlatformTeam',
      type: 'object',
    },
    PlatformTeamRole: {
      enum: [
        'admin',
        'infra-admin',
        'developer',
        'stakeholder',
        'firefighter',
        'product-owner',
      ],
      title: 'PlatformTeamRole',
      type: 'string',
    },
    PolicyReport: {
      properties: {
        permissions: {
          description:
            'List of role assignments defined in the synthesized stack.',
          items: {
            $ref: '#/$defs/RoleAssignmentInfo',
          },
          title: 'Role Assignments',
          type: 'array',
        },
      },
      required: ['permissions'],
      title: 'PolicyReport',
      type: 'object',
    },
    PolicyReportEntry: {
      properties: {
        stackVersion: {
          description:
            'The version of the infrastructure stack that provisioned the resources.',
          title: 'Stack Version',
          type: 'string',
        },
        signature: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Signature',
        },
        report: {
          $ref: '#/$defs/PolicyReport',
        },
      },
      required: ['stackVersion', 'report'],
      title: 'PolicyReportEntry',
      type: 'object',
    },
    PortNumber: {
      description: 'Port valid number.',
      maximum: 65535,
      minimum: 1,
      title: 'Port number',
      type: 'integer',
    },
    PortalConfig: {
      properties: {
        config: {
          anyOf: [
            {
              $ref: '#/$defs/PortalConfigBase',
            },
            {
              $ref: '#/$defs/StagedConfigDict_PortalConfigBase_',
            },
          ],
          description: 'Configuration for the resource',
          title: 'Config',
        },
        repositories: {
          $ref: '#/$defs/PortalRepositories',
          default: {
            templates: 'dh-scaffolder-templates',
            portal: 'dh-developer-portal',
          },
        },
      },
      required: ['config'],
      title: 'PortalConfig',
      type: 'object',
    },
    PortalConfigBase: {
      properties: {
        postgres: {
          $ref: '#/$defs/PostgresConfigBase',
          default: {
            version: '16',
            skuName: 'B_Standard_B2s',
            storageAutoGrowEnabled: true,
            storageSize: 32768,
            databases: [],
            backupRetentionDays: 7,
            additionalAdminGroup: {},
            serverParameters: null,
          },
        },
        location: {
          $ref: '#/$defs/AzureRegion',
          default: 'westeurope',
        },
      },
      title: 'PortalConfigBase',
      type: 'object',
    },
    PortalInfo: {
      properties: {
        templateRepository: {
          description: 'Name of the repository publishing templates',
          title: 'Template Repository',
          type: 'string',
        },
      },
      required: ['templateRepository'],
      title: 'PortalInfo',
      type: 'object',
    },
    PortalRepositories: {
      properties: {
        templates: {
          default: 'dh-scaffolder-templates',
          description: 'Name of the repo for Software Templates',
          title: 'Templates',
          type: 'string',
        },
        portal: {
          default: 'dh-developer-portal',
          description: 'Name of the repo deploying the orchestration portal',
          title: 'Portal',
          type: 'string',
        },
      },
      title: 'PortalRepositories',
      type: 'object',
    },
    PortalResources: {
      properties: {
        techdocsContainer: {
          $ref: '#/$defs/StorageContainerResource',
          description:
            'A storage container where workspaces can release their techdocs.',
          title: 'TechDocs container',
        },
        templatesContainer: {
          anyOf: [
            {
              $ref: '#/$defs/StorageContainerResource',
            },
            {
              type: 'null',
            },
          ],
          description:
            'A storage container where software templates are shared.',
          title: 'Templates container',
        },
      },
      required: ['techdocsContainer', 'templatesContainer'],
      title: 'PortalResources',
      type: 'object',
    },
    PostgresAzureExtension: {
      enum: [
        'address_standardizer',
        'address_standardizer_data_us',
        'age',
        'amcheck',
        'anon',
        'auto_explain',
        'azure',
        'azure_ai',
        'azure_local_ai',
        'azure_storage',
        'azure_keyvault',
        'azurelib',
        'bloom',
        'btree_gin',
        'btree_gist',
        'citext',
        'cube',
        'dblink',
        'dict_int',
        'dict_xsyn',
        'earthdistance',
        'fuzzystrmatch',
        'hll',
        'hstore',
        'hypopg',
        'intagg',
        'intarray',
        'isn',
        'lo',
        'login_hook',
        'ltree',
        'oracle_fdw',
        'orafce',
        'pageinspect',
        'pg_buffercache',
        'pg_cron',
        'pg_diskann',
        'pg_failover_slots',
        'pg_freespacemap',
        'pg_hint_plan',
        'pg_partman',
        'pg_partman_bgw',
        'pg_prewarm',
        'pg_repack',
        'pgrouting',
        'pgrowlocks',
        'pg_squeeze',
        'pg_stat_statements',
        'pgstattuple',
        'pg_trgm',
        'pg_visibility',
        'pgaudit',
        'pgcrypto',
        'pglogical',
        'plpgsql',
        'plv8',
        'postgis',
        'postgis_raster',
        'postgis_sfcgal',
        'postgis_tiger_geocoder',
        'postgis_topology',
        'postgres_fdw',
        'postgres_protobuf',
        'semver',
        'session_variable',
        'sslinfo',
        'tablefunc',
        'tdigest',
        'tds_fdw',
        'timescaledb',
        'topn',
        'tsm_system_rows',
        'tsm_system_time',
        'unaccent',
        'uuid-ossp',
        'vector',
        'wal2json',
      ],
      type: 'string',
    },
    PostgresConfig: {
      properties: {
        config: {
          anyOf: [
            {
              $ref: '#/$defs/PostgresConfigBase',
            },
            {
              $ref: '#/$defs/PostgresConfigDict',
            },
          ],
          default: {
            version: '16',
            skuName: 'B_Standard_B1ms',
            storageAutoGrowEnabled: true,
            storageSize: 32768,
            databases: [],
            backupRetentionDays: 7,
            additionalAdminGroup: {},
            serverParameters: null,
          },
          description: 'Configuration for the resource',
          title: 'Config',
          'ui:title': 'Select configuration type',
        },
        kind: {
          const: 'postgres',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
      },
      required: ['kind'],
      title: 'PostgresConfig',
      type: 'object',
      'ui:options': {
        icon: 'postgresql',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/storage/postgres/',
          },
        ],
        supportedTargets: ['L4', 'L5'],
        tags: ['database', 'sql'],
        teaser: 'Advanced open source relational database.',
      },
      'ui:title': 'PostgreSQL',
    },
    PostgresConfigBase: {
      description:
        'Allows provisioning Postgres databases for use in product services.',
      properties: {
        version: {
          $ref: '#/$defs/PostgresVersion',
          default: '16',
          description: 'Version of postgres server to use.',
          'ui:title': 'PostgreSQL Version',
        },
        skuName: {
          $ref: '#/$defs/PostgresSku',
          default: 'B_Standard_B1ms',
          description:
            'SKU name to be used as base model for the Postgres server.',
          'ui:title': 'SKU Name',
        },
        storageAutoGrowEnabled: {
          default: true,
          description: 'Enable auto grow of the Postgres Server storage',
          title: 'Storageautogrowenabled',
          type: 'boolean',
          'ui:title': 'Storage Auto Grow',
        },
        storageSize: {
          $ref: '#/$defs/PostgresStorageSize',
          default: 32768,
          description: 'Size of the Postgres Server storage in MB',
          'ui:title': 'Storage Size (MB)',
        },
        databases: {
          default: [],
          description: 'Additional databases to be created in the service.',
          items: {
            $ref: '#/$defs/DatabaseName',
          },
          title: 'Databases',
          type: 'array',
          'ui:title': 'Databases',
          'vsc:action': 'array',
        },
        backupRetentionDays: {
          default: 7,
          description: 'Backup retention in days.',
          maximum: 35,
          minimum: 7,
          title: 'Backupretentiondays',
          type: 'integer',
          'ui:title': 'Backup Retention Days',
        },
        additionalAdminGroup: {
          additionalProperties: {
            format: 'uuid',
            type: 'string',
          },
          default: {},
          deprecated: true,
          description:
            'Additional administrators to be mapped to Azure Groups.',
          title: 'Additionaladmingroup',
          type: 'object',
          'ui:title': 'Additional Admin Groups',
        },
        serverParameters: {
          anyOf: [
            {
              $ref: '#/$defs/PostgresServerParameters',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'Configurable server parameters.',
          'ui:title': 'Select server parameters',
        },
      },
      title: 'PostgresConfigBase',
      type: 'object',
      'ui:title': 'Base configuration',
    },
    PostgresConfigDict: {
      description:
        'A staged configuration for the Postgres databases that belong to this cell.',
      properties: {
        development: {
          anyOf: [
            {
              $ref: '#/$defs/PostgresConfigBase',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Development configuration',
        },
        production: {
          anyOf: [
            {
              $ref: '#/$defs/PostgresConfigBase',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Production configuration',
        },
      },
      title: 'PostgresConfigDict',
      type: 'object',
      'ui:title': 'Configuration per environment',
    },
    PostgresDatabaseResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'postgres-db',
          title: 'Kind',
          type: 'string',
        },
        charset: {
          description: 'The character set to use for the database.',
          title: 'Character set',
          type: 'string',
        },
        collation: {
          description: 'The collation to use for the database.',
          title: 'Collation',
          type: 'string',
        },
      },
      required: ['resourceId', 'kind', 'charset', 'collation'],
      title: 'PostgresDatabaseResource',
      type: 'object',
    },
    PostgresServerParameters: {
      description:
        'The Postgres server parameters. See [documentation]\n\n[documentation]: https://learn.microsoft.com/en-us/azure/postgresql/flexible-server/concepts-server-parameters',
      properties: {
        maxConnections: {
          anyOf: [
            {
              maximum: 5000,
              minimum: 25,
              type: 'integer',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'The maximum number of concurrent connections to the database server.',
          title: 'Maxconnections',
          'ui:title': 'Maximum Connections',
        },
        azureExtensions: {
          anyOf: [
            {
              items: {
                $ref: '#/$defs/PostgresAzureExtension',
              },
              type: 'array',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'The list of extensions to be installed on the database server.',
          title: 'Azureextensions',
          'ui:title': 'Azure Extensions',
        },
      },
      title: 'PostgresServerParameters',
      type: 'object',
      'ui:title': 'PostgreSQL server parameters',
    },
    PostgresServerResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'postgres',
          title: 'Kind',
          type: 'string',
        },
        location: {
          description: 'Azure region where the Postgres server is deployed.',
          title: 'Azure region',
          type: 'string',
        },
        fqdn: {
          description: 'Fully qualified domain name of the server.',
          title: 'FQDN',
          type: 'string',
        },
        skuName: {
          description: 'The SKU of the Postgres server.',
          title: 'SKU name',
          type: 'string',
        },
      },
      required: ['resourceId', 'kind', 'location', 'fqdn', 'skuName'],
      title: 'PostgresServerResource',
      type: 'object',
    },
    PostgresSku: {
      description:
        'The Postgres server SKU name to be used, see the [documentation] for details.\n\n[documentation]: https://learn.microsoft.com/en-us/azure/postgresql/flexible-server/concepts-compute-storage#compute-tiers-vcores-and-server-types',
      enum: [
        'B_Standard_B1ms',
        'B_Standard_B2s',
        'B_Standard_B4ms',
        'GP_Standard_D2ds_v5',
        'GP_Standard_D4ds_v5',
      ],
      title: 'PostgresSku',
      type: 'string',
    },
    PostgresStorageSize: {
      enum: [
        32768, 65536, 131072, 262144, 524288, 1048576, 2097152, 4193280,
        4194304, 8388608, 16777216, 33553408,
      ],
      type: 'integer',
    },
    PostgresVersion: {
      description:
        'The Postgres server version to be used, see the [documentation] for details.\n\n[documentation]: https://learn.microsoft.com/en-us/azure/postgresql/flexible-server/concepts-supported-versions',
      enum: ['14', '15', '16', '17', '18'],
      title: 'PostgresVersion',
      type: 'string',
    },
    Principal: {
      properties: {
        objectId: {
          description: 'Object ID of the principal.',
          format: 'uuid',
          title: 'Objectid',
          type: 'string',
          'ui:title': 'Object ID',
        },
        principalType: {
          $ref: '#/$defs/PrincipalType',
          description: 'Type of principal.',
          'ui:title': 'Principal Type',
        },
      },
      required: ['objectId', 'principalType'],
      title: 'Principal',
      type: 'object',
    },
    PrincipalType: {
      description: 'Type of principal to be assigned a role.',
      enum: ['Group', 'ServicePrincipal'],
      title: 'PrincipalType',
      type: 'string',
    },
    ProductCellManifest: {
      properties: {
        apiVersion: {
          const: 'core.argus.ai/v1alpha1',
          title: 'Apiversion',
          type: 'string',
        },
        kind: {
          const: 'ProductCellManifest',
          default: 'ProductCellManifest',
          title: 'Kind',
          type: 'string',
        },
        metadata: {
          $ref: '#/$defs/ManifestMetadata',
          description: 'Metadata for the entity.',
          title: 'Metadata',
        },
        spec: {
          $ref: '#/$defs/ProductCellSpec',
          description: 'Platform specification.',
          title: 'Platform specification',
        },
        clusterSetup: {
          $ref: '#/$defs/ClusterSetup',
          default: {
            certManager: {
              config: {
                chartVersion: 'v1.16.1',
                enabled: false,
              },
            },
            opentelemetry: {
              config: {
                backend: {
                  azureDataExplorer: {
                    enabled: false,
                  },
                },
                enabled: false,
              },
            },
          },
          description: 'The setup of the cluster.',
          title: 'Cluster setup',
        },
      },
      required: ['spec', 'metadata', 'apiVersion', 'kind'],
      title: 'ProductCellManifest',
      type: 'object',
    },
    ProductCellSpec: {
      properties: {
        owner: {
          description:
            'AAD object ID or display name for the group owning the entity.',
          format: 'uuid',
          title: 'Owner group',
          type: 'string',
          'ui:field': 'AzureGroupSearch',
          'ui:title': 'AAD Group',
          'vsc:picker': 'aad-group',
        },
        primaryContact: {
          anyOf: [
            {
              format: 'uuid',
              type: 'string',
            },
            {
              format: 'email',
              type: 'string',
            },
          ],
          description: 'AAD object id or email of the primary contact person.',
          title: 'Primary contact',
          'ui:field': 'AzureUserSearch',
          'ui:title': 'AAD User',
          'vsc:picker': 'aad-user',
        },
        secondaryContact: {
          anyOf: [
            {
              format: 'uuid',
              type: 'string',
            },
            {
              format: 'email',
              type: 'string',
            },
          ],
          description:
            'AAD object id or email of the secondary contact person.',
          title: 'Secondary contact',
          'ui:field': 'AzureUserSearch',
          'ui:title': 'AAD User',
          'vsc:picker': 'aad-user',
        },
        uniqueKey: {
          $ref: '#/$defs/UniqueKeyType',
          description: 'A unique identifier for the entity.',
          title: 'Unique key.',
          'ui:disabled': true,
          'ui:title': 'Unique Key',
        },
        lifecycle: {
          $ref: '#/$defs/argus__provisioning__models__common__registry__Lifecycle',
          default: 'production',
          description: 'Lifecycle of the entity.',
          title: 'Entity lifecycle stage.',
        },
        featureFlags: {
          default: [],
          description:
            'Feature flags allow op-ting into experimental functionality in the stack',
          items: {
            $ref: '#/$defs/FeatureFlag',
          },
          title: 'Feature flags.',
          type: 'array',
          'vsc:action': 'array',
        },
        instance: {
          description: 'Platform instance name.',
          title: 'Platform instance',
          type: 'string',
        },
      },
      required: [
        'owner',
        'primaryContact',
        'secondaryContact',
        'uniqueKey',
        'instance',
      ],
      title: 'ProductCellSpec',
      type: 'object',
    },
    ProductEntry: {
      properties: {
        stackVersion: {
          description:
            'The version of the infrastructure stack that provisioned the resources.',
          title: 'Stack Version',
          type: 'string',
        },
        signature: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'A signature that can be used to validate the integrity of the data.',
          title: 'Signature',
        },
        output: {
          $ref: '#/$defs/ProductReport',
        },
        input: {
          $ref: '#/$defs/ProductManifestArgusV1Alpha1',
        },
        policies: {
          $ref: '#/$defs/PolicyReport',
          title: 'Policies.',
        },
      },
      required: ['stackVersion', 'output', 'input', 'policies'],
      title: 'ProductEntry',
      type: 'object',
    },
    ProductManifestArgusV1Alpha1: {
      description:
        'Product manifest for Argus v1alpha1.\nThis manifest is used to define a product in the Argus platform.\nIt includes metadata, specifications, capabilities, modules, routing,\ncode locations, application configuration, and extensions.',
      properties: {
        apiVersion: {
          const: 'core.argus.ai/v1alpha1',
          description: 'Api version',
          title: 'Apiversion',
          type: 'string',
        },
        kind: {
          const: 'ProductManifest',
          description: 'Kind of manifest',
          title: 'Kind',
          type: 'string',
        },
        metadata: {
          $ref: '#/$defs/ManifestMetadata',
          description: 'Metadata for the entity.',
        },
        spec: {
          $ref: '#/$defs/ProductSpec',
        },
        capabilities: {
          default: [],
          description: 'Product capabilities',
          items: {
            $ref: '#/$defs/AnyCapabilityConfig',
          },
          title: 'Capabilities',
          type: 'array',
        },
        modules: {
          default: [],
          description:
            'Modules provide functionalities and experiences inside a product context.',
          items: {
            $ref: '#/$defs/AnyModuleConfig',
          },
          title: 'Modules',
          type: 'array',
        },
        routing: {
          $ref: '#/$defs/RoutingConfig',
          default: {
            exposedServices: [],
            linkedProducts: [],
            subDomainOverride: null,
            token: null,
          },
          description: 'Manage traffic in and out of a product namespace.',
        },
        codeLocations: {
          default: [],
          description: 'List of code locations for the product.',
          items: {
            $ref: '#/$defs/CodeLocation',
          },
          title: 'Codelocations',
          type: 'array',
        },
        application: {
          $ref: '#/$defs/ApplicationConfig',
          default: {
            requiredResourceAccess: [],
          },
          description: 'Application configuration for the product.',
        },
        extensions: {
          $ref: '#/$defs/ExtensionConfig',
          default: {
            appstore: [],
            dsp: null,
            aigateway: null,
          },
          description: 'List of extensions for the product. [experimental]',
        },
      },
      required: ['apiVersion', 'kind', 'metadata', 'spec'],
      title: 'ProductManifestArgusV1Alpha1',
      type: 'object',
    },
    ProductManifestDevHubDMZV1Alpha1: {
      description:
        'DMZ Product manifest for DevHub DMZ v1alpha1.\nThis manifest defines a product for DMZ environments.\n** Note: not yet released, still under development! **\n\nDMZ (De-Militarized Zone) products are restricted to:\n- Essential modules only (PostgreSQL for data persistence)\n- Specific Azure PaaS regions (GermanyWestCentral)\n- No experimental extensions',
      properties: {
        apiVersion: {
          const: 'core.devhub.ai.dmz/v1alpha1',
          title: 'Apiversion',
          type: 'string',
        },
        kind: {
          const: 'ProductManifest',
          description: 'Kind of manifest',
          title: 'Kind',
          type: 'string',
        },
        metadata: {
          $ref: '#/$defs/DevHubManifestMetadata',
          description: 'Metadata for the DMZ product.',
        },
        spec: {
          $ref: '#/$defs/DevHubProductSpecDMZ',
          description: 'Specification for the product.',
        },
        modules: {
          default: [],
          description: 'Limited modules for DMZ.',
          items: {
            $ref: '#/$defs/AnyModuleConfigDMZ',
          },
          title: 'Modules',
          type: 'array',
        },
        routing: {
          $ref: '#/$defs/RoutingConfigDMZ',
          default: {
            exposedServices: [],
            linkedProducts: [],
            subDomainOverride: null,
            token: null,
            customDomains: null,
          },
          description: 'Manage traffic in and out of a product namespace.',
        },
        codeLocations: {
          default: [],
          description:
            'List of code locations for the product (DMZ-restricted to approved repositories).',
          items: {
            $ref: '#/$defs/CodeLocation',
          },
          title: 'Codelocations',
          type: 'array',
        },
        application: {
          $ref: '#/$defs/ApplicationConfig',
          default: {
            requiredResourceAccess: [],
          },
          description:
            'Application configuration for the product (DMZ-restricted with security-hardened defaults).',
        },
      },
      required: ['apiVersion', 'kind', 'metadata', 'spec'],
      title: 'ProductManifestDevHubDMZV1Alpha1',
      type: 'object',
    },
    ProductManifestDevHubV1Alpha1: {
      description:
        'Product manifest for DevHub v1alpha1.\nThis manifest is used to define a product in the DevHub platform.\nIt includes metadata, specifications, capabilities, modules, routing,\ncode locations, application configuration, and extensions.',
      properties: {
        apiVersion: {
          const: 'core.devhub.ai/v1alpha1',
          title: 'Apiversion',
          type: 'string',
        },
        kind: {
          const: 'ProductManifest',
          description: 'Kind of manifest',
          title: 'Kind',
          type: 'string',
        },
        metadata: {
          $ref: '#/$defs/DevHubManifestMetadata',
          description: 'Metadata for the product.',
        },
        spec: {
          $ref: '#/$defs/DevHubProductSpec',
          description: 'Specification for the product.',
        },
        modules: {
          default: [],
          description:
            'Modules provide functionalities and experiences inside a product context.',
          items: {
            $ref: '#/$defs/AnyDevHubModuleConfig',
          },
          title: 'Modules',
          type: 'array',
        },
        routing: {
          $ref: '#/$defs/RoutingConfig',
          default: {
            exposedServices: [],
            linkedProducts: [],
            subDomainOverride: null,
            token: null,
          },
          description: 'Manage traffic in and out of a product namespace. ',
        },
        codeLocations: {
          default: [],
          description: 'List of code locations for the product.',
          items: {
            $ref: '#/$defs/CodeLocation',
          },
          title: 'Codelocations',
          type: 'array',
        },
        application: {
          $ref: '#/$defs/ApplicationConfig',
          default: {
            requiredResourceAccess: [],
          },
          description: 'Application configuration for the product.',
        },
        extensions: {
          $ref: '#/$defs/ExtensionConfig',
          default: {
            appstore: [],
            dsp: null,
            aigateway: null,
          },
          description: 'List of extensions for the product. [experimental]',
        },
      },
      required: ['apiVersion', 'kind', 'metadata', 'spec'],
      title: 'ProductManifestDevHubV1Alpha1',
      type: 'object',
    },
    ProductReport: {
      properties: {
        apiVersion: {
          const: 'core.argus.ai/v1alpha1',
          title: 'Apiversion',
          type: 'string',
        },
        kind: {
          const: 'ProductCatalogEntry',
          default: 'ProductCatalogEntry',
          title: 'Kind',
          type: 'string',
        },
        metadata: {
          $ref: '#/$defs/Metadata',
          description: 'Basic metadata about the product. ',
          title: 'Product metadata.',
        },
        identity: {
          $ref: '#/$defs/IdentityInfo',
          description:
            'Default identity to use for accessing product resources.',
          title: 'Product identity.',
        },
        k8sRelease: {
          anyOf: [
            {
              $ref: '#/$defs/K8sReleaseInfo',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'Basic information about K8s environment for the product.',
          title: 'K8s release info.',
        },
        resources: {
          default: [],
          description: 'Azure resources provisioned for the product.',
          items: {
            $ref: '#/$defs/AnyResource',
          },
          title: 'Product resources.',
          type: 'array',
        },
        routing: {
          $ref: '#/$defs/RoutingReport',
          description: 'List of exposed services.',
          title: 'Product routing.',
        },
      },
      required: ['apiVersion', 'metadata', 'identity', 'routing'],
      title: 'ProductReport',
      type: 'object',
    },
    ProductSpec: {
      properties: {
        owner: {
          description:
            'AAD object ID or display name for the group owning the entity.',
          format: 'uuid',
          title: 'Owner group',
          type: 'string',
          'ui:field': 'AzureGroupSearch',
          'ui:title': 'AAD Group',
          'vsc:picker': 'aad-group',
        },
        primaryContact: {
          anyOf: [
            {
              format: 'uuid',
              type: 'string',
            },
            {
              format: 'email',
              type: 'string',
            },
          ],
          description: 'AAD object id or email of the primary contact person.',
          title: 'Primary contact',
          'ui:field': 'AzureUserSearch',
          'ui:title': 'AAD User',
          'vsc:picker': 'aad-user',
        },
        secondaryContact: {
          anyOf: [
            {
              format: 'uuid',
              type: 'string',
            },
            {
              format: 'email',
              type: 'string',
            },
          ],
          description:
            'AAD object id or email of the secondary contact person.',
          title: 'Secondary contact',
          'ui:field': 'AzureUserSearch',
          'ui:title': 'AAD User',
          'vsc:picker': 'aad-user',
        },
        uniqueKey: {
          $ref: '#/$defs/UniqueKeyType',
          description: 'A unique identifier for the entity.',
          title: 'Unique key.',
          'ui:disabled': true,
          'ui:title': 'Unique Key',
        },
        lifecycle: {
          $ref: '#/$defs/argus__provisioning__models__common__registry__Lifecycle',
          default: 'production',
          description: 'Lifecycle of the entity.',
          title: 'Entity lifecycle stage.',
        },
        featureFlags: {
          default: [],
          description:
            'Feature flags allow op-ting into experimental functionality in the stack',
          items: {
            $ref: '#/$defs/FeatureFlag',
          },
          title: 'Feature flags.',
          type: 'array',
          'vsc:action': 'array',
        },
        workspace: {
          description: 'Name of the workspace this product is managed by.',
          errorMessage: {
            pattern:
              'Must start with a lowercase letter, can contain lowercase letters, numbers, hyphens, and underscores.',
          },
          maxLength: 62,
          minLength: 1,
          pattern: '^[a-z]+(?:[-_]?[a-z0-9]+)+$',
          title: 'Workspace name',
          type: 'string',
        },
        resourceQuotaRequests: {
          $ref: '#/$defs/K8sResources',
          default: {
            cpu: '2000m',
            memory: '3Gi',
          },
          description: 'Requested Kubernetes resources for product namespace.',
          title: 'Resource requests',
        },
        resourceQuotaLimits: {
          $ref: '#/$defs/K8sResources',
          default: {
            cpu: '4000m',
            memory: '5Gi',
          },
          description:
            'Upper limit for Kubernetes resources for product namespace.',
          title: 'Resource limits',
        },
        location: {
          $ref: '#/$defs/AzureRegion',
          default: 'westeurope',
          description: 'Azure region where resources should be provisioned.',
          title: 'Location',
        },
      },
      required: [
        'owner',
        'primaryContact',
        'secondaryContact',
        'uniqueKey',
        'workspace',
      ],
      title: 'ProductSpec',
      type: 'object',
    },
    ProductSubscriptionBaseReport: {
      properties: {
        objectId: {
          description: 'The object ID of the service principal.',
          title: 'Object ID',
          type: 'string',
        },
        repositories: {
          description: 'The repositories the service principal has access to.',
          items: {
            type: 'string',
          },
          title: 'Repositories',
          type: 'array',
        },
        environmentName: {
          description:
            'The name of the environment the service principal is used in.',
          title: 'Environment name',
          type: 'string',
        },
        targetCell: {
          $ref: '#/$defs/InstanceCellRef',
          description: 'The cell the agent can deploy to.',
          title: 'Target cell',
        },
        subscriptionShorthand: {
          description:
            'The shorthand of the subscription the agent can deploy to.',
          title: 'Subscription shorthand',
          type: 'string',
        },
        vnetAddressSpace: {
          description: 'The address space of the VNet the agent can deploy to.',
          title: 'VNet address space',
          type: 'string',
        },
        subscriptionName: {
          description: 'The name of the subscription the agent can deploy to.',
          title: 'Subscription name',
          type: 'string',
        },
        workflowSchedule: {
          description:
            'The schedule for product deployments in this subscription.',
          title: 'Workflow schedule',
          type: 'string',
        },
      },
      required: [
        'objectId',
        'environmentName',
        'targetCell',
        'subscriptionShorthand',
        'vnetAddressSpace',
        'subscriptionName',
        'workflowSchedule',
      ],
      title: 'ProductSubscriptionBaseReport',
      type: 'object',
    },
    ProductSubscriptionBaseReportPE: {
      properties: {
        targetCell: {
          $ref: '#/$defs/InstanceCellRef',
          description: 'The cell the agent can deploy to.',
          title: 'Target cell',
        },
        subscriptionShorthand: {
          description:
            'The shorthand of the subscription the agent can deploy to.',
          title: 'Subscription shorthand',
          type: 'string',
        },
        subscriptionName: {
          description: 'The name of the subscription the agent can deploy to.',
          title: 'Subscription name',
          type: 'string',
        },
        subscriptionId: {
          default: '',
          description: 'The ID of the subscription the agent can deploy to.',
          title: 'Subscription ID',
          type: 'string',
        },
      },
      required: ['targetCell', 'subscriptionShorthand', 'subscriptionName'],
      title: 'ProductSubscriptionBaseReportPE',
      type: 'object',
    },
    ProductSubscriptionBaseReportV2: {
      properties: {
        objectId: {
          description: 'The object ID of the service principal.',
          title: 'Object ID',
          type: 'string',
        },
        repositories: {
          description: 'The repositories the service principal has access to.',
          items: {
            type: 'string',
          },
          title: 'Repositories',
          type: 'array',
        },
        environmentName: {
          description:
            'The name of the environment the service principal is used in.',
          title: 'Environment name',
          type: 'string',
        },
        targetCell: {
          $ref: '#/$defs/InstanceCellRef',
          description: 'The cell the agent can deploy to.',
          title: 'Target cell',
        },
        subscriptionShorthand: {
          description:
            'The shorthand of the subscription the agent can deploy to.',
          title: 'Subscription shorthand',
          type: 'string',
        },
        vnetAddressSpace: {
          description: 'The address space of the VNet the agent can deploy to.',
          title: 'VNet address space',
          type: 'string',
        },
        subscriptionName: {
          description: 'The name of the subscription the agent can deploy to.',
          title: 'Subscription name',
          type: 'string',
        },
      },
      required: [
        'objectId',
        'environmentName',
        'targetCell',
        'subscriptionShorthand',
        'vnetAddressSpace',
        'subscriptionName',
      ],
      title: 'ProductSubscriptionBaseReportV2',
      type: 'object',
    },
    ProductSubscriptionBaseReportVnet: {
      properties: {
        objectId: {
          description: 'The object ID of the service principal.',
          title: 'Object ID',
          type: 'string',
        },
        repositories: {
          description: 'The repositories the service principal has access to.',
          items: {
            type: 'string',
          },
          title: 'Repositories',
          type: 'array',
        },
        environmentName: {
          description:
            'The name of the environment the service principal is used in.',
          title: 'Environment name',
          type: 'string',
        },
        targetCell: {
          $ref: '#/$defs/InstanceCellRef',
          description: 'The cell the agent can deploy to.',
          title: 'Target cell',
        },
        subscriptionShorthand: {
          description:
            'The shorthand of the subscription the agent can deploy to.',
          title: 'Subscription shorthand',
          type: 'string',
        },
        vnetAddressSpace: {
          description: 'The address space of the VNet the agent can deploy to.',
          title: 'VNet address space',
          type: 'string',
        },
        subscriptionName: {
          description: 'The name of the subscription the agent can deploy to.',
          title: 'Subscription name',
          type: 'string',
        },
        subscriptionId: {
          default: '',
          description: 'The ID of the subscription the agent can deploy to.',
          title: 'Subscription ID',
          type: 'string',
        },
        readerAgentObjectId: {
          description:
            'The object ID of the service principal with Reader permissions on the subscription.',
          title: 'Reader agent object ID',
          type: 'string',
        },
      },
      required: [
        'objectId',
        'environmentName',
        'targetCell',
        'subscriptionShorthand',
        'vnetAddressSpace',
        'subscriptionName',
        'readerAgentObjectId',
      ],
      title: 'ProductSubscriptionBaseReportVnet',
      type: 'object',
    },
    ProductSubscriptionRefPE: {
      properties: {
        name: {
          description: 'The name of the subscription.',
          title: 'name',
          type: 'string',
        },
        shorthand: {
          description: 'The unique identifier of the subscription.',
          title: 'subscription shorthand',
          type: 'string',
        },
        subscriptionId: {
          default: '',
          description: 'The Azure subscription ID.',
          title: 'subscription ID',
          type: 'string',
        },
      },
      required: ['name', 'shorthand'],
      title: 'ProductSubscriptionRefPE',
      type: 'object',
    },
    ProductSubscriptionRefV2: {
      description: 'A reference to a azure product subscription.',
      properties: {
        name: {
          description: 'The name of the subscription.',
          title: 'name',
          type: 'string',
        },
        shorthand: {
          description: 'The unique identifier of the subscription.',
          title: 'subscription shorthand',
          type: 'string',
        },
        defaultPrincipalName: {
          description: 'The default principal name for the subscription.',
          title: 'default principal name',
          type: 'string',
        },
        subscriptionId: {
          default: '',
          description: 'The Azure subscription ID.',
          title: 'subscription ID',
          type: 'string',
        },
        vnetAddressSpace: {
          description: 'The main subscription VNet address space.',
          title: 'VNet Address Space',
          type: 'string',
        },
      },
      required: [
        'name',
        'shorthand',
        'defaultPrincipalName',
        'vnetAddressSpace',
      ],
      title: 'ProductSubscriptionRefV2',
      type: 'object',
    },
    ProductSubscriptionRefVnet: {
      description: 'A reference to a azure product subscription.',
      properties: {
        name: {
          description: 'The name of the subscription.',
          title: 'name',
          type: 'string',
        },
        shorthand: {
          description: 'The unique identifier of the subscription.',
          title: 'subscription shorthand',
          type: 'string',
        },
        defaultPrincipalName: {
          description: 'The default principal name for the subscription.',
          title: 'default principal name',
          type: 'string',
        },
        subscriptionId: {
          default: '',
          description: 'The Azure subscription ID.',
          title: 'subscription ID',
          type: 'string',
        },
        vnetAddressSpace: {
          description: 'The main subscription VNet address space.',
          title: 'VNet Address Space',
          type: 'string',
        },
      },
      required: [
        'name',
        'shorthand',
        'defaultPrincipalName',
        'vnetAddressSpace',
      ],
      title: 'ProductSubscriptionRefVnet',
      type: 'object',
    },
    PublicIpResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'public-ip',
          title: 'Kind',
          type: 'string',
        },
        publicIp: {
          description: 'Public IP address of the network.',
          title: 'Public IP',
          type: 'string',
        },
      },
      required: ['resourceId', 'kind', 'publicIp'],
      title: 'PublicIpResource',
      type: 'object',
    },
    RFC1035Name: {
      description: 'A string compliant with RFC1035.',
      errorMessage: {
        pattern:
          'Must start with a lowercase letter, can contain lowercase letters, numbers, and hyphens.',
      },
      maxLength: 62,
      minLength: 1,
      pattern: '^[a-z]+(?:-?[a-z0-9]+)+$',
      title: 'RFC1035 name',
      type: 'string',
    },
    RabbitMQConfig: {
      description:
        'Message broker enabling applications to communicate through messaging.',
      properties: {
        kind: {
          const: 'rabbitmq',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
        uiPathPrefix: {
          $ref: '#/$defs/UrlPathSegment',
          default: '/rabbitmq',
          description: 'Route where the main UI is served.',
          title: 'UI path prefix',
          'ui:title': 'UI Path Prefix',
        },
        allowedGroups: {
          default: [],
          description:
            'AAD groups allowed to access the RabbitMQ management UI.',
          items: {
            $ref: '#/$defs/Group',
          },
          title: 'Allowedgroups',
          type: 'array',
          'ui:title': 'Allowed Groups',
          'vsc:action': 'array',
          'vsc:picker': 'aad-group',
        },
        storageSize: {
          default: '10Gi',
          description:
            "Persistent storage size for RabbitMQ data (e.g., '10Gi', '20Gi')",
          title: 'Storagesize',
          type: 'string',
          'ui:title': 'Storage Size',
        },
        resourcesRequests: {
          $ref: '#/$defs/K8sResources',
          default: {
            cpu: '2000m',
            memory: '2Gi',
          },
          description: 'Request Kubernetes resources for RabbitMQ containers',
          'ui:title': 'K8s Resource Requests',
        },
        resourcesLimits: {
          $ref: '#/$defs/K8sResources',
          default: {
            cpu: '4000m',
            memory: '4Gi',
          },
          description: 'Limits on Kubernetes resources for RabbitMQ containers',
          'ui:title': 'K8s Resource Limits',
        },
      },
      required: ['kind'],
      title: 'RabbitMQConfig',
      type: 'object',
      'ui:options': {
        icon: 'rabbitmq',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/messaging/rabbitmq/',
          },
        ],
        supportedTargets: ['L5'],
        tags: ['messaging', 'broker', 'queue', 'amqp'],
        teaser: 'Message broker for reliable messaging between applications.',
      },
      'ui:title': 'RabbitMQ',
    },
    Region: {
      description: 'Region where the lab resources are deployed',
      enum: ['northeurope', 'chinanorth3'],
      title: 'Region',
      type: 'string',
    },
    RegistryAgentReport: {
      properties: {
        objectId: {
          description: 'The object ID of the service principal.',
          title: 'Object ID',
          type: 'string',
        },
        repositories: {
          description: 'The repositories the service principal has access to.',
          items: {
            type: 'string',
          },
          title: 'Repositories',
          type: 'array',
        },
        environmentName: {
          description:
            'The name of the environment the service principal is used in.',
          title: 'Environment name',
          type: 'string',
        },
        targetCell: {
          $ref: '#/$defs/InstanceCellRef',
          description: 'The cell the agent can deploy to.',
          title: 'Target cell',
        },
      },
      required: ['objectId', 'environmentName', 'targetCell'],
      title: 'RegistryAgentReport',
      type: 'object',
    },
    ResourceGroupResource: {
      properties: {
        kind: {
          const: 'resource-group',
          default: 'resource-group',
          title: 'Kind',
          type: 'string',
        },
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        location: {
          description: 'Azure region where the resource group is deployed.',
          title: 'Azure region',
          type: 'string',
        },
      },
      required: ['kind', 'resourceId', 'location'],
      title: 'ResourceGroupResource',
      type: 'object',
    },
    ResourcesReport: {
      properties: {
        resources: {
          description: 'List of azure resources provisioned in the stack.',
          items: {
            $ref: '#/$defs/AnyResource',
          },
          title: 'Azure resources',
          type: 'array',
        },
        resourceLocations: {
          additionalProperties: {
            type: 'string',
          },
          description:
            'Mapping of a node in the construct tree to the resource id.',
          title: 'Resource locations',
          type: 'object',
        },
      },
      required: ['resources', 'resourceLocations'],
      title: 'ResourcesReport',
      type: 'object',
    },
    ResourcesReportEntry: {
      properties: {
        stackVersion: {
          description:
            'The version of the infrastructure stack that provisioned the resources.',
          title: 'Stack Version',
          type: 'string',
        },
        signature: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Signature',
        },
        report: {
          $ref: '#/$defs/ResourcesReport',
        },
      },
      required: ['stackVersion', 'report'],
      title: 'ResourcesReportEntry',
      type: 'object',
    },
    RoleAssignmentInfo: {
      properties: {
        roleDefinitionName: {
          description:
            'Name of the role definition this role assignment applies to.',
          title: 'Role Definition Name',
          type: 'string',
        },
        roleDefinitionId: {
          description:
            'Azure Resource Manager ID of the role definition this role assignment applies to.',
          title: 'Role Definition ID',
          type: 'string',
        },
        resourceId: {
          description:
            'Azure Resource Manager ID of the resource this role applies to.',
          title: 'Resource id.',
          type: 'string',
        },
        principalId: {
          description:
            'Azure Resource Manager ID of the principal this role assignment applies to.',
          title: 'Principal ID',
          type: 'string',
        },
        principalType: {
          description:
            'Type of the principal this role assignment applies to (e.g., User, Group, Service Principal).',
          title: 'Principal Type',
          type: 'string',
        },
        description: {
          description:
            'A short description on why the role assignment is required.',
          title: 'Motivation',
          type: 'string',
        },
      },
      required: [
        'roleDefinitionName',
        'roleDefinitionId',
        'resourceId',
        'principalId',
        'principalType',
        'description',
      ],
      title: 'RoleAssignmentInfo',
      type: 'object',
    },
    RoutingConfig: {
      properties: {
        exposedServices: {
          default: [],
          description: 'Product services exposed to clients or other products.',
          items: {
            $ref: '#/$defs/ExposedServiceConfig',
          },
          title: 'Exposedservices',
          type: 'array',
        },
        linkedProducts: {
          default: [],
          description:
            'Products that are allowed to invoke services from this product.',
          items: {
            $ref: '#/$defs/EntityName',
          },
          title: 'Linkedproducts',
          type: 'array',
        },
        subDomainOverride: {
          anyOf: [
            {
              $ref: '#/$defs/SubDomainName',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'Custom sub-domain for apps and services deployed in the product.',
        },
        token: {
          anyOf: [
            {
              $ref: '#/$defs/TokenConfigDict',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'Token configuration for the product.',
        },
      },
      title: 'RoutingConfig',
      type: 'object',
    },
    RoutingConfigDMZ: {
      description:
        'Extended routing configuration for DMZ products.\n\nAdds support for custom DNS domains (e.g. ``myapp.fep.basf.net``)\nbeyond the standard ``<product>.<cell>-app.basf.net`` domain.\n\nTLS certificates for custom domains are automatically provisioned\nby cert-manager using the DigiCert ACME ClusterIssuer on DMZ clusters.',
      properties: {
        exposedServices: {
          default: [],
          description: 'Product services exposed to clients or other products.',
          items: {
            $ref: '#/$defs/ExposedServiceConfig',
          },
          title: 'Exposedservices',
          type: 'array',
        },
        linkedProducts: {
          default: [],
          description:
            'Products that are allowed to invoke services from this product.',
          items: {
            $ref: '#/$defs/EntityName',
          },
          title: 'Linkedproducts',
          type: 'array',
        },
        subDomainOverride: {
          anyOf: [
            {
              $ref: '#/$defs/SubDomainName',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'Custom sub-domain for apps and services deployed in the product.',
        },
        token: {
          anyOf: [
            {
              $ref: '#/$defs/TokenConfigDict',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'Token configuration for the product.',
        },
        customDomains: {
          anyOf: [
            {
              $ref: '#/$defs/StagedConfigDict_CustomDomainConfig_',
            },
            {
              type: 'null',
            },
          ],
          default: null,
        },
      },
      title: 'RoutingConfigDMZ',
      type: 'object',
    },
    RoutingReport: {
      properties: {
        logicalStage: {
          $ref: '#/$defs/LogicalStage',
          description: 'The logical stage this routing report is for.',
        },
        services: {
          items: {
            $ref: '#/$defs/ExposedServiceEntry',
          },
          title: 'Services',
          type: 'array',
        },
        egressListeners: {
          default: [],
          description: 'Egress listeners.',
          items: {
            $ref: '#/$defs/EgressListenerConfig',
          },
          title: 'Egresslisteners',
          type: 'array',
        },
      },
      required: ['logicalStage', 'services'],
      title: 'RoutingReport',
      type: 'object',
    },
    RoutingRule: {
      properties: {
        path: {
          $ref: '#/$defs/UrlPathSegment',
          description: 'Matched path segment.',
        },
        pathMatch: {
          $ref: '#/$defs/PathMatchType',
          default: 'prefix',
          description: 'Path matcher type.',
        },
        pathRewrite: {
          anyOf: [
            {
              $ref: '#/$defs/UrlPathSegment',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'Rewrite matched path before sending request to downstream service.',
        },
        allowedGroups: {
          default: [],
          description: 'Restrict access to this route to specific AAD groups.',
          items: {
            $ref: '#/$defs/Group',
          },
          title: 'Allowedgroups',
          type: 'array',
          'ui:title': 'Groups',
          'vsc:action': 'array',
          'vsc:picker': 'aad-group',
        },
        allowedServicePrincipals: {
          anyOf: [
            {
              description: 'List of service principals.',
              items: {
                $ref: '#/$defs/argus__provisioning__models__types__fields__ServicePrincipal',
              },
              type: 'array',
              'ui:title': 'Service Principals',
              'vsc:action': 'array',
              'vsc:picker': 'aad-serviceprincipal',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'Allow access to this route to specific AAD service principals.',
          title: 'Allowedserviceprincipals',
        },
        timeout: {
          default: 15,
          maximum: 90,
          minimum: 0,
          title: 'Timeout in seconds: e.g. 15',
          type: 'integer',
        },
        idleTimeout: {
          anyOf: [
            {
              maximum: 3600,
              minimum: 1,
              type: 'integer',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'Idle timeout for streaming responses (SSE, WebSockets, long-polling).',
          title: 'Stream idle timeout in seconds',
        },
        unsafeDisableAuth: {
          default: false,
          description: 'UNSAFE - Disable authentication for this route.',
          title: 'Unsafedisableauth',
          type: 'boolean',
        },
        autoHostRewrite: {
          anyOf: [
            {
              type: 'boolean',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'Rewrite the host header to match the target service host.',
          title: 'Autohostrewrite',
        },
      },
      required: ['path'],
      title: 'RoutingRule',
      type: 'object',
    },
    RuleSet: {
      enum: ['base', 'strict'],
      title: 'RuleSet',
      type: 'string',
    },
    SearchServiceResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'ai-search',
          title: 'Kind',
          type: 'string',
        },
        location: {
          description: 'The location where the Search Service is deployed.',
          title: 'Azure region',
          type: 'string',
        },
        sku: {
          description: 'The SKU of the Search Service.',
          title: 'SKU',
          type: 'string',
        },
      },
      required: ['resourceId', 'kind', 'location', 'sku'],
      title: 'SearchServiceResource',
      type: 'object',
    },
    ServerlessConfiguration: {
      enum: ['privateEndpoint', 'subnet', ''],
      title: 'ServerlessConfiguration',
      type: 'string',
    },
    ServiceLevel: {
      description: 'Service level associated with the deployment',
      enum: ['Support Offering 1', 'Support Offering 2', 'Support Offering 3'],
      title: 'ServiceLevel',
      type: 'string',
    },
    ServiceLevelAgreement: {
      description: 'Service level agreement associated with the deployment',
      enum: ['Bronze', 'Silver', 'Gold'],
      title: 'ServiceLevelAgreement',
      type: 'string',
    },
    SharedGroupsReport: {
      properties: {
        workspaceOwner: {
          $ref: '#/$defs/GroupInfo',
        },
        productOwner: {
          $ref: '#/$defs/GroupInfo',
        },
        workspaceIdentities: {
          $ref: '#/$defs/GroupInfo',
        },
        platformContributors: {
          $ref: '#/$defs/GroupInfo',
        },
        basfGlobalMembers: {
          $ref: '#/$defs/GroupInfo',
        },
      },
      required: [
        'workspaceOwner',
        'productOwner',
        'workspaceIdentities',
        'platformContributors',
        'basfGlobalMembers',
      ],
      title: 'SharedGroupsReport',
      type: 'object',
    },
    SqlDataSharing: {
      properties: {
        kind: {
          const: 'sql-datasharing',
          default: 'sql-datasharing',
          description: 'The kind of datasharing.',
          title: 'Datasharing Kind',
          type: 'string',
        },
        databaseName: {
          description: 'The database name of the datasharing.',
          title: 'Database Name',
          type: 'string',
        },
        identityObjectId: {
          description: 'The group object ID of the datasharing.',
          title: 'Identity Object ID',
          type: 'string',
        },
      },
      required: ['databaseName', 'identityObjectId'],
      title: 'SqlDataSharing',
      type: 'object',
    },
    SqlFirewallRule: {
      properties: {
        AuthorEmail: {
          description: 'The email of the author of the firewall rule.',
          title: 'Author Email',
          type: 'string',
        },
        kind: {
          const: 'sql-firewall-rule',
          default: 'sql-firewall-rule',
          description: 'The kind of firewall rule.',
          title: 'Firewall Rule Kind',
          type: 'string',
        },
        RuleName: {
          description: 'The name of the firewall rule.',
          title: 'Rule Name',
          type: 'string',
        },
        StartIPAddress: {
          anyOf: [
            {
              format: 'ipvanyaddress',
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'The start IP address of the firewall rule.',
          title: 'Start IP Address',
        },
        EndIPAddress: {
          anyOf: [
            {
              format: 'ipvanyaddress',
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'The end IP address of the firewall rule.',
          title: 'End IP Address',
        },
      },
      required: ['AuthorEmail', 'RuleName'],
      title: 'SqlFirewallRule',
      type: 'object',
    },
    SqlPermission: {
      properties: {
        kind: {
          const: 'sql-permission',
          default: 'sql-permission',
          description: 'SQL Permission Kind',
          title: 'SQL Permission Kind',
          type: 'string',
        },
        Database: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          description: 'The database to which permissions are assigned',
          title: 'Database',
        },
        Permissions: {
          anyOf: [
            {
              items: {
                enum: ['Read', 'ReadWrite'],
                type: 'string',
              },
              type: 'array',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'The permissions assigned to the sql group.',
          title: 'Permissions',
        },
      },
      required: ['Database'],
      title: 'SqlPermission',
      type: 'object',
    },
    SqlResource: {
      properties: {
        kind: {
          const: 'sql_resource',
          default: 'sql_resource',
          description: 'Sql Resource Resource Kind',
          title: 'Sql Resource Kind',
          type: 'string',
        },
        Name: {
          description: 'Sql Resource Name',
          title: 'Sql Resource Name',
          type: 'string',
        },
      },
      required: ['Name'],
      title: 'SqlResource',
      type: 'object',
    },
    SqlServer: {
      properties: {
        kind: {
          const: 'sql_server',
          default: 'sql_server',
          description: 'SqlServer Resource Kind',
          title: 'SqlServer Resource Kind',
          type: 'string',
        },
        Name: {
          description: 'SqlServer Resource Name',
          title: 'SqlServer Resource Name',
          type: 'string',
        },
        ElasticPool: {
          default: false,
          description: 'elasticPool option enabled',
          title: 'elasticPool option enabled',
          type: 'boolean',
        },
        SQLAuditing: {
          default: false,
          description: 'sqlAuditing option enabled',
          title: 'sqlAuditing option enabled',
          type: 'boolean',
        },
        Repo: {
          default: 0,
          description: '# of repos for resource',
          maximum: 10,
          minimum: 0,
          title: '# of repos for resource',
          type: 'integer',
        },
        RepoFork: {
          default: false,
          description: 'Repo Fork option enabled',
          title: 'Repo Fork option enabled',
          type: 'boolean',
        },
        DiagnosticSettingsLaw: {
          default: false,
          description: 'Diagnostic Settings Law',
          title: 'Diagnostic Settings Law',
          type: 'boolean',
        },
        DiagnosticSettingsSta: {
          default: false,
          description: 'Diagnostic Settings Sta',
          title: 'Diagnostic Settings Sta',
          type: 'boolean',
        },
        Logs: {
          default: [],
          description: 'Logs for ADF resource',
          items: {
            type: 'string',
          },
          title: 'Logs for ADF resource',
          type: 'array',
        },
        Metrics: {
          default: [],
          description: 'Metrics for ADF resource',
          items: {
            type: 'string',
          },
          title: 'Metrics for ADF resource',
          type: 'array',
        },
        Databases: {
          default: [],
          description: 'List of SQL DBs deployed in server',
          items: {
            $ref: '#/$defs/SqlResource',
          },
          title: 'SQL DBs deployed in server',
          type: 'array',
        },
        Serverless: {
          default: [],
          description: 'List of serverless deployed in server',
          items: {
            $ref: '#/$defs/SqlResource',
          },
          title: 'Serverless deployed in server',
          type: 'array',
        },
        SQLPools: {
          default: [],
          description: 'List of SQL Pools deployed in server',
          items: {
            $ref: '#/$defs/SqlResource',
          },
          title: 'SQL Pools deployed in server',
          type: 'array',
        },
      },
      required: ['Name'],
      title: 'SqlServer',
      type: 'object',
    },
    SqlServerConfig: {
      properties: {
        config: {
          anyOf: [
            {
              $ref: '#/$defs/SqlServerConfigBase',
            },
            {
              $ref: '#/$defs/SqlServerConfigDict',
            },
          ],
          default: {
            databases: [],
            sqlPools: [],
            serverless: [],
            minCapacity: 1,
            serverlessPauseDelay: 60,
            sqlSku: 'S0',
            elasticpoolDeployment: false,
            diagnosticSettingsSta: false,
            diagnosticSettingsLaw: false,
            logs: [],
            metrics: [],
          },
          description: 'Configuration for the resource',
          title: 'Config',
          'ui:title': 'Select configuration type',
        },
        kind: {
          const: 'sql-server',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
      },
      required: ['kind'],
      title: 'SqlServerConfig',
      type: 'object',
      'ui:options': {
        icon: 'sql-server',
        links: [
          {
            url: 'https://devhub.intranet.basf.com/documentation/devhub/modules/storage/sqlserver/',
          },
        ],
        supportedTargets: ['L5'],
        tags: ['database', 'sql', 'microsoft'],
        teaser: 'Microsoft SQL Server database.',
      },
      'ui:title': 'SQL Server',
    },
    SqlServerConfigBase: {
      properties: {
        databases: {
          default: [],
          description: 'List of databases.',
          items: {
            type: 'string',
          },
          title: 'List of databases',
          type: 'array',
          'ui:title': 'Databases',
        },
        sqlPools: {
          default: [],
          description: 'List of custom names for sql pools databases',
          items: {
            type: 'string',
          },
          title: 'List of custom names for sql pools databases',
          type: 'array',
          'ui:title': 'SQL Pools',
        },
        serverless: {
          default: [],
          description: 'Serverless deployed in server',
          items: {
            type: 'string',
          },
          title: 'Serverless deployed in server',
          type: 'array',
          'ui:title': 'Serverless',
        },
        minCapacity: {
          default: 1,
          description: 'Minimum capacity allocated for serverless databases.',
          maximum: 5,
          minimum: 1,
          title: 'Minimum capacity',
          type: 'integer',
          'ui:title': 'Minimum Capacity',
        },
        serverlessPauseDelay: {
          default: 60,
          description: 'Time after which db is automatically paused.',
          maximum: 300,
          minimum: 60,
          title: 'serverless pause delay',
          type: 'integer',
          'ui:title': 'Serverless Pause Delay',
        },
        sqlSku: {
          $ref: '#/$defs/SqlServerSku',
          default: 'S0',
          description: 'SKU of the sql database.',
          'ui:title': 'SQL Database SKU',
        },
        elasticpoolDeployment: {
          default: false,
          description: 'Elasticpool deployment.',
          title: 'Elasticpooldeployment',
          type: 'boolean',
          'ui:title': 'Elasticpool Deployment',
        },
        diagnosticSettingsSta: {
          default: false,
          description: 'Diagnostic Settings Storage Account',
          title: 'Diagnostic Settings Storage Account',
          type: 'boolean',
          'ui:title': 'Diagnostic Settings Storage Account',
        },
        diagnosticSettingsLaw: {
          default: false,
          description: 'Diagnostic Settings Log Analytics Workspace',
          title: 'Diagnostic Settings Log Analytics Workspace',
          type: 'boolean',
          'ui:title': 'Diagnostic Settings Log Analytics Workspace',
        },
        logs: {
          default: [],
          description: 'Logs for ADF resource',
          items: {
            type: 'string',
          },
          title: 'Logs for ADF resource',
          type: 'array',
          'ui:title': 'Logs',
        },
        metrics: {
          default: [],
          description: 'Metrics for ADF resource',
          items: {
            type: 'string',
          },
          title: 'Metrics for ADF resource',
          type: 'array',
          'ui:title': 'Metrics',
        },
      },
      title: 'SqlServerConfigBase',
      type: 'object',
      'ui:title': 'Base configuration',
    },
    SqlServerConfigDict: {
      description:
        'A staged configuration for the Postgres databases that belong to this cell.',
      properties: {
        development: {
          anyOf: [
            {
              $ref: '#/$defs/SqlServerConfigBase',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Development configuration',
        },
        production: {
          anyOf: [
            {
              $ref: '#/$defs/SqlServerConfigBase',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Production configuration',
        },
      },
      title: 'SqlServerConfigDict',
      type: 'object',
      'ui:title': 'Configuration per environment',
    },
    SqlServerDatabaseResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'sql-server-database',
          title: 'Kind',
          type: 'string',
        },
        collation: {
          description: 'The collation to use for the database.',
          title: 'Collation',
          type: 'string',
        },
        skuName: {
          description: 'The SKU of the sql-server database.',
          title: 'SKU name',
          type: 'string',
        },
      },
      required: ['resourceId', 'kind', 'collation', 'skuName'],
      title: 'SqlServerDatabaseResource',
      type: 'object',
    },
    SqlServerElasticpoolResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'sql-server-elastic-pool',
          title: 'Kind',
          type: 'string',
        },
        location: {
          description:
            'Azure region where the Elasticpool resource is deployed.',
          title: 'Azure region',
          type: 'string',
        },
        skuName: {
          description: 'The SKU of the Elasticpool resource.',
          title: 'SKU name',
          type: 'string',
        },
      },
      required: ['resourceId', 'kind', 'location', 'skuName'],
      title: 'SqlServerElasticpoolResource',
      type: 'object',
    },
    SqlServerResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'sql-server',
          title: 'Kind',
          type: 'string',
        },
        location: {
          description: 'Azure region where the sql-server server is deployed.',
          title: 'Azure region',
          type: 'string',
        },
        fqdn: {
          description: 'Fully qualified domain name of the server.',
          title: 'FQDN',
          type: 'string',
        },
      },
      required: ['resourceId', 'kind', 'location', 'fqdn'],
      title: 'SqlServerResource',
      type: 'object',
    },
    SqlServerSku: {
      enum: ['S0'],
      title: 'SqlServerSku',
      type: 'string',
    },
    StagedConfigDict_CertManagerConfigBase_: {
      properties: {
        development: {
          anyOf: [
            {
              $ref: '#/$defs/CertManagerConfigBase',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Development configuration',
        },
        production: {
          anyOf: [
            {
              $ref: '#/$defs/CertManagerConfigBase',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Production configuration',
        },
      },
      title: 'StagedConfigDict[CertManagerConfigBase]',
      type: 'object',
    },
    StagedConfigDict_CustomDomainConfig_: {
      properties: {
        development: {
          anyOf: [
            {
              $ref: '#/$defs/CustomDomainConfig',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Development configuration',
        },
        production: {
          anyOf: [
            {
              $ref: '#/$defs/CustomDomainConfig',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Production configuration',
        },
      },
      title: 'StagedConfigDict[CustomDomainConfig]',
      type: 'object',
    },
    StagedConfigDict_InstanceSpecV3_: {
      properties: {
        development: {
          anyOf: [
            {
              $ref: '#/$defs/InstanceSpecV3',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Development configuration',
        },
        production: {
          anyOf: [
            {
              $ref: '#/$defs/InstanceSpecV3',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Production configuration',
        },
      },
      title: 'StagedConfigDict[InstanceSpecV3]',
      type: 'object',
    },
    StagedConfigDict_InstanceSpecV4_: {
      properties: {
        development: {
          anyOf: [
            {
              $ref: '#/$defs/InstanceSpecV4',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Development configuration',
        },
        production: {
          anyOf: [
            {
              $ref: '#/$defs/InstanceSpecV4',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Production configuration',
        },
      },
      title: 'StagedConfigDict[InstanceSpecV4]',
      type: 'object',
    },
    StagedConfigDict_InstanceSpecV5_: {
      properties: {
        development: {
          anyOf: [
            {
              $ref: '#/$defs/InstanceSpecV5',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Development configuration',
        },
        production: {
          anyOf: [
            {
              $ref: '#/$defs/InstanceSpecV5',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Production configuration',
        },
      },
      title: 'StagedConfigDict[InstanceSpecV5]',
      type: 'object',
    },
    StagedConfigDict_OpenTelemetryConfigBase_: {
      properties: {
        development: {
          anyOf: [
            {
              $ref: '#/$defs/OpenTelemetryConfigBase',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Development configuration',
        },
        production: {
          anyOf: [
            {
              $ref: '#/$defs/OpenTelemetryConfigBase',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Production configuration',
        },
      },
      title: 'StagedConfigDict[OpenTelemetryConfigBase]',
      type: 'object',
    },
    StagedConfigDict_PortalConfigBase_: {
      properties: {
        development: {
          anyOf: [
            {
              $ref: '#/$defs/PortalConfigBase',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Development configuration',
        },
        production: {
          anyOf: [
            {
              $ref: '#/$defs/PortalConfigBase',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Production configuration',
        },
      },
      title: 'StagedConfigDict[PortalConfigBase]',
      type: 'object',
    },
    StorageAccountResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'storage-account',
          title: 'Kind',
          type: 'string',
        },
        location: {
          description: 'Azure region where the storage account is deployed.',
          title: 'Azure region',
          type: 'string',
        },
        hnsEnabled: {
          description:
            'Whether hierarchical namespace is enabled for the storage account.',
          title: 'HNS enabled',
          type: 'boolean',
        },
        replicationType: {
          description: 'The replication type of the storage account.',
          title: 'Replication type',
          type: 'string',
        },
      },
      required: [
        'resourceId',
        'kind',
        'location',
        'hnsEnabled',
        'replicationType',
      ],
      title: 'StorageAccountResource',
      type: 'object',
    },
    StorageConfig: {
      properties: {
        config: {
          anyOf: [
            {
              $ref: '#/$defs/StorageConfigBase',
            },
            {
              $ref: '#/$defs/StorageConfigDict',
            },
          ],
          default: {
            externalAccess: false,
            hnsEnabled: true,
            location: 'westeurope',
            containers: [],
            accountDataContributors: [],
            containerRoleAssignments: [],
          },
          description: 'Configuration for the resource',
          title: 'Config',
        },
        kind: {
          const: 'object-store',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
      },
      required: ['kind'],
      title: 'StorageConfig',
      type: 'object',
    },
    StorageConfigBase: {
      description:
        'Allows provisioning storage containers within Azure storage accounts.\n\nThe `storage` capability provisions a [storage account] and enables users and\nmodules to request containers in that account.\n\n[storage account]: https://learn.microsoft.com/en-us/azure/storage/blobs/storage-blobs-introduction',
      properties: {
        externalAccess: {
          default: false,
          description:
            'Enable external access to the service via the product Gateway.',
          title: 'External access',
          type: 'boolean',
          'ui:title': 'External Access',
        },
        hnsEnabled: {
          default: true,
          description: 'Enable hierarchical namespace on the storage.',
          title: 'Hnsenabled',
          type: 'boolean',
          'ui:title': 'Hierarchical Namespace Enabled',
        },
        location: {
          default: 'westeurope',
          description: 'Specify where the resource is deployed.',
          title: 'Location',
          type: 'string',
          'ui:title': 'Location',
        },
        containers: {
          default: [],
          description:
            'Storage containers to be created in the account.\n\n### Warning: Reserved names\n\nDo not use the reserved container names `argus*`, `dagster`, `mlflow`.\nThis may lead to unexpected behavior until we have implemented conflict\nresolution on container names.',
          items: {
            $ref: '#/$defs/StorageContainerName',
          },
          title: 'Containers',
          type: 'array',
          'ui:title': 'Containers',
          'vsc:action': 'array',
        },
        accountDataContributors: {
          default: [],
          description:
            'Object IDs for all principals to get account level data access.',
          items: {
            description: 'AAD object ID of the group.',
            format: 'uuid',
            title: 'AAD group',
            type: 'string',
            'ui:field': 'AzureGroupSearch',
            'ui:title': 'AAD Group',
            'vsc:picker': 'aad-group',
          },
          title: 'Accountdatacontributors',
          type: 'array',
          'ui:title': 'Account Data Contributors',
          'vsc:action': 'array',
        },
        containerRoleAssignments: {
          default: [],
          description: 'Container specific role assignments.',
          items: {
            $ref: '#/$defs/StorageContainerRoleAssignment',
          },
          title: 'Containerroleassignments',
          type: 'array',
          'ui:title': 'Container Role Assignments',
          'vsc:action': 'array',
        },
      },
      title: 'StorageConfigBase',
      type: 'object',
    },
    StorageConfigDict: {
      description:
        'A staged configuration for the storage containers that belong to this cell.',
      properties: {
        development: {
          anyOf: [
            {
              $ref: '#/$defs/StorageConfigBase',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Development configuration',
        },
        production: {
          anyOf: [
            {
              $ref: '#/$defs/StorageConfigBase',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Production configuration',
        },
      },
      title: 'StorageConfigDict',
      type: 'object',
    },
    StorageContainerName: {
      description: 'A valid name for a storage container.',
      errorMessage: {
        pattern:
          'Must start with a lowercase letter, can contain lowercase letters, numbers, and hyphens.',
      },
      maxLength: 63,
      minLength: 3,
      pattern: '^[a-z]+(?:-?[a-z0-9]+)+$',
      title: 'Container name',
      type: 'string',
    },
    StorageContainerResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'storage-container',
          title: 'Kind',
          type: 'string',
        },
        url: {
          description: 'The URL of the storage container.',
          title: 'URL',
          type: 'string',
        },
      },
      required: ['resourceId', 'kind', 'url'],
      title: 'StorageContainerResource',
      type: 'object',
    },
    StorageContainerRole: {
      description:
        'Available storage roles to be assigned, see the [documentation] for details.\n\n[documentation]: https://learn.microsoft.com/en-us/azure/role-based-access-control/built-in-roles',
      enum: [
        'Storage Blob Data Contributor',
        'Storage Blob Data Owner',
        'Storage Blob Data Reader',
      ],
      title: 'StorageContainerRole',
      type: 'string',
    },
    StorageContainerRoleAssignment: {
      properties: {
        objectId: {
          description: 'AAD entity for which the role is to be assigned.',
          format: 'uuid',
          title: 'Objectid',
          type: 'string',
          'ui:title': 'Object ID',
        },
        containerName: {
          $ref: '#/$defs/StorageContainerName',
          description: 'The storage container this role applies to.',
          'ui:title': 'Container Name',
        },
        role: {
          $ref: '#/$defs/StorageContainerRole',
          description: 'Role to be assigned to principal.',
          'ui:title': 'Role',
        },
      },
      required: ['objectId', 'containerName', 'role'],
      title: 'StorageContainerRoleAssignment',
      type: 'object',
    },
    SubDomainName: {
      description: 'A valid sub-domain name.',
      errorMessage: {
        pattern:
          'Must start with a lowercase letter, can contain lowercase letters, numbers, and hyphens.',
      },
      maxLength: 50,
      minLength: 3,
      pattern: '^[a-z]+(?:-?[a-z0-9]+)+$',
      title: 'Sub Domain Name',
      type: 'string',
    },
    SubscriptionID: {
      description: 'Subscription ID associated with the deployment',
      enum: [
        '15cf7065-76e8-4c4c-9a94-efcf975d03bf',
        'c842cf2d-e63c-4215-9ae5-11edd91a74cd',
        'c9643df3-75c7-4e98-801c-e0c7f5a57ab4',
        '830c0582-5aba-40b4-beae-d1f73c64173c',
        'cc08ce38-9309-4bc6-ae35-8a3f9c5bd5c5',
        '18432760-6a4b-4908-9101-1f7e19ecf7e1',
        'a70f15ec-c939-44db-a3a6-116f3688fe99',
        '9e97ce43-d54b-4b43-99b4-2af1f215b13e',
        'f2da4344-9050-420e-9536-5c3721a24cb1',
      ],
      title: 'SubscriptionID',
      type: 'string',
    },
    SubscriptionKind: {
      enum: ['product', 'workspace', 'platform'],
      title: 'SubscriptionKind',
      type: 'string',
    },
    SubscriptionNetworkType: {
      enum: ['PI', 'IN', 'DMZ', 'AG'],
      title: 'SubscriptionNetworkType',
      type: 'string',
    },
    SubscriptionRefV2: {
      description: 'A reference to a azure subscription.',
      properties: {
        name: {
          description: 'The name of the subscription.',
          title: 'name',
          type: 'string',
        },
        shorthand: {
          description: 'The unique identifier of the subscription.',
          title: 'subscription shorthand',
          type: 'string',
        },
        defaultPrincipalName: {
          description: 'The default principal name for the subscription.',
          title: 'default principal name',
          type: 'string',
        },
        subscriptionId: {
          default: '',
          description: 'The Azure subscription ID.',
          title: 'subscription ID',
          type: 'string',
        },
      },
      required: ['name', 'shorthand', 'defaultPrincipalName'],
      title: 'SubscriptionRefV2',
      type: 'object',
    },
    SupersetConfig: {
      description:
        'An open-source modern data exploration and visualization platform.',
      properties: {
        kind: {
          const: 'superset',
          description: 'Module discriminator',
          title: 'Kind',
          type: 'string',
          'ui:disabled': true,
        },
        chartVersion: {
          default: '1.2.2',
          description: 'Version of the service chart to deploy.',
          title: 'Helm chart version',
          type: 'string',
          'ui:title': 'Helm Chart Version',
        },
        uiPathPrefix: {
          $ref: '#/$defs/UrlPathSegment',
          default: '/',
          description: 'Route where the main UI is served.',
          title: 'UI path prefix',
          'ui:title': 'UI Path Prefix',
        },
        allowedGroups: {
          default: [],
          description: 'AAD groups allowed to access the Superset UI.',
          items: {
            $ref: '#/$defs/Group',
          },
          title: 'Allowedgroups',
          type: 'array',
          'ui:title': 'Allowed Groups',
          'vsc:action': 'array',
          'vsc:picker': 'aad-group',
        },
        globalResources: {
          $ref: '#/$defs/K8sResourceConfig',
          default: {
            requests: {
              cpu: '200m',
              memory: '256Mi',
            },
            limits: {
              cpu: '500m',
              memory: '512Mi',
            },
          },
          description: 'Default Superset worker resources',
          'ui:title': 'K8s Global Resource configuration',
        },
        workerResources: {
          $ref: '#/$defs/K8sResourceConfig',
          default: {
            requests: {
              cpu: '200m',
              memory: '512Mi',
            },
            limits: {
              cpu: '500m',
              memory: '1Gi',
            },
          },
          description: 'Default Superset worker resources',
          'ui:title': 'K8s Worker Resource configuration',
        },
        nodeResources: {
          $ref: '#/$defs/K8sResourceConfig',
          default: {
            requests: {
              cpu: '100m',
              memory: '128Mi',
            },
            limits: {
              cpu: '200m',
              memory: '256Mi',
            },
          },
          description: 'Default Superset node resources',
          'ui:title': 'K8s Node Resource configuration',
        },
      },
      required: ['kind'],
      title: 'SupersetConfig',
      type: 'object',
      'ui:options': {
        icon: 'superset',
        supportedTargets: ['L5'],
        tags: ['visualization', 'dashboard'],
        teaser: 'Modern data exploration and visualization platform.',
      },
      'ui:title': 'Apache Superset',
    },
    Synapse: {
      properties: {
        kind: {
          const: 'synapse',
          default: 'synapse',
          description: 'Synapse Resource Kind',
          title: 'Synapse Resource Kind',
          type: 'string',
        },
        Name: {
          description: 'Synapse Resource Name',
          title: 'Synapse Resource Name',
          type: 'string',
        },
        Repo: {
          default: 0,
          description: '# of repos for resource',
          maximum: 10,
          minimum: 0,
          title: '# of repos for resource',
          type: 'integer',
        },
        RepoFork: {
          default: false,
          description: 'Repo Fork option enabled',
          title: 'Repo Fork option enabled',
          type: 'boolean',
        },
      },
      required: ['Name'],
      title: 'Synapse',
      type: 'object',
    },
    TeamRole: {
      enum: ['developers', 'admins'],
      title: 'TeamRole',
      type: 'string',
    },
    TokenConfig: {
      properties: {
        audiences: {
          description: 'Allowed audiences for the token validation.',
          items: {
            type: 'string',
          },
          title: 'Audiences',
          type: 'array',
        },
      },
      required: ['audiences'],
      title: 'TokenConfig',
      type: 'object',
    },
    TokenConfigDict: {
      description:
        'A dictionary of token configurations for the product.\n\nThe keys are the names of the token configurations, and the values are the\n[TokenConfig] objects.',
      properties: {
        development: {
          anyOf: [
            {
              $ref: '#/$defs/TokenConfig',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Development configuration',
        },
        production: {
          anyOf: [
            {
              $ref: '#/$defs/TokenConfig',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          title: 'Production configuration',
        },
      },
      title: 'TokenConfigDict',
      type: 'object',
    },
    UcCatalog: {
      properties: {
        kind: {
          const: 'uc_catalog',
          default: 'uc_catalog',
          description: 'UC Catalog Resource Kind',
          title: 'UC Catalog Resource Kind',
          type: 'string',
        },
        Name: {
          description: 'Catalog Name',
          title: 'Catalog Name',
          type: 'string',
        },
        Type: {
          $ref: '#/$defs/DbksBindingType',
          description: 'Catalog Type',
          title: 'Catalog Type',
        },
        Source: {
          $ref: '#/$defs/DbksCatalogSource',
          description: 'Catalog Source',
          title: 'Catalog Source',
        },
        ExternalLocation: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'External Location Name',
          title: 'External Location Name',
        },
        ExternalLocationUrl: {
          anyOf: [
            {
              format: 'uri',
              minLength: 1,
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description: 'External Location URL',
          title: 'External Location URL',
        },
      },
      required: ['Name', 'Type', 'Source'],
      title: 'UcCatalog',
      type: 'object',
    },
    UniqueKeyType: {
      errorMessage: {
        pattern:
          'Must be either 3 lowercase letters (legacy pattern) or 4 lowercase letters or numbers and start with a lowercase letter.',
      },
      maxLength: 4,
      minLength: 3,
      pattern: '^[a-z][a-z0-9]*$',
      type: 'string',
    },
    UnityCatalogResource: {
      properties: {
        resourceId: {
          description: 'Canonical reference to resource.',
          title: 'Azure resource id.',
          type: 'string',
          'ui:title': 'Azure Resource ID',
        },
        kind: {
          const: 'unity-catalog',
          title: 'Kind',
          type: 'string',
        },
        catalogName: {
          description: 'The name of the Unity Catalog.',
          title: 'Catalog Name',
          type: 'string',
        },
        metastoreId: {
          description: 'The ID of the metastore this catalog belongs to.',
          title: 'Metastore ID',
          type: 'string',
        },
        owner: {
          description: 'The owner of the catalog.',
          title: 'Owner',
          type: 'string',
        },
      },
      required: ['resourceId', 'kind', 'catalogName', 'metastoreId', 'owner'],
      title: 'UnityCatalogResource',
      type: 'object',
    },
    UpstreamCredential: {
      description:
        'UpstreamCredential specifies the type of credential to use for the upstream.',
      enum: ['azure:msgraph', 'azure:cognitiveservices', 'azure:databricks'],
      title: 'UpstreamCredential',
      type: 'string',
    },
    UpstreamRef: {
      description:
        'UpstreamRef is a reference to an upstream service to forward matched requests to.',
      properties: {
        name: {
          description: 'Name of the upstream.',
          title: 'Name',
          type: 'string',
        },
        host: {
          description: 'Host of the upstream.',
          title: 'Host',
          type: 'string',
        },
        port: {
          description: 'Port of the upstream.',
          title: 'Port',
          type: 'integer',
        },
      },
      required: ['name', 'host', 'port'],
      title: 'UpstreamRef',
      type: 'object',
    },
    UrlPathSegment: {
      description: 'URL path segment.',
      errorMessage: {
        pattern:
          "Must start with '/', can contain letters, numbers, underscores, periods, hyphens, and slashes.",
      },
      pattern: '^\\/[a-zA-Z0-9_\\.\\/\\-]*?$',
      title: 'URL path segment',
      type: 'string',
    },
    UserEnvironment: {
      properties: {
        image: {
          description:
            'Docker image to be used when spawning this environment.',
          title: 'Image',
          type: 'string',
          'ui:title': 'Image',
        },
        tag: {
          default: 'latest',
          description: 'User image tag to be used',
          title: 'Tag',
          type: 'string',
          'ui:title': 'Tag',
        },
        displayName: {
          $ref: '#/$defs/DisplayName',
          default: 'Default Data Science Notebook',
          description: 'User image display name to be used',
          'ui:title': 'Display Name',
        },
        description: {
          default:
            'Default notebook containing various popular data science libraries.',
          description: 'User image description to be used',
          title: 'Description',
          type: 'string',
          'ui:title': 'Description',
        },
      },
      required: ['image'],
      title: 'UserEnvironment',
      type: 'object',
      'ui:title': 'User Environment',
    },
    Workflow: {
      description: 'Workflow configured on a code location.',
      enum: ['github-flow'],
      title: 'Workflow',
      type: 'string',
    },
    WorkspaceEntry: {
      properties: {
        stackVersion: {
          description:
            'The version of the infrastructure stack that provisioned the resources.',
          title: 'Stack Version',
          type: 'string',
        },
        signature: {
          anyOf: [
            {
              type: 'string',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'A signature that can be used to validate the integrity of the data.',
          title: 'Signature',
        },
        output: {
          $ref: '#/$defs/WorkspaceReport',
        },
        input: {
          $ref: '#/$defs/WorkspaceManifest',
        },
        policies: {
          $ref: '#/$defs/PolicyReport',
          title: 'Policies.',
        },
      },
      required: ['stackVersion', 'output', 'input', 'policies'],
      title: 'WorkspaceEntry',
      type: 'object',
    },
    WorkspaceManifest: {
      description: 'Manifest for a workspace in argus.',
      properties: {
        apiVersion: {
          const: 'core.argus.ai/v1alpha1',
          description: 'Api version',
          title: 'Apiversion',
          type: 'string',
        },
        kind: {
          const: 'WorkspaceManifest',
          description: 'Manifest kind',
          title: 'Kind',
          type: 'string',
        },
        metadata: {
          $ref: '#/$defs/ManifestMetadata',
          description: 'Metadata for the entity.',
          title: 'Metadata',
        },
        spec: {
          $ref: '#/$defs/WorkspaceSpec',
          description: 'Workspace specification.',
          title: 'Workspace specification',
        },
      },
      required: ['apiVersion', 'kind', 'metadata', 'spec'],
      title: 'WorkspaceManifest',
      type: 'object',
    },
    WorkspaceManifestDevHubV1Alpha1: {
      properties: {
        apiVersion: {
          const: 'core.devhub.ai/v1alpha1',
          description: 'Api version',
          title: 'Apiversion',
          type: 'string',
        },
        kind: {
          const: 'WorkspaceManifest',
          description: 'Manifest kind',
          title: 'Kind',
          type: 'string',
        },
        metadata: {
          $ref: '#/$defs/DevHubManifestMetadata',
          description: 'Metadata for the entity.',
          title: 'Metadata',
        },
        spec: {
          $ref: '#/$defs/WorkspaceSpecDevHubV1Alpha1',
          description: 'Workspace specification.',
          title: 'Workspace specification',
        },
      },
      required: ['apiVersion', 'kind', 'metadata', 'spec'],
      title: 'WorkspaceManifestDevHubV1Alpha1',
      type: 'object',
    },
    WorkspaceReport: {
      properties: {
        apiVersion: {
          const: 'core.argus.ai/v1alpha1',
          title: 'Apiversion',
          type: 'string',
        },
        kind: {
          const: 'WorkspaceCatalogEntry',
          title: 'Kind',
          type: 'string',
        },
        metadata: {
          $ref: '#/$defs/ManifestMetadata',
          description: 'Basic metadata about the workspace.',
          title: 'Workspace metadata.',
        },
        identity: {
          $ref: '#/$defs/IdentityInfo',
          description: 'Identity information for the workspace.',
          title: 'Workspace identity.',
        },
        application: {
          $ref: '#/$defs/ApplicationInfo',
          description: 'Application information for the workspace.',
          title: 'Workspace application.',
        },
        devops: {
          anyOf: [
            {
              $ref: '#/$defs/DevOpsOutput',
            },
            {
              type: 'null',
            },
          ],
          default: null,
        },
        githubTeams: {
          default: [],
          description: 'GitHub teams associated with the workspace.',
          items: {
            $ref: '#/$defs/GitHubTeamInfo',
          },
          title: 'GitHub teams',
          type: 'array',
        },
        resources: {
          default: [],
          description: 'Azure resources provisioned for the workspace.',
          items: {
            $ref: '#/$defs/AnyResource',
          },
          title: 'Workspace resources',
          type: 'array',
        },
      },
      required: ['apiVersion', 'kind', 'metadata', 'identity', 'application'],
      title: 'WorkspaceReport',
      type: 'object',
    },
    WorkspaceSpec: {
      properties: {
        owner: {
          description:
            'AAD object ID or display name for the group owning the entity.',
          format: 'uuid',
          title: 'Owner group',
          type: 'string',
          'ui:field': 'AzureGroupSearch',
          'ui:title': 'AAD Group',
          'vsc:picker': 'aad-group',
        },
        primaryContact: {
          anyOf: [
            {
              format: 'uuid',
              type: 'string',
            },
            {
              format: 'email',
              type: 'string',
            },
          ],
          description: 'AAD object id or email of the primary contact person.',
          title: 'Primary contact',
          'ui:field': 'AzureUserSearch',
          'ui:title': 'AAD User',
          'vsc:picker': 'aad-user',
        },
        secondaryContact: {
          anyOf: [
            {
              format: 'uuid',
              type: 'string',
            },
            {
              format: 'email',
              type: 'string',
            },
          ],
          description:
            'AAD object id or email of the secondary contact person.',
          title: 'Secondary contact',
          'ui:field': 'AzureUserSearch',
          'ui:title': 'AAD User',
          'vsc:picker': 'aad-user',
        },
        uniqueKey: {
          $ref: '#/$defs/UniqueKeyType',
          description: 'A unique identifier for the entity.',
          title: 'Unique key.',
          'ui:disabled': true,
          'ui:title': 'Unique Key',
        },
        lifecycle: {
          $ref: '#/$defs/argus__provisioning__models__common__registry__Lifecycle',
          default: 'production',
          description: 'Lifecycle of the entity.',
          title: 'Entity lifecycle stage.',
        },
        featureFlags: {
          default: [],
          description:
            'Feature flags allow op-ting into experimental functionality in the stack',
          items: {
            $ref: '#/$defs/FeatureFlag',
          },
          title: 'Feature flags.',
          type: 'array',
          'vsc:action': 'array',
        },
        location: {
          $ref: '#/$defs/AzureRegion',
          default: 'westeurope',
          description: 'Azure region where resources should be provisioned.',
          title: 'Location',
        },
        devOps: {
          anyOf: [
            {
              $ref: '#/$defs/AzureDevOpsProject',
            },
            {
              type: 'null',
            },
          ],
          default: null,
          description:
            'Azure DevOps project, where workspace presence is to be installed.',
          title: 'Azure DevOps project',
        },
        devTeams: {
          description:
            'List of development teams that should have access to the workspace.',
          items: {
            $ref: '#/$defs/GitHubTeamConfig',
          },
          title: 'Development teams',
          type: 'array',
        },
        linkedTeams: {
          description:
            'List of teams that are assigned permissions on workspace repositories.',
          items: {
            $ref: '#/$defs/LinkedGitHubTeamConfig',
          },
          title: 'Linked GitHub teams',
          type: 'array',
        },
      },
      required: ['owner', 'primaryContact', 'secondaryContact', 'uniqueKey'],
      title: 'WorkspaceSpec',
      type: 'object',
    },
    WorkspaceSpecDevHubV1Alpha1: {
      properties: {
        owner: {
          description:
            'AAD object ID or display name for the group owning the entity.',
          format: 'uuid',
          title: 'Owner group',
          type: 'string',
          'ui:field': 'AzureGroupSearch',
          'ui:title': 'AAD Group',
          'vsc:picker': 'aad-group',
        },
        primaryContact: {
          anyOf: [
            {
              format: 'uuid',
              type: 'string',
            },
            {
              format: 'email',
              type: 'string',
            },
          ],
          description: 'AAD object id or email of the primary contact person.',
          title: 'Primary contact',
          'ui:field': 'AzureUserSearch',
          'ui:title': 'AAD User',
          'vsc:picker': 'aad-user',
        },
        secondaryContact: {
          anyOf: [
            {
              format: 'uuid',
              type: 'string',
            },
            {
              format: 'email',
              type: 'string',
            },
          ],
          description:
            'AAD object id or email of the secondary contact person.',
          title: 'Secondary contact',
          'ui:field': 'AzureUserSearch',
          'ui:title': 'AAD User',
          'vsc:picker': 'aad-user',
        },
        uniqueKey: {
          $ref: '#/$defs/UniqueKeyType',
          description: 'A unique identifier for the entity.',
          title: 'Unique key.',
          'ui:disabled': true,
          'ui:title': 'Unique Key',
        },
        lifecycle: {
          $ref: '#/$defs/argus__provisioning__models__common__registry__Lifecycle',
          default: 'production',
          description: 'Lifecycle of the entity.',
          title: 'Entity lifecycle stage.',
        },
        featureFlags: {
          default: [],
          description:
            'Feature flags allow op-ting into experimental functionality in the stack',
          items: {
            $ref: '#/$defs/FeatureFlag',
          },
          title: 'Feature flags.',
          type: 'array',
          'vsc:action': 'array',
        },
        location: {
          $ref: '#/$defs/AzureRegion',
          default: 'northeurope',
          description: 'Azure region where resources should be provisioned.',
          title: 'Location',
        },
        devTeams: {
          description:
            'List of development teams that should have access to the workspace.',
          items: {
            $ref: '#/$defs/GitHubTeamConfig',
          },
          title: 'Development teams',
          type: 'array',
        },
        linkedTeams: {
          description:
            'List of teams that are assigned permissions on workspace repositories.',
          items: {
            $ref: '#/$defs/LinkedGitHubTeamConfig',
          },
          title: 'Linked GitHub teams',
          type: 'array',
        },
      },
      required: ['owner', 'primaryContact', 'secondaryContact', 'uniqueKey'],
      title: 'WorkspaceSpecDevHubV1Alpha1',
      type: 'object',
    },
    WorkspaceSubscriptionBaseReport: {
      properties: {
        objectId: {
          description: 'The object ID of the service principal.',
          title: 'Object ID',
          type: 'string',
        },
        repositories: {
          description: 'The repositories the service principal has access to.',
          items: {
            type: 'string',
          },
          title: 'Repositories',
          type: 'array',
        },
        environmentName: {
          description:
            'The name of the environment the service principal is used in.',
          title: 'Environment name',
          type: 'string',
        },
        subscriptionShorthand: {
          description:
            'The shorthand of the subscription the agent can deploy to.',
          title: 'Subscription shorthand',
          type: 'string',
        },
        subscriptionName: {
          description: 'The name of the subscription the agent can deploy to.',
          title: 'Subscription name',
          type: 'string',
        },
        subscriptionId: {
          default: '',
          description: 'The ID of the subscription the agent can deploy to.',
          title: 'Subscription ID',
          type: 'string',
        },
      },
      required: [
        'objectId',
        'environmentName',
        'subscriptionShorthand',
        'subscriptionName',
      ],
      title: 'WorkspaceSubscriptionBaseReport',
      type: 'object',
    },
    argus__provisioning__models__common__registry__Lifecycle: {
      enum: ['experimental', 'development', 'production', 'end-of-life'],
      title: 'Lifecycle',
      type: 'string',
    },
    argus__provisioning__models__types__fields__ServicePrincipal: {
      format: 'uuid',
      type: 'string',
    },
    dsp_deployment__models__manifests___spec_choices__Lifecycle: {
      description: 'Lifecycle of the model',
      enum: ['development', 'production', 'staging', 'testing'],
      title: 'Lifecycle',
      type: 'string',
    },
    dsp_deployment__models__manifests__identities__identities__ServicePrincipal:
      {
        properties: {
          kind: {
            const: 'service-principal',
            default: 'service-principal',
            description: 'The kind of identity.',
            title: 'Service Principal Kind',
            type: 'string',
          },
          Name: {
            description: 'The name of the service principal.',
            title: 'Service Principal Name',
            type: 'string',
          },
          Owner: {
            default: '',
            description: 'The owner Object ID of the service principal.',
            title: 'Owner',
            type: 'string',
          },
          Type: {
            description: 'The type of the service principal.',
            title: 'Service Principal Type',
            type: 'string',
          },
          Permissions: {
            default: [],
            description: 'The permissions assigned to the group.',
            items: {
              discriminator: {
                mapping: {
                  'adls-permission': '#/$defs/AdlsPermission',
                  'sql-permission': '#/$defs/SqlPermission',
                },
                propertyName: 'kind',
              },
              oneOf: [
                {
                  $ref: '#/$defs/SqlPermission',
                },
                {
                  $ref: '#/$defs/AdlsPermission',
                },
              ],
            },
            title: 'Permissions',
            type: 'array',
          },
        },
        required: ['Name', 'Type'],
        title: 'ServicePrincipal',
        type: 'object',
      },
  },
  title: 'CatalogEntries',
  oneOf: [
    {
      $ref: '#/$defs/DspManifest',
    },
    {
      $ref: '#/$defs/ProductManifestArgusV1Alpha1',
    },
    {
      $ref: '#/$defs/WorkspaceManifest',
    },
    {
      $ref: '#/$defs/PlatformManifestDevhubV1',
    },
    {
      $ref: '#/$defs/PlatformManifestDevhubV2',
    },
    {
      $ref: '#/$defs/PlatformManifestDevhubV3',
    },
    {
      $ref: '#/$defs/GitHubEnterpriseManifest',
    },
    {
      $ref: '#/$defs/ProductCellManifest',
    },
    {
      $ref: '#/$defs/PlatformEntryV4',
    },
    {
      $ref: '#/$defs/PlatformEntryV5',
    },
    {
      $ref: '#/$defs/PlatformEntryV6',
    },
    {
      $ref: '#/$defs/InfrastructureCellEntry',
    },
    {
      $ref: '#/$defs/GatewayEntry',
    },
    {
      $ref: '#/$defs/ProductEntry',
    },
    {
      $ref: '#/$defs/WorkspaceEntry',
    },
    {
      $ref: '#/$defs/GitHubEnterpriseEntry',
    },
    {
      $ref: '#/$defs/PolicyReportEntry',
    },
    {
      $ref: '#/$defs/ResourcesReportEntry',
    },
    {
      $ref: '#/$defs/DevHubProductEntry',
    },
    {
      $ref: '#/$defs/DevHubProductEntryDMZ',
    },
    {
      $ref: '#/$defs/DevHubProductBaseEntryDMZ',
    },
    {
      $ref: '#/$defs/DevHubWorkspaceEntry',
    },
    {
      $ref: '#/$defs/InstanceEntryV3',
    },
    {
      $ref: '#/$defs/InstanceEntryV4',
    },
    {
      $ref: '#/$defs/InstanceEntryV5',
    },
    {
      $ref: '#/$defs/InstanceCellEntry',
    },
    {
      $ref: '#/$defs/InstanceRegistryReport',
    },
    {
      $ref: '#/$defs/RegistryAgentReport',
    },
    {
      $ref: '#/$defs/CellClusterReport',
    },
    {
      $ref: '#/$defs/InstanceCellReport',
    },
    {
      $ref: '#/$defs/DeployedInstanceLayout',
    },
    {
      $ref: '#/$defs/InstanceResources',
    },
    {
      $ref: '#/$defs/InstanceGroups',
    },
    {
      $ref: '#/$defs/ProductCellSpec',
    },
    {
      $ref: '#/$defs/WorkspaceSpec',
    },
    {
      $ref: '#/$defs/InstanceSpecV3',
    },
    {
      $ref: '#/$defs/ProductSpec',
    },
    {
      $ref: '#/$defs/PlatformSpec',
    },
    {
      $ref: '#/$defs/DspSpec',
    },
  ],
  type: 'object',
} as const;
